import React, { useState, useEffect, useRef } from "react";
import {
  FiUser,
  FiSearch,
  FiMoreHorizontal,
  FiEdit2,
  FiCheck,
  FiX,
} from "react-icons/fi";
import {
  AlertTriangle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Trash2,
  ClipboardList,
  Users,
  Clock,
} from "lucide-react";

import { useOutletContext, useNavigate, useParams } from "react-router-dom";
import useProject from "../hooks/useProject";
import projectService from "../services/projectService";
import meetingMinuteService from "../services/meetingMinuteService";
import sprintService from "../services/sprintService";
// Shared with the Kanban Add Card popup, so both apply the same rules: the
// 8-hours/day cap (which skips non-working Saturdays) and the colour picker.
import { getMaxEstimatedHours } from "../lib/taskHours";
import ColorPicker from "../components/Kanban/ColorPicker";
import HoverScrollbar from "../components/HoverScrollbar";
import { CARD_COLORS } from "../lib/cardColors";
import { pickFromGoogleDrive } from "../lib/googlePicker";
import changeRequestService from "../services/changeRequestService";
import AddClientQuestionModal from "../components/Organisation/AddClientQuestionModal";
import useDashboard from "../hooks/useDashboard";
import kanbanService from "../services/kanbanService";
import useKanban from "../hooks/useKanban";
import { Icons } from "../components/icons";
import { pushNotification } from "../components/Topbar";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
const MEETING_DATETIME_STYLES = `
  .meeting-date-input::-webkit-calendar-picker-indicator,
  .meeting-time-input::-webkit-calendar-picker-indicator {
    display: none;
    -webkit-appearance: none;
  }
  .meeting-date-input::-webkit-inner-spin-button {
    display: none;
  }
`;

const DATEPICKER_ALIGN_STYLES = `
  .react-datepicker__navigation {
    top: 14px !important;
    width: 28px !important;
    height: 28px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  .react-datepicker__navigation--previous {
    left: 12px !important;
  }
  .react-datepicker__navigation--next {
    right: 12px !important;
  }
  .react-datepicker__navigation-icon::before {
    border-color: #374151 !important;
    border-width: 2px 2px 0 0 !important;
    width: 8px !important;
    height: 8px !important;
  }
  .react-datepicker__navigation:hover .react-datepicker__navigation-icon::before {
    border-color: #4253C7 !important;
  }
`;
const normalizeLists = (lists = []) =>
  lists.map((list) => ({
    ...list,
    tasks: Array.isArray(list?.tasks) ? list.tasks : [],
  }));

// Normalize a picked date to noon UTC of its calendar day so the picker's
// click-time stamp can't make a valid end date compare as before the start.
const toDateOnlyISO = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  return new Date(
    Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate(), 12),
  ).toISOString();
};

const PROJECT_STATUS_CONFIG = {
  "at-risk": {
    label: "AT-RISK",
    pill: "bg-[#FFE9EC] text-[#E11D48]",
    dot: "bg-[#E11D48]",
    bar: "#F43F5E",
  },
  "on-track": {
    label: "ON TRACK",
    pill: "bg-[#E6F8F0] text-[#059669]",
    dot: "bg-[#059669]",
    bar: "#10B981",
  },
  completed: {
    label: "COMPLETED",
    pill: "bg-[#E8EDFF] text-[#4253C7]",
    dot: "bg-[#4253C7]",
    bar: "#4F46E5",
  },
};

const resolveProjectStatus = (p) => {
  const raw = String(
    p?.trackingStatus || p?.status || p?.projectStatus || "",
  ).toLowerCase();
  if (Number(p?.progress) === 100 || raw === "completed") return "completed";
  if (raw === "at-risk") return "at-risk";
  return "on-track";
};

const AVATAR_COLORS = ["#6366F1", "#EC4899", "#10B981", "#F59E0B", "#06B6D4"];
const getRoleBadge = (role) => {
  const r = (role || "").toLowerCase();
  if (r === "admin" || r === "owner") return "bg-[#EEF1FF] text-[#4253C7]";
  if (r === "manager") return "bg-[#FFF1D6] text-[#8A6100]";
  return "bg-[#E6F8F0] text-[#059669]";
};
const Projects = () => {
  const [selectedTaskDesc, setSelectedTaskDesc] = useState(null);
  const [activeProjectTab, setActiveProjectTab] = useState("overview");
  const [isTimelineCollapsed, setIsTimelineCollapsed] = useState(false);
  const [showGanttChart, setShowGanttChart] = useState(false);
  const [ganttMonth, setGanttMonthState] = useState(null);
  const [showNewSprintForm, setShowNewSprintForm] = useState(false);
  const [editingSprintId, setEditingSprintId] = useState(null);
  const [selectedSprint, setSelectedSprint] = useState(null);
  const [deleteConfirmSprint, setDeleteConfirmSprint] = useState(null);
  const [newSprintDraft, setNewSprintDraft] = useState({
    name: "",
    scope: "",
    duration: "",
    startDate: "",
    endDate: "",
    status: "Scheduled",
  });
  const [sprintMetadata, setSprintMetadata] = useState({});
  const [isSavingSprint, setIsSavingSprint] = useState(false);
  const [newSprintError, setNewSprintError] = useState("");
  const [isLoadingSprints, setIsLoadingSprints] = useState(false);
  // The project's Kanban lists for the sprint form's picker. Each carries
  // `assignedTo` — the sprint already covering it, or null when free.
  const [availableLists, setAvailableLists] = useState([]);
  // ── Add Sprint: automatic or manual ──────────────────────────────
  // The chooser that opens from "+ New Sprint", and the import flow behind
  // its automatic option.
  const [showAddSprintChooser, setShowAddSprintChooser] = useState(false);
  const [showSprintImport, setShowSprintImport] = useState(false);
  const [sprintImportFile, setSprintImportFile] = useState(null);
  const [sprintImportRows, setSprintImportRows] = useState([]);
  const [isParsingSprintFile, setIsParsingSprintFile] = useState(false);
  const [isImportingSprints, setIsImportingSprints] = useState(false);
  const [sprintImportError, setSprintImportError] = useState("");
  const [autoImportAvailable, setAutoImportAvailable] = useState(false);
  const sprintFileInputRef = useRef(null);
  // Google Drive picker config from the server. `available` is false until
  // GOOGLE_CLIENT_ID and GOOGLE_API_KEY are set, and the option stays hidden.
  const [driveConfig, setDriveConfig] = useState({ available: false });
  const [driveFile, setDriveFile] = useState(null);
  const [isOpeningDrive, setIsOpeningDrive] = useState(false);
  const [showMeetingMinuteForm, setShowMeetingMinuteForm] = useState(false);
  const [meetingMinutes, setMeetingMinutes] = useState([]);
  const [selectedMeetingMinuteId, setSelectedMeetingMinuteId] = useState(null);
  // Entries are { id, text, raised }. `raised: true` means a row already
  // exists in the Client Change Request table for it — set on the ones loaded
  // back when editing, so saving again cannot duplicate them.
  const [meetingChangeRequests, setMeetingChangeRequests] = useState([]);
  const [showChangeRequestInput, setShowChangeRequestInput] = useState(false);
  // Turns the "Raise as change request" button green once pressed, to show
  // the change has been marked for raising.
  const [hasRaisedChange, setHasRaisedChange] = useState(false);
  const [meetingAttachments, setMeetingAttachments] = useState([]);
  const meetingAttachmentInputRef = useRef(null);
  const changeRequestCardRef = useRef(null);
  const decisionsSectionRef = useRef(null);
  const [meetingMinuteDraft, setMeetingMinuteDraft] = useState({
    title: "",
    date: "",
    time: "",
    type: "Client Review",
    taskId: "",
    attendeeIds: [],
    notes: "",
    decisions: "",
    changeRequest: "",
  });
  const [meetingDecisions, setMeetingDecisions] = useState([]);
  const [confirmedDecisionIndexes, setConfirmedDecisionIndexes] = useState([]);
  // Null while adding; the minute's id while editing an existing one. The
  // same form serves both, so this is what tells Save which way to go.
  const [editingMinuteId, setEditingMinuteId] = useState(null);
  // ── Draft from transcript ────────────────────────────────────────
  // Only shown when the server reports a key is configured.
  const [transcriptDraftAvailable, setTranscriptDraftAvailable] =
    useState(false);
  const [showTranscriptBox, setShowTranscriptBox] = useState(false);
  const [transcriptText, setTranscriptText] = useState("");
  const [isDrafting, setIsDrafting] = useState(false);
  const [draftNotice, setDraftNotice] = useState("");
  // "paste" or "upload" — which half of the Auto Fill dialog is showing.
  const [transcriptMode, setTranscriptMode] = useState("paste");
  const [transcriptFile, setTranscriptFile] = useState(null);
  const [transcriptError, setTranscriptError] = useState("");
  const transcriptFileInputRef = useRef(null);
  // Server state for the minutes tab.
  const [isLoadingMinutes, setIsLoadingMinutes] = useState(false);
  const [isSavingMinutes, setIsSavingMinutes] = useState(false);
  const [meetingMinuteError, setMeetingMinuteError] = useState("");
  // ── Client Change Request table ──────────────────────────────────
  const [changeRequests, setChangeRequests] = useState([]);
  const [isLoadingChangeRequests, setIsLoadingChangeRequests] = useState(false);
  const [changeRequestError, setChangeRequestError] = useState("");
  const [showNewRequestForm, setShowNewRequestForm] = useState(false);
  const [isSavingRequest, setIsSavingRequest] = useState(false);
  // The row whose detail card is open, and that card's loaded contents.
  // Clicking the same row again, or anywhere outside the card, closes it.
  // Change requests awaiting approval, and whether this user may act on
  // them. Both come from the server, which is the authority — the UI only
  // decides what to show.
  const [pendingRequests, setPendingRequests] = useState([]);
  const [pendingMinutes, setPendingMinutes] = useState([]);
  const [canReviewSubmissions, setCanReviewSubmissions] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);
  const [openRequestId, setOpenRequestId] = useState(null);
  const [requestDetail, setRequestDetail] = useState(null);
  const [isLoadingRequestDetail, setIsLoadingRequestDetail] = useState(false);
  const changeRequestTableRef = useRef(null);

  const { currentUser } = useOutletContext();
  const [showDisabledProjects, setShowDisabledProjects] = useState(false);

  const {
    columns,
    allUsers,
    selectedProject,
    isAddingMember,
    isFetchingSummary,
    loadProjectLists,
    loadProjectSummary,
    loadProject,
    isUpdatingProject,
    updateProject,
    loadAllUsers,
    addMember,
    removeMember,
    selectProject,
    deselectProject,
    updateColumns,
    clearUsers,
    exportCSV,
    disableProject,
    enableProject,
  } = useProject();

  const {
    projects,
    disabledProjects,
    isDisabledProjectsLoading,
    loadProjects,
    loadDisabledProjects,
  } = useDashboard();
  const { projects: kanbanProjects, loadProjects: loadKanbanProjects } =
    useKanban();
  // ── Load users for member picker on mount ────────────────────────
  useEffect(() => {
    clearUsers();
    loadAllUsers(true);
    loadProjects(); // keep projects list fresh
    loadKanbanProjects(); // same source Kanban.js uses, for accurate task counts
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch lists + summary when a project is selected ────────────
  // ── Local UI state (unchanged) ───────────────────────────────────

  const taskStartDateRef = useRef(null);
  const taskEndDateRef = useRef(null);
  const meetingDateInputRef = useRef(null);
  const meetingTimeInputRef = useRef(null);
  const [showMenu, setShowMenu] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showInviteClientModal, setShowInviteClientModal] = useState(false);
  // "Add Client" opens a chooser first: pick an existing viewer, or invite a
  // new one (which then opens the invite form below).
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [availableClients, setAvailableClients] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isAddingClient, setIsAddingClient] = useState(false);
  const [addClientError, setAddClientError] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientDescription, setClientDescription] = useState("");
  const [clientStartDate, setClientStartDate] = useState("");
  const [clientEmailError, setClientEmailError] = useState("");
  const [showResources] = useState(true);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [taskPage, setTaskPage] = useState(1);
  const tasksPerPage = 5;
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskFormError, setTaskFormError] = useState("");
  const [taskForm, setTaskForm] = useState({
    listId: "", // ← was listName: ""
    taskName: "",
    type: "design",
    priority: "high",
    startDate: "",
    endDate: "",
    assignee: "",
    hours: "",
    description: "",
    color: null,
  });
  // Anchor for the Add Task colour popover (null = closed), same as Kanban.
  const [taskColorAnchor, setTaskColorAnchor] = useState(null);

  const navigate = useNavigate();
  const { projectRef } = useParams();

  const projectId = projectRef?.split("-").pop();
  const requestedProjectIdRef = useRef(null);

  // Single source of truth for which view to show — computed directly from
  // the URL and Redux state on every render, so switching between projects
  // (or back to the list) never flashes stale content.
  const showProjectDashboard =
    Boolean(projectId) &&
    String(selectedProject?._id || "") === String(projectId);
  const showProjectList = !projectId;
  const isResolvingProject = Boolean(projectId) && !showProjectDashboard;

  // ── Disabled-project access control ───────────────────────────────
  // isProjectDisabled: true for EVERYONE when the project is disabled —
  //   drives the dimmed/locked look for the dashboard body.
  // canManage: admins/managers keep a live header ("..." Edit/Enable menu)
  //   so they can re-enable the project.
  // isBlockedByDisable: true only for non-privileged users — drives the
  //   "Contact your admin" overlay message.
  const isProjectDisabled =
    showProjectDashboard && Boolean(selectedProject?.isDisabled);
  // Normalized once so every role check below is case-insensitive — avoids
  // managers silently losing access if the backend ever sends "Manager"
  // instead of "manager".
  const userRole = currentUser?.role?.toLowerCase() || "";
  const canManage = userRole === "admin" || userRole === "manager";
  const isAdmin = userRole === "admin";
  // Viewers may add other clients to a project they already belong to — the
  // backend re-checks their project access. This is the one client action a
  // viewer can take, and it exists only here (never on the organisation page).
  const isViewerRole = userRole === "viewer";
  // Members are read-only in Meeting Minutes and Client Change Request;
  // admins, managers and clients may submit. The backend enforces this too.
  const canSubmitMinutes =
    userRole === "admin" || userRole === "manager" || userRole === "viewer";
  const canInviteClient =
    userRole === "admin" || userRole === "manager" || isViewerRole;
  const isBlockedByDisable = isProjectDisabled && !canManage;

  // URL drives the view: a project id in the URL opens that project;
  // no id (Back button or browser back) closes it in one click.
  useEffect(() => {
    if (!projectId) {
      requestedProjectIdRef.current = null;
      deselectProject();
      return;
    }
    // Always fetch by id so the page never sticks on "Loading project..."
    // waiting for the dashboard list. Prefer list row for an instant paint.
    const fromList = [...(projects || []), ...(disabledProjects || [])].find(
      (p) => String(p._id) === String(projectId),
    );
    if (fromList) selectProject(fromList);
    if (requestedProjectIdRef.current !== String(projectId)) {
      requestedProjectIdRef.current = String(projectId);
      loadProject(projectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, projects, disabledProjects]);

  // Each project opens on its overview instead of retaining the tab from the
  // project that was previously viewed.
  useEffect(() => {
    setActiveProjectTab("overview");
    setIsTimelineCollapsed(false);
    setShowGanttChart(false);
    setGanttMonthState(null);
    setShowNewSprintForm(false);
    setEditingSprintId(null);
    setNewSprintDraft({
      name: "",
      scope: "",
      duration: "",
      startDate: "",
      endDate: "",
      status: "Scheduled",
      lists: [],
    });
    setNewSprintError("");
    // Sprints belong to a project, so the previous project's must not linger
    // on screen while the new project's are being fetched.
    setSprintMetadata({});
    setSelectedSprint(null);
    setDeleteConfirmSprint(null);
    setShowAddSprintChooser(false);
    setShowSprintImport(false);
    setSprintImportFile(null);
    setDriveFile(null);
    setSprintImportRows([]);
    setSprintImportError("");
  }, [projectId]);
  useEffect(() => {
    if (!showNewSprintForm) return;
    const activeElement = document.activeElement;
    if (
      activeElement?.classList.contains(
        "react-datepicker-ignore-onclickoutside",
      )
    ) {
      activeElement.blur();
    }
  }, [newSprintDraft.startDate, newSprintDraft.endDate, showNewSprintForm]);
  useEffect(() => {
    if (!showNewSprintForm) return undefined;
    const closeAfterDateSelection = (event) => {
      if (!event.target.closest(".react-datepicker__day")) return;
      requestAnimationFrame(() => {
        document.getElementById("sprintStartDatePicker")?.blur();
        document.getElementById("sprintEndDatePicker")?.blur();
        document.body.dispatchEvent(
          new MouseEvent("mousedown", { bubbles: true }),
        );
      });
    };
    document.addEventListener("click", closeAfterDateSelection, true);
    return () =>
      document.removeEventListener("click", closeAfterDateSelection, true);
  }, [showNewSprintForm]);
  const rawStatus = String(
    selectedProject?.trackingStatus || selectedProject?.status || "",
  ).toLowerCase();
  const status =
    selectedProject?.progress === 100 || rawStatus === "completed"
      ? "completed"
      : rawStatus === "at-risk"
        ? "at-risk"
        : "on-track";
  // Prefer member data already cached with the selected project, then fall
  // back to either project list while the summary endpoint is still loading.
  const listedProject = (projects || []).find(
    (project) => String(project._id) === String(selectedProject?._id),
  );
  const listedKanbanProject = (kanbanProjects || []).find(
    (project) => String(project._id) === String(selectedProject?._id),
  );
  const rawResources =
    [
      selectedProject?.members,
      listedProject?.members,
      listedKanbanProject?.members,
    ].find((members) => Array.isArray(members) && members.length > 0) || [];

  // Viewers are clients, not teammates — they are added through "Add Client"
  // instead, which records them as clients rather than team members. The
  // backend rejects a viewer on POST /projects/:id/members for the same reason.
  const filteredMembers = (allUsers || []).filter(
    (m) =>
      (m.role || "").toLowerCase() !== "viewer" &&
      m.name?.toLowerCase().includes(search.toLowerCase()),
  );
  const visibleProjects = showDisabledProjects ? disabledProjects : projects;
  // ── Task helpers (unchanged) ─────────────────────────────────────
  const allTasks = normalizeLists(columns).flatMap((col) =>
    col.tasks.map((task) => ({ ...task, listName: col.name })),
  );

  const timelineTypes = [
    { name: "requirements", label: "Requirements", color: "#10B981" },
    { name: "design", label: "Design", color: "#6366F1" },
    { name: "frontend", label: "Front-End", color: "#2563EB" },
    { name: "backend", label: "Back-End", color: "#06B6D4" },
    { name: "others", label: "Development", color: "#F59E0B" },
    { name: "testing", label: "Testing", color: "#A855F7" },
    { name: "deployment", label: "Deployment", color: "#EC4899" },
  ];
  const getTimelineTypeColor = (typeName) =>
    timelineTypes.find(
      (type) => type.name === String(typeName || "").toLowerCase(),
    )?.color || "#9AA3B5";
  const timelineData = timelineTypes.map((type) => {
    const tasks = allTasks.filter(
      (task) => task.taskType?.toLowerCase() === type.name,
    );
    const completed = tasks.filter((task) => task.status === "done").length;
    return {
      ...type,
      percent: tasks.length ? Math.round((completed / tasks.length) * 100) : 0,
    };
  });

  const getInitials = (name = "") => name.trim().charAt(0).toUpperCase() || "?";

  const getProfileImageUrl = (member) => {
    if (!member) return "";
    const source =
      member.user && typeof member.user === "object" ? member.user : member;
    const picture =
      source.profilePicture ||
      source.avatar ||
      source.profileImage ||
      member.profilePicture ||
      member.avatar ||
      member.profileImage;
    if (typeof picture === "string") return picture;
    return picture?.url || picture?.secure_url || "";
  };
  // Normalizes a project-member object: { user: {...}, role, _id }
  // into a flat { _id, name, role, profilePicture } shape.
  const getMemberInfo = (m = {}) => {
    const source = m.user && typeof m.user === "object" ? m.user : m;
    return {
      _id: m._id || source._id,
      name: source.name || m.name || "",
      role: m.role || source.role || "",
      email: source.email || m.email || "",
      profilePicture:
        source.profilePicture || source.avatar || source.profileImage || "",
    };
  };
  const kanbanMembers =
    kanbanProjects
      .find((project) => String(project._id) === String(selectedProject?._id))
      ?.members?.map(getMemberInfo) || [];
  const resources = rawResources.length
    ? rawResources.map((rawMember) => {
        const member = getMemberInfo(rawMember);
        const rawIds = [rawMember?._id, rawMember?.user?._id, rawMember?.userId]
          .filter(Boolean)
          .map(String);
        const kanbanMember = kanbanMembers.find(
          (candidate) =>
            rawIds.includes(String(candidate._id)) ||
            (member.email && candidate.email === member.email),
        );
        return {
          ...member,
          profilePicture:
            member.profilePicture || kanbanMember?.profilePicture || "",
        };
      })
    : kanbanMembers;
  const sprintRows = Object.entries(sprintMetadata)
    .filter(([key, value]) => value?.name && key === (value.id || key))
    .map(([key, sprint], index) => {
      const start = sprint.startDate || null;
      const end = sprint.endDate || null;
      const duration =
        sprint.duration ||
        (start && end
          ? `${Math.max(1, Math.ceil((new Date(end) - new Date(start)) / 86400000) + 1)} days`
          : "—");
      const rawStatus = String(sprint.status || "").toLowerCase();
      const resolvedDuration = sprint.duration || duration;
      const status =
        rawStatus === "milestone met"
          ? "Milestone Met"
          : rawStatus === "completed"
            ? "Completed"
            : rawStatus === "active" ||
                rawStatus === "in progress" ||
                rawStatus === "in-progress"
              ? "Active"
              : "Scheduled";
      return {
        id: sprint.id || key,
        sprint: sprint.name || `Sprint ${index + 1}`,
        scope: sprint.scope || sprint.goal || "—",
        tasks: [],
        start,
        end,
        totalHours: 0,
        duration: resolvedDuration,
        status,
        // The Kanban lists under this sprint.
        lists: sprint.lists || [],
        listNames: sprint.listNames || [],
        // Server-computed, from the tasks in those lists.
        hasLists: Boolean(sprint.hasLists),
        totalTasks: sprint.totalTasks || 0,
        completed: sprint.completedTasks || 0,
        completedTasks: sprint.completedTasks || 0,
        progress: sprint.progress || 0,
      };
    });
  const formatSprintDateRange = (start, end) => {
    if (!start || !end) return "Dates not set";
    const startDate = new Date(`${String(start).slice(0, 10)}T12:00:00`);
    const endDate = new Date(`${String(end).slice(0, 10)}T12:00:00`);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime()))
      return "Dates not set";
    const startLabel = startDate.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
    });
    const endLabel = endDate.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
    });
    return `${startLabel} – ${endLabel}`;
  };
  const formatSprintWeeks = (start, end, duration) => {
    if (!start || !end) return duration || "Duration not set";
    const days = Math.max(
      1,
      Math.ceil(
        (new Date(`${String(end).slice(0, 10)}T12:00:00`) -
          new Date(`${String(start).slice(0, 10)}T12:00:00`)) /
          86400000,
      ) + 1,
    );
    const weeks = Math.ceil(days / 7);
    return `${weeks} ${weeks === 1 ? "Week" : "Weeks"} (W1–W${weeks})`;
  };

  // The progress bar for one sprint, always labelled with its percentage.
  //
  // A sprint with no lists shows "—": nothing has been scoped to it, which is
  // a different thing from nothing being done. A sprint whose lists are empty
  // genuinely is 0%, and shows a bar.
  //
  // `size` lets the details panel render a taller bar than the table row
  // without duplicating the logic.
  const renderSprintProgress = (sprint, size = "sm") => {
    if (!sprint?.hasLists) {
      return (
        <span
          className="text-[#8A97AB]"
          title="No lists are under this sprint yet"
        >
          —
        </span>
      );
    }
    const percent = Math.max(0, Math.min(100, Number(sprint.progress) || 0));
    const done = sprint.completedTasks ?? 0;
    const total = sprint.totalTasks ?? 0;
    const tall = size === "lg";
    // No min-width: the column is a percentage of the table, so a fixed
    // floor would push the row wider than its container and bring back the
    // horizontal scrollbar.
    return (
      <div className="w-full">
        <div className="flex items-center gap-2">
          <div
            className={`flex-1 overflow-hidden rounded-full bg-[#E2E8F0] ${tall ? "h-2.5" : "h-1.5"}`}
          >
            <div
              className={`h-full rounded-full transition-all duration-500 ${percent === 100 ? "bg-[#22A06B]" : "bg-[#4F46E5]"}`}
              style={{ width: `${percent}%` }}
            />
          </div>
          <span
            className={`shrink-0 font-semibold text-[#253858] ${tall ? "text-[14px]" : "text-[12px]"}`}
          >
            {percent}%
          </span>
        </div>
        <p
          className={`mt-1 text-[#8A97AB] ${tall ? "text-[12px]" : "text-[11px]"}`}
        >
          {done} of {total} task{total === 1 ? "" : "s"} done
        </p>
      </div>
    );
  };

  const formatSprintStatus = (status) =>
    status === "Milestone Met" ? "Completed" : status;
  const getSprintStatusClass = (status) => {
    if (status === "Completed") return "bg-[#DCFCE7] text-[#15803D]";
    if (status === "Active") return "bg-[#DBEAFE] text-[#1D4ED8]";
    return "bg-[#E2E8F0] text-[#475569]";
  };
  const sprintStatusCounts = sprintRows.reduce(
    (counts, sprint) => ({
      ...counts,
      [sprint.status]: (counts[sprint.status] || 0) + 1,
    }),
    { Completed: 0, Active: 0, Scheduled: 0 },
  );
  const ganttDates = sprintRows
    .flatMap((sprint) => [sprint.start, sprint.end])
    .filter((date) => date && !Number.isNaN(new Date(date).getTime()))
    .map((date) => new Date(date));
  const firstGanttDate = ganttDates.length
    ? new Date(Math.min(...ganttDates.map((date) => date.getTime())))
    : new Date();
  const monthKey = (date) => `${date.getFullYear()}-${date.getMonth()}`;
  const firstAvailableGanttMonth = ganttDates.length
    ? new Date(Math.min(...ganttDates.map((date) => date.getTime())))
    : new Date(firstGanttDate.getFullYear(), firstGanttDate.getMonth(), 1);
  firstAvailableGanttMonth.setDate(1);
  const lastAvailableGanttMonth = ganttDates.length
    ? new Date(Math.max(...ganttDates.map((date) => date.getTime())))
    : firstAvailableGanttMonth;
  lastAvailableGanttMonth.setDate(1);
  const availableGanttMonths = [];
  const monthCursor = new Date(firstAvailableGanttMonth);
  while (monthCursor <= lastAvailableGanttMonth) {
    availableGanttMonths.push(new Date(monthCursor));
    monthCursor.setMonth(monthCursor.getMonth() + 1);
  }
  const selectedGanttMonth =
    ganttMonth &&
    availableGanttMonths.some(
      (month) => monthKey(month) === monthKey(ganttMonth),
    )
      ? ganttMonth
      : firstAvailableGanttMonth;
  const visibleGanttMonth = selectedGanttMonth;
  const canGoToPreviousGanttMonth = visibleGanttMonth > availableGanttMonths[0];
  const canGoToNextGanttMonth =
    visibleGanttMonth < availableGanttMonths[availableGanttMonths.length - 1];
  const setGanttMonth = (nextMonth) => {
    if (!nextMonth) {
      setGanttMonthState(null);
      return;
    }
    const direction = nextMonth >= visibleGanttMonth ? 1 : -1;
    const nextAvailableMonth = availableGanttMonths
      .filter((month) =>
        direction > 0 ? month > visibleGanttMonth : month < visibleGanttMonth,
      )
      .sort((first, second) =>
        direction > 0 ? first - second : second - first,
      )[0];
    setGanttMonthState(nextAvailableMonth || visibleGanttMonth);
  };
  const ganttStart = new Date(
    visibleGanttMonth.getFullYear(),
    visibleGanttMonth.getMonth(),
    1,
  );
  const ganttEnd = new Date(
    visibleGanttMonth.getFullYear(),
    visibleGanttMonth.getMonth() + 1,
    0,
  );
  const ganttDayCount = ganttEnd.getDate();
  const ganttWeekCount = Math.ceil(ganttDayCount / 7);
  const visibleGanttSprints = sprintRows;
  const ganttWeeks = Array.from({ length: ganttWeekCount }, (_, index) => {
    const date = new Date(ganttStart);
    date.setDate(date.getDate() + index * 7);
    return date;
  });
  const getGanttBarStyle = (sprint) => {
    if (!ganttStart || !sprint.start || !sprint.end) return { display: "none" };
    const sprintStart = new Date(sprint.start);
    const sprintEnd = new Date(sprint.end);
    const visibleStart = new Date(
      Math.max(sprintStart.getTime(), ganttStart.getTime()),
    );
    const visibleEnd = new Date(
      Math.min(sprintEnd.getTime(), ganttEnd.getTime()),
    );
    if (visibleStart > visibleEnd) return { display: "none" };
    const left = Math.max(0, (visibleStart - ganttStart) / 86400000);
    const width = Math.max(0.65, (visibleEnd - visibleStart) / 86400000 + 1);
    return {
      left: `${(left / ganttDayCount) * 100}%`,
      width: `${Math.min(width / ganttDayCount, 1 - left / ganttDayCount) * 100}%`,
      backgroundColor:
        sprint.status === "Completed"
          ? "#10B981"
          : sprint.status === "Active"
            ? "#4F46E5"
            : "#94A3B8",
    };
  };

  useEffect(() => {
    if (!showGanttChart) return undefined;
    const chart = document.querySelector('[aria-label="Sprint plan"]');
    if (!chart) return undefined;
    const previousButton = chart.querySelector('[aria-label="Previous month"]');
    const nextButton = chart.querySelector('[aria-label="Next month"]');
    if (previousButton)
      previousButton.style.visibility = canGoToPreviousGanttMonth
        ? "visible"
        : "hidden";
    if (nextButton)
      nextButton.style.visibility = canGoToNextGanttMonth
        ? "visible"
        : "hidden";
    return () => {
      if (previousButton) previousButton.style.visibility = "";
      if (nextButton) nextButton.style.visibility = "";
    };
  }, [
    showGanttChart,
    visibleGanttMonth,
    canGoToPreviousGanttMonth,
    canGoToNextGanttMonth,
  ]);

  // Sprint data is intentionally held in React state only, so refreshing clears it.
  // Sprints live on the server. They are held in `sprintMetadata` keyed by
  // id — the same shape the page already used locally — so `sprintRows`, the
  // Gantt and the details panel all keep working unchanged.
  const loadSprints = async (projectRefId) => {
    if (!projectRefId) return;
    setIsLoadingSprints(true);
    try {
      const response = await sprintService.getSprints(projectRefId);
      const byId = {};
      (response.data || []).forEach((sprint) => {
        byId[sprint._id] = {
          id: sprint._id,
          name: sprint.name,
          scope: sprint.scope,
          duration: sprint.duration,
          startDate: sprint.startDate,
          endDate: sprint.endDate,
          status: sprint.status,
          // Ids preselect the form; names are what the table shows.
          lists: sprint.listIds || [],
          listNames: sprint.listNames || [],
          // Progress across the tasks in this sprint's lists, computed by
          // the server. hasLists false means nothing is scoped to it yet.
          hasLists: sprint.hasLists,
          totalTasks: sprint.totalTasks,
          completedTasks: sprint.completedTasks,
          progress: sprint.progress,
        };
      });
      setSprintMetadata(byId);
      setNewSprintError("");
    } catch (error) {
      setNewSprintError(
        error.response?.data?.message ||
          error.message ||
          "Could not load the sprints.",
      );
    } finally {
      setIsLoadingSprints(false);
    }
  };

  // The lists a sprint can cover. Fetched when the form opens rather than
  // with the sprints, since it depends on which sprint is being edited.
  const loadAvailableLists = async (sprintId) => {
    if (!selectedProject?._id) return;
    try {
      const response = await sprintService.getAvailableLists(
        selectedProject._id,
        sprintId,
      );
      setAvailableLists(response.data || []);
      setAutoImportAvailable(Boolean(response.autoImportAvailable));
      setDriveConfig(response.googleDrive || { available: false });
    } catch {
      // Not fatal — the sprint can still be saved without picking lists.
      setAvailableLists([]);
    }
  };

  // Opens the manual form, from the chooser or directly.
  const openManualSprintForm = () => {
    setShowAddSprintChooser(false);
    setEditingSprintId(null);
    setNewSprintDraft({
      name: "",
      scope: "",
      duration: "",
      startDate: "",
      endDate: "",
      status: "Scheduled",
      lists: [],
    });
    setNewSprintError("");
    setShowNewSprintForm(true);
    loadAvailableLists();
  };

  const closeSprintImport = () => {
    setShowSprintImport(false);
    setSprintImportFile(null);
    setDriveFile(null);
    setSprintImportRows([]);
    setSprintImportError("");
  };

  // "Upload from this device" — straight to the import dialog, which opens
  // the file browser for them.
  const openSprintImportFromDevice = () => {
    setShowAddSprintChooser(false);
    setSprintImportRows([]);
    setSprintImportFile(null);
    setDriveFile(null);
    setSprintImportError("");
    setShowSprintImport(true);
    // Open the file browser immediately, so the option does what it says
    // rather than showing another button to press.
    requestAnimationFrame(() => sprintFileInputRef.current?.click());
  };

  // Opens Google's own picker. The token it returns is scoped to the picked
  // file only and is handed to our backend, which does the download — the
  // browser never touches the file's bytes.
  const chooseFromGoogleDrive = async () => {
    setSprintImportError("");
    setIsOpeningDrive(true);
    try {
      const picked = await pickFromGoogleDrive(driveConfig);
      // null means the user closed the picker without choosing.
      if (picked) {
        setDriveFile(picked);
        setSprintImportFile(null);
      }
      return picked;
    } catch (error) {
      setSprintImportError(error.message || "Could not open Google Drive.");
      return null;
    } finally {
      setIsOpeningDrive(false);
    }
  };

  // "Upload from Google Drive" — opens the picker from the chooser, then
  // shows the import dialog with the chosen file already selected. If they
  // close the picker without choosing, the chooser simply stays put.
  const openSprintImportFromDrive = async () => {
    setSprintImportRows([]);
    setSprintImportFile(null);
    const picked = await chooseFromGoogleDrive();
    if (!picked) return;
    setShowAddSprintChooser(false);
    setShowSprintImport(true);
  };

  // Reads the sprint table out of the chosen file. Nothing is saved yet —
  // the rows come back for review first, because a misread date or a
  // duplicated sprint is much easier to fix before it is in the table.
  const parseSprintFile = async () => {
    if ((!sprintImportFile && !driveFile) || !selectedProject?._id) return;
    setIsParsingSprintFile(true);
    setSprintImportError("");
    try {
      // Whichever source was chosen; the two return the same shape.
      const response = driveFile
        ? await sprintService.parseSprintDriveFile(
            selectedProject._id,
            driveFile,
          )
        : await sprintService.parseSprintFile(
            selectedProject._id,
            sprintImportFile,
          );
      // Rows flagged as duplicates start unticked, so importing again does
      // not quietly create a second "Sprint 1".
      setSprintImportRows(
        (response.data || []).map((row) => ({
          ...row,
          include: !row.duplicate,
        })),
      );
    } catch (error) {
      setSprintImportError(
        error.response?.data?.message ||
          error.message ||
          "Could not read that file.",
      );
    } finally {
      setIsParsingSprintFile(false);
    }
  };

  const confirmSprintImport = async () => {
    const chosen = sprintImportRows.filter((row) => row.include);
    if (!chosen.length || !selectedProject?._id) return;
    setIsImportingSprints(true);
    setSprintImportError("");
    try {
      await sprintService.importSprints(
        selectedProject._id,
        chosen.map(({ include, duplicate, ...row }) => row),
      );
      closeSprintImport();
      await loadSprints(selectedProject._id);
    } catch (error) {
      setSprintImportError(
        error.response?.data?.message ||
          error.message ||
          "Could not import those sprints.",
      );
    } finally {
      setIsImportingSprints(false);
    }
  };

  const toggleSprintList = (listId) => {
    setNewSprintDraft((draft) => {
      const current = draft.lists || [];
      return {
        ...draft,
        lists: current.includes(listId)
          ? current.filter((id) => id !== listId)
          : [...current, listId],
      };
    });
  };

  const saveNewSprint = async () => {
    const name = newSprintDraft.name.trim();
    if (!name || !selectedProject?._id) return;
    if (
      newSprintDraft.startDate &&
      newSprintDraft.endDate &&
      newSprintDraft.endDate < newSprintDraft.startDate
    ) {
      setNewSprintError("End date must be on or after the start date.");
      return;
    }

    setIsSavingSprint(true);
    setNewSprintError("");
    try {
      const payload = {
        name,
        scope: newSprintDraft.scope.trim(),
        duration: newSprintDraft.duration.trim(),
        startDate: newSprintDraft.startDate,
        endDate: newSprintDraft.endDate,
        status: newSprintDraft.status,
        lists: newSprintDraft.lists || [],
      };
      if (editingSprintId) {
        await sprintService.updateSprint(
          selectedProject._id,
          editingSprintId,
          payload,
        );
      } else {
        await sprintService.createSprint(selectedProject._id, payload);
      }
      setNewSprintDraft({
        name: "",
        scope: "",
        duration: "",
        startDate: "",
        endDate: "",
        status: "Scheduled",
        lists: [],
      });
      setEditingSprintId(null);
      setShowNewSprintForm(false);
      await loadSprints(selectedProject._id);
    } catch (error) {
      setNewSprintError(
        error.response?.data?.message ||
          error.message ||
          "Could not save the sprint.",
      );
    } finally {
      setIsSavingSprint(false);
    }
  };

  const editSprint = (sprint) => {
    setEditingSprintId(sprint.id);
    setNewSprintDraft({
      name: sprint.sprint,
      scope: sprint.scope === "—" ? "" : sprint.scope,
      duration: sprint.duration === "—" ? "" : sprint.duration,
      startDate: sprint.start ? String(sprint.start).slice(0, 10) : "",
      endDate: sprint.end ? String(sprint.end).slice(0, 10) : "",
      status: sprint.status === "Milestone Met" ? "Completed" : sprint.status,
      lists: sprint.lists || [],
    });
    setNewSprintError("");
    setShowNewSprintForm(true);
    // Asked for with this sprint's id, so its own lists are offered as free
    // rather than shown as taken by itself.
    loadAvailableLists(sprint.id);
  };

  const deleteSprint = async (sprint) => {
    if (!selectedProject?._id || !sprint.id) return;
    setDeleteConfirmSprint(sprint);
  };

  const confirmDeleteSprint = async () => {
    const sprint = deleteConfirmSprint;
    if (!sprint || !selectedProject?._id) return;
    try {
      await sprintService.deleteSprint(selectedProject._id, sprint.id);
      setDeleteConfirmSprint(null);
      // Close the details panel if it was showing the sprint just removed.
      setSelectedSprint((current) =>
        current && current.id === sprint.id ? null : current,
      );
      await loadSprints(selectedProject._id);
    } catch (error) {
      setDeleteConfirmSprint(null);
      setNewSprintError(
        error.response?.data?.message ||
          error.message ||
          "Could not delete the sprint.",
      );
    }
  };

  const totalTaskItems = allTasks.length;
  const taskStartIndex = (taskPage - 1) * tasksPerPage;
  const taskEndIndex = taskStartIndex + tasksPerPage;
  const currentTasks = allTasks.slice(taskStartIndex, taskEndIndex);
  const totalTaskPages = Math.ceil(totalTaskItems / tasksPerPage);
  const taskPageWindowStart = Math.min(
    taskPage,
    Math.max(1, totalTaskPages - 2),
  );
  const visibleTaskPages = Array.from(
    { length: Math.min(3, totalTaskPages) },
    (_, index) => taskPageWindowStart + index,
  );

  const updateMeetingMinuteDraft = (field, value) => {
    setMeetingMinuteDraft((draft) => ({ ...draft, [field]: value }));
  };

  const toggleMeetingAttendee = (memberId) => {
    setMeetingMinuteDraft((draft) => ({
      ...draft,
      attendeeIds: draft.attendeeIds.includes(memberId)
        ? draft.attendeeIds.filter((id) => id !== memberId)
        : [...draft.attendeeIds, memberId],
    }));
  };

  // Every field on the left side of the form is compulsory. This drives the
  // Save button's disabled state, and the server re-checks the same rules so
  // a stale tab can't slip an incomplete record through.
  const savedDecisions = meetingDecisions.filter((d) => d.trim());
  const isMeetingMinuteComplete =
    Boolean(meetingMinuteDraft.title.trim()) &&
    Boolean(meetingMinuteDraft.date) &&
    Boolean(meetingMinuteDraft.time) &&
    Boolean(meetingMinuteDraft.type) &&
    Boolean(meetingMinuteDraft.taskId) &&
    meetingMinuteDraft.attendeeIds.length > 0 &&
    Boolean(meetingMinuteDraft.notes.trim()) &&
    savedDecisions.length > 0;

  const resetMeetingMinuteForm = () => {
    setMeetingMinuteDraft({
      title: "",
      date: "",
      time: "",
      type: "Client Review",
      taskId: "",
      attendeeIds: [],
      notes: "",
      decisions: "",
      changeRequest: "",
    });
    setMeetingDecisions([]);
    setConfirmedDecisionIndexes([]);
    setMeetingAttachments([]);
    setMeetingChangeRequests([]);
    setMeetingMinuteError("");
    setEditingMinuteId(null);
    setHasRaisedChange(false);
    setShowTranscriptBox(false);
    setTranscriptText("");
    setDraftNotice("");
  };

  // Fills the notes and decisions from a pasted transcript.
  //
  // Deliberately a DRAFT: the decisions land unconfirmed, so the tick-to-
  // confirm step forces the user to read each one before the minutes can be
  // saved. These decisions feed change requests, which feed billing, so a
  // wrong line that nobody read is the failure worth designing against.
  const closeTranscriptDialog = () => {
    setShowTranscriptBox(false);
    setTranscriptText("");
    setTranscriptFile(null);
    setTranscriptError("");
    setTranscriptMode("paste");
  };

  const draftFromTranscript = async () => {
    if (!selectedProject?._id) return;
    const pasted = transcriptText.trim();
    const usingFile = transcriptMode === "upload";
    if (usingFile ? !transcriptFile : !pasted) return;

    setIsDrafting(true);
    setTranscriptError("");
    setMeetingMinuteError("");
    setDraftNotice("");
    try {
      const response = usingFile
        ? await meetingMinuteService.draftFromTranscriptFile(
            selectedProject._id,
            transcriptFile,
          )
        : await meetingMinuteService.draftFromTranscript(
            selectedProject._id,
            pasted,
          );
      const { notes, decisions } = response.data || {};

      if (notes) updateMeetingMinuteDraft("notes", notes);
      if (Array.isArray(decisions) && decisions.length) {
        setMeetingDecisions(decisions);
        // Empty on purpose — nothing is pre-ticked.
        setConfirmedDecisionIndexes([]);
      }

      closeTranscriptDialog();
      setDraftNotice(
        `Draft filled in${
          decisions?.length
            ? ` with ${decisions.length} suggested decision${decisions.length === 1 ? "" : "s"}`
            : ""
        }. Check every line, then tick each decision to keep it.`,
      );
    } catch (error) {
      // Shown inside the dialog, which stays open so the user can fix the
      // input rather than losing what they pasted.
      setTranscriptError(
        error.response?.data?.message ||
          error.message ||
          "Could not draft from that transcript.",
      );
    } finally {
      setIsDrafting(false);
    }
  };

  // Opens the Add Minutes form prefilled with a saved card, so editing uses
  // exactly the same view rather than a second layout. Admin only — the icon
  // that calls this is hidden for everyone else and the backend re-checks.
  const startEditMinute = async (minute) => {
    if (!isAdmin || !minute) return;
    setMeetingMinuteError("");
    const minuteId = minute._id || minute.id;
    setEditingMinuteId(minuteId);
    setMeetingMinuteDraft({
      title: minute.title || "",
      // `dateInput` is the YYYY-MM-DD form of the meeting date; `date` is the
      // human-readable version the card prints and is not a valid input value.
      date: minute.dateInput || "",
      time: minute.time || "",
      type: minute.type || "Client Review",
      taskId: minute.taskId || "",
      attendeeIds: minute.attendeeIds || [],
      notes: minute.notes || "",
      decisions: "",
      changeRequest: "",
    });
    const decisions = minute.decisions || [];
    setMeetingDecisions(decisions);
    // Saved decisions come back confirmed (read-only with no tick button), the
    // same state they were left in, so they are not mistaken for unsaved text.
    setConfirmedDecisionIndexes(decisions.map((_, index) => index));
    // Already-uploaded attachments are not re-sent on save; only newly picked
    // files carry a `file` handle, and only those are uploaded.
    setMeetingAttachments([]);
    setShowMeetingMinuteForm(true);

    // Load the changes already raised from this meeting and mark them
    // `raised`, so they are visible while editing but are not posted again.
    // Anything typed during the edit is unraised and does get a new row.
    setMeetingChangeRequests([]);
    setHasRaisedChange(false);
    if (selectedProject?._id && minuteId) {
      try {
        const response = await meetingMinuteService.getMinuteChangeRequests(
          selectedProject._id,
          minuteId,
        );
        const existing = (response.data || []).map((request) => ({
          id: request._id,
          text: request.title,
          ccrNo: request.ccrNo,
          raised: true,
        }));
        setMeetingChangeRequests(existing);
        // Already-raised changes mean the button has done its job before.
        if (existing.length) setHasRaisedChange(true);
      } catch {
        // The edit form is still usable without this; the worst case is the
        // panel showing nothing previously raised.
      }
    }
  };

  // Pulls the project's saved minutes and maps them into the exact shape the
  // cards already render, so nothing about the layout has to change.
  const loadMeetingMinutes = async (projectRefId) => {
    if (!projectRefId) return;
    setIsLoadingMinutes(true);
    try {
      const response = await meetingMinuteService.getMinutes(projectRefId);
      setCanReviewSubmissions(Boolean(response.canReview));
      const minutes = (response.data || []).map((m) => ({
        ...meetingMinuteService.fromServerMinute(m),
        // Carried through so a pending card can be badged and reviewed.
        approvalStatus: m.approvalStatus,
        isPending: m.isPending,
        createdByName: m.createdByName,
      }));
      setPendingMinutes(minutes.filter((m) => m.isPending));
      // The list endpoint returns counts, not the decision text itself. The
      // right-hand panel prints the text, so each card is topped up with its
      // detail — cheap here because a project has few meetings.
      const detailed = await Promise.all(
        minutes.map(async (minute) => {
          try {
            const detail = await meetingMinuteService.getMinuteById(
              projectRefId,
              minute._id,
            );
            // Keep the approval flags the list already resolved — the mapper
            // only knows about the meeting itself.
            return {
              ...meetingMinuteService.fromServerMinute(detail.data),
              approvalStatus: detail.data.approvalStatus,
              isPending: detail.data.isPending,
              createdByName: detail.data.createdByName,
            };
          } catch {
            // A single unreadable meeting shouldn't blank the whole tab.
            return minute;
          }
        }),
      );
      setMeetingMinutes(detailed);
    } catch (error) {
      setMeetingMinuteError(
        error.response?.data?.message ||
          error.message ||
          "Could not load meeting minutes.",
      );
    } finally {
      setIsLoadingMinutes(false);
    }
  };

  // Pulls the project's change requests and maps each into a table row.
  const loadChangeRequests = async (projectRefId) => {
    if (!projectRefId) return;
    setIsLoadingChangeRequests(true);
    try {
      const response =
        await changeRequestService.getChangeRequests(projectRefId);
      setChangeRequests(
        (response.data || []).map(changeRequestService.toTableRow),
      );
      // Pending rows never enter the table — they sit in the panel above it.
      setPendingRequests(
        (response.pending || []).map(changeRequestService.toTableRow),
      );
      setCanReviewSubmissions(Boolean(response.canReview));
      setChangeRequestError("");
    } catch (error) {
      setChangeRequestError(
        error.response?.data?.message ||
          error.message ||
          "Could not load change requests.",
      );
    } finally {
      setIsLoadingChangeRequests(false);
    }
  };

  // Approve or reject something a client submitted. Admin/manager only; the
  // backend re-checks, so hiding the controls is presentation, not security.
  //
  // Approving a change request is when its CCR number is allocated and it
  // joins the table. Approving minutes also releases the change requests
  // raised from the same meeting.
  const reviewSubmission = async (kind, id, decision) => {
    if (!selectedProject?._id || !canReviewSubmissions) return;
    // A rejection is worth a sentence — the client sees it.
    let note = "";
    if (decision === "rejected") {
      // eslint-disable-next-line no-alert
      const reason = window.prompt(
        "Why is this being rejected? The client will see this.",
        "",
      );
      // Cancel means abandon the rejection, not reject without a reason.
      if (reason === null) return;
      note = reason;
    }

    setReviewingId(id);
    setChangeRequestError("");
    try {
      if (kind === "minute") {
        await meetingMinuteService.reviewMinute(
          selectedProject._id,
          id,
          decision,
          note,
        );
      } else {
        await changeRequestService.reviewChangeRequest(
          selectedProject._id,
          id,
          decision,
          note,
        );
      }
      // Both lists can change: approving minutes releases their requests.
      await loadChangeRequests(selectedProject._id);
      await loadMeetingMinutes(selectedProject._id);
    } catch (error) {
      setChangeRequestError(
        error.response?.data?.message ||
          error.message ||
          "Could not record that decision.",
      );
    } finally {
      setReviewingId(null);
    }
  };

  // Opens the detail card under a clicked row, or closes it if that row is
  // already open (the toggle). The card is fetched rather than built from the
  // row, because the meeting's notes, decisions and attachments are not in
  // the table's data.
  const toggleRequestDetail = async (requestId) => {
    if (openRequestId === requestId) {
      setOpenRequestId(null);
      setRequestDetail(null);
      return;
    }
    setOpenRequestId(requestId);
    setRequestDetail(null);
    if (!selectedProject?._id) return;
    setIsLoadingRequestDetail(true);
    try {
      const response = await changeRequestService.getChangeRequestById(
        selectedProject._id,
        requestId,
      );
      setRequestDetail(changeRequestService.toDetailCard(response.data));
    } catch (error) {
      setChangeRequestError(
        error.response?.data?.message ||
          error.message ||
          "Could not load the change request.",
      );
      setOpenRequestId(null);
    } finally {
      setIsLoadingRequestDetail(false);
    }
  };

  // Clicking anywhere outside the table + card closes the card. Bound only
  // while one is open, so it costs nothing the rest of the time.
  useEffect(() => {
    if (!openRequestId) return;
    const handleOutsideClick = (event) => {
      if (
        changeRequestTableRef.current &&
        !changeRequestTableRef.current.contains(event.target)
      ) {
        setOpenRequestId(null);
        setRequestDetail(null);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [openRequestId]);

  // "New Request" — uses the same Add Minutes form, so saving it records a
  // real meeting and raises a change request against it. Both tabs are
  // therefore updated by one save: the minute appears under Meeting Minutes,
  // and its CCR row appears in the table with the meeting's date, time,
  // attendees and decision count.
  const saveNewChangeRequest = async () => {
    if (!isMeetingMinuteComplete || !selectedProject?._id) return;
    setIsSavingRequest(true);
    setChangeRequestError("");

    try {
      const payload = meetingMinuteService.toServerPayload(meetingMinuteDraft, {
        decisions: savedDecisions,
        memberIds: resources.map(getMemberInfo).map((member) => member._id),
      });
      const response = await meetingMinuteService.createMinute(
        selectedProject._id,
        payload,
      );
      const minute = response.data;

      // Attachments picked on the form upload against the new meeting.
      const files = meetingAttachments.map((item) => item.file).filter(Boolean);
      if (files.length && minute?._id) {
        try {
          await meetingMinuteService.uploadMinuteAttachments(
            selectedProject._id,
            minute._id,
            files,
          );
        } catch {
          setChangeRequestError(
            "Saved, but the attachments could not be uploaded.",
          );
        }
      }

      // The change request itself. Any changes listed in the sidebar each get
      // their own row; with none listed, the meeting title is the change, so
      // the tab always gains the row the button promised.
      const raised = meetingChangeRequests.filter((r) => !r.raised);
      const titles = raised.length
        ? [...raised].reverse().map((r) => r.text)
        : [meetingMinuteDraft.title.trim()];
      for (const title of titles) {
        await changeRequestService.createChangeRequest(selectedProject._id, {
          title,
          sourceMinuteId: minute._id,
        });
      }

      setShowNewRequestForm(false);
      resetMeetingMinuteForm();
      // Both tabs are refreshed, since both now have new content.
      await loadChangeRequests(selectedProject._id);
      await loadMeetingMinutes(selectedProject._id);
    } catch (error) {
      setChangeRequestError(
        error.response?.data?.message ||
          error.message ||
          "Could not raise the change request.",
      );
    } finally {
      setIsSavingRequest(false);
    }
  };

  // ── The Auto Fill dialog ─────────────────────────────────────────
  //
  // Asks for the transcript two ways — paste it, or upload a Word/text
  // document — then fills the notes and decisions. Rendered once, by both
  // tabs, alongside the shared form.
  const renderAutoFillDialog = () =>
    showTranscriptBox ? (
      <>
        <div
          className="fixed inset-0 z-50 bg-black/30"
          onClick={closeTranscriptDialog}
        />
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Auto fill from transcript"
          className="fixed left-1/2 top-1/2 z-50 w-[560px] max-w-[calc(100vw-32px)] max-h-[90vh] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-[14px] bg-white p-6 shadow-2xl"
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-[20px] font-medium text-[#151A2D]">
                Auto fill from a transcript
              </h3>
              <p className="mt-1 text-[12px] leading-[18px] text-[#667085]">
                Fills the discussion notes and the decisions made. Nothing is
                saved — you review and edit before saving.
              </p>
            </div>
            <button
              type="button"
              onClick={closeTranscriptDialog}
              aria-label="Close"
              className="shrink-0 text-[#60709C] hover:text-[#151A2D]"
            >
              <FiX size={18} />
            </button>
          </div>

          {/* Paste or upload. */}
          <div className="mt-5 flex gap-2">
            {[
              ["paste", "Paste text"],
              ["upload", "Upload document"],
            ].map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                onClick={() => {
                  setTranscriptMode(mode);
                  setTranscriptError("");
                }}
                className={`h-9 flex-1 rounded-[7px] border text-[12px] font-medium ${transcriptMode === mode ? "border-[#4253C7] bg-[#F4F6FF] text-[#4253C7]" : "border-[#DCE2F0] text-[#667085]"}`}
              >
                {label}
              </button>
            ))}
          </div>

          {transcriptMode === "paste" ? (
            <textarea
              value={transcriptText}
              onChange={(e) => {
                setTranscriptText(e.target.value);
                setTranscriptError("");
              }}
              rows="9"
              className="mt-4 w-full rounded-[7px] border border-[#DCE2F0] p-3 text-[13px] font-normal outline-none"
              placeholder="Paste the meeting transcript here. Speaker labels help — keep them if you have them."
            />
          ) : (
            <div className="mt-4">
              <input
                ref={transcriptFileInputRef}
                type="file"
                accept=".docx,.txt,.md,.vtt,.srt,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                className="hidden"
                onChange={(e) => {
                  setTranscriptFile(e.target.files?.[0] || null);
                  setTranscriptError("");
                  e.target.value = "";
                }}
              />
              <button
                type="button"
                onClick={() => transcriptFileInputRef.current?.click()}
                className="flex min-h-[120px] w-full flex-col items-center justify-center rounded-[8px] border border-dashed border-[#B8C0CC] bg-[#FBFCFF] px-5 text-center hover:bg-[#F7F8FC]"
              >
                <span className="text-[13px] font-medium text-[#4253C7]">
                  {transcriptFile
                    ? "Choose a different file"
                    : "+ Choose a document"}
                </span>
                <span className="mt-1 text-[11px] text-[#7D89A3]">
                  Word (.docx) or text (.txt, .vtt) · up to 10 MB
                </span>
              </button>
              {transcriptFile && (
                <p
                  className="mt-2 truncate text-[12px] text-[#465675]"
                  title={transcriptFile.name}
                >
                  Selected: {transcriptFile.name}
                </p>
              )}
              <p className="mt-2 text-[11px] leading-[16px] text-[#7D89A3]">
                The document is read for its text and then discarded — it is not
                stored. Older .doc files are not supported; save as .docx first.
              </p>
            </div>
          )}

          {transcriptError && (
            <p className="mt-3 rounded-[7px] bg-[#FEF3F2] px-3 py-2 text-[12px] text-[#B42318]">
              {transcriptError}
            </p>
          )}

          <div className="mt-5 flex items-center justify-between gap-3 border-t border-[#E8ECF5] pt-4">
            <p className="text-[11px] leading-[16px] text-[#7D89A3]">
              Every suggested decision still has to be ticked before it saves.
            </p>
            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                onClick={closeTranscriptDialog}
                className="rounded-[7px] border border-[#DCE2F0] px-3 py-2 text-[12px]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={draftFromTranscript}
                disabled={
                  isDrafting ||
                  (transcriptMode === "upload"
                    ? !transcriptFile
                    : !transcriptText.trim())
                }
                className="rounded-[7px] bg-[#4253C7] px-4 py-2 text-[12px] font-medium text-white disabled:opacity-50"
              >
                {isDrafting ? "Reading…" : "Fill in"}
              </button>
            </div>
          </div>
        </div>
      </>
    ) : null;

  // ── The form sidebar ─────────────────────────────────────────────
  //
  // The "raise a change" and Attachments panels. Shared by both tabs for
  // the same reason as the form itself.
  const renderMinutesSidebar = () => (
    <aside className="space-y-4">
      <div
        ref={changeRequestCardRef}
        className="rounded-[20px] bg-white p-5 shadow-[0_0_4px_rgba(0,0,0,0.06)]"
      >
        <h4 className="text-[15px] font-semibold text-[#20263A]">
          Raise a change from this meeting
        </h4>
        <p className="mt-3 text-[12px] leading-[18px] text-[#5E6C8D]">
          Clients ask for changes verbally. Convert a line of the minutes into a
          tracked change request so it never becomes unbilled scope.
        </p>
        {showChangeRequestInput && (
          <div className="relative mt-3">
            <input
              value={meetingMinuteDraft.changeRequest}
              onChange={(e) =>
                updateMeetingMinuteDraft("changeRequest", e.target.value)
              }
              className="h-[40px] w-full rounded-[8px] border border-[#DFE5F1] bg-[#FBFCFF] px-3 pr-10 text-[12px] outline-none placeholder:text-[#A7B2CA]"
              placeholder="Client asked for..."
            />
            <button
              type="button"
              onClick={saveMeetingChangeRequest}
              disabled={!meetingMinuteDraft.changeRequest.trim()}
              aria-label="Add change request"
              title="Add change request"
              className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center text-[#4253C7] transition-colors hover:text-[#3443AD] disabled:cursor-not-allowed disabled:text-[#B8C0CC]"
            >
              <FiCheck size={18} />
            </button>
          </div>
        )}
        <button
          type="button"
          onClick={() => {
            updateMeetingMinuteDraft("changeRequest", "");
            setShowChangeRequestInput(true);
          }}
          className="mt-3 h-[40px] w-full rounded-[8px] border border-[#D5DCF0] px-3 text-[12px] font-medium text-[#4253C7]"
        >
          + Add
        </button>
        {/* The changes listed above become rows in the Client Change Request
  table when the minutes are saved — they need the meeting's id to
  carry its date, attendees and decision count. Pressing this marks
  them as raised and turns the button green; the table then opens
  once Save Minutes / Save Changes is pressed. Disabled until at
  least one change has been added. */}
        <button
          type="button"
          onClick={() => setHasRaisedChange(true)}
          disabled={meetingChangeRequests.length === 0 || hasRaisedChange}
          title={
            meetingChangeRequests.length === 0
              ? "Add a change above first."
              : hasRaisedChange
                ? "Change raised — the table opens when you save."
                : "Mark these changes to be raised on save."
          }
          className={`mt-2 flex h-[40px] w-full items-center justify-center gap-1.5 rounded-[8px] border border-dashed text-[12px] font-semibold disabled:cursor-not-allowed ${
            hasRaisedChange
              ? "border-[#22A06B] bg-[#EAF8F0] text-[#168353] disabled:opacity-100"
              : "border-[#9AAEFF] bg-[#F2F4FF] text-[#3E5AF6] disabled:opacity-50"
          }`}
        >
          {hasRaisedChange && <FiCheck size={14} />}
          {hasRaisedChange ? "Change raised" : "Raise as change request"}
        </button>
        {meetingChangeRequests.length === 0 ? (
          <p className="mt-3 font-mono text-[12px] text-[#73809B]">
            {editingMinuteId
              ? "Changes are raised when minutes are first saved"
              : "Nothing raised from this meeting yet"}
          </p>
        ) : (
          <div className="mt-3 space-y-2 border-t border-[#E8ECF5] pt-3">
            {/* An already-raised change shows its CCR number, so it
                    is obvious which ones are in the table and which are
                    still waiting for the next save. */}
            {meetingChangeRequests.map((request) => (
              <p
                key={request.id}
                className={`text-[12px] leading-[18px] ${request.raised ? "text-[#168353]" : "text-[#4253C7]"}`}
              >
                • {request.text}
                {request.raised && request.ccrNo
                  ? ` · CCR ${request.ccrNo}`
                  : ""}
              </p>
            ))}
          </div>
        )}
      </div>
      <div className="rounded-[20px] bg-white p-5 shadow-[0_0_4px_rgba(0,0,0,0.06)]">
        <h4 className="text-[15px] font-semibold text-[#20263A]">
          Attachments
        </h4>
        <input
          ref={meetingAttachmentInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={selectMeetingAttachments}
        />
        <button
          type="button"
          onClick={() => meetingAttachmentInputRef.current?.click()}
          className="mt-3 flex min-h-[64px] w-full items-center justify-center rounded-[8px] border border-dashed border-[#B8C0CC] bg-white px-5 text-center text-[12px] font-medium text-[#4253C7] hover:bg-[#F7F8FC]"
        >
          + Add attachment
        </button>
        {meetingAttachments.length > 0 && (
          <div className="mt-2 space-y-1">
            {meetingAttachments.map((file, index) => (
              <p
                key={`${file.name}-${index}`}
                className="truncate text-[12px] text-[#5E6C8D]"
              >
                {file.name}
              </p>
            ))}
          </div>
        )}
      </div>
    </aside>
  );

  // ── The Add Minutes form ─────────────────────────────────────────
  //
  // Rendered by BOTH the Meeting Minutes tab and the Client Change
  // Request tab, so the two views are the same markup rather than a copy
  // that could drift. Only the eyebrow label, the Save label and the two
  // handlers differ; every field, class and layout is shared.
  const renderMinutesForm = ({ eyebrow, saveLabel, onSave, onCancel }) => (
    <>
      <div className="rounded-[14px] bg-white shadow-[0_0_4px_rgba(0,0,0,0.06)]">
        <div className="flex items-center justify-between border-b border-[#E8ECF5] px-5 py-4">
          <div className="min-w-0 flex-1">
            <p className="mb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#60709C]">
              {eyebrow}
            </p>
            <input
              value={meetingMinuteDraft.title}
              onChange={(e) =>
                updateMeetingMinuteDraft("title", e.target.value)
              }
              className="w-full bg-transparent pl-3 text-[18px] font-medium outline-none placeholder:text-[#AAB4CC]"
              placeholder="Meeting title — e.g.Task1 client review"
            />
          </div>
          <div className="ml-3 flex shrink-0 gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded-[7px] border border-[#DCE2F0] px-3 py-2 text-[12px]"
            >
              Cancel
            </button>
            {/* One saving flag per tab, so the button reflects the save
                  actually in flight. */}
            <button
              type="button"
              onClick={onSave}
              disabled={
                !isMeetingMinuteComplete || isSavingMinutes || isSavingRequest
              }
              title={
                isMeetingMinuteComplete
                  ? ""
                  : "Fill in every field on the left, tick at least one attendee and save at least one decision."
              }
              className="rounded-[7px] bg-[#4253C7] px-3 py-2 text-[12px] text-white disabled:opacity-50"
            >
              {isSavingMinutes || isSavingRequest ? "Saving…" : saveLabel}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2 lg:grid-cols-3 items-start">
          <div className="text-[12px] font-medium text-[#43506B]">
            Date
            <div
              onMouseDown={(e) => {
                e.preventDefault();
                if (meetingDateInputRef.current?.showPicker) {
                  try {
                    meetingDateInputRef.current.showPicker();
                  } catch {
                    meetingDateInputRef.current?.focus();
                  }
                } else {
                  meetingDateInputRef.current?.focus();
                }
              }}
              className="relative mt-1.5 h-9 w-full cursor-pointer rounded-[6px] border border-[#DCE2F0]"
            >
              <div className="absolute left-0 top-0 h-9 w-9 flex items-center justify-center rounded-l-[6px] bg-[#0404040A] border-r border-[#DCE2F0] z-10">
                <Icons.Calendar />
              </div>
              <input
                ref={meetingDateInputRef}
                type="date"
                value={meetingMinuteDraft.date}
                onChange={(e) =>
                  updateMeetingMinuteDraft("date", e.target.value)
                }
                className="meeting-date-input pointer-events-none h-9 w-full rounded-[6px] border-none bg-transparent pl-11 pr-2 text-[13px] font-normal outline-none"
              />
            </div>
          </div>

          <div className="text-[12px] font-medium text-[#43506B]">
            Time
            <div
              onMouseDown={(e) => {
                e.preventDefault();
                if (meetingTimeInputRef.current?.showPicker) {
                  try {
                    meetingTimeInputRef.current.showPicker();
                  } catch {
                    meetingTimeInputRef.current?.focus();
                  }
                } else {
                  meetingTimeInputRef.current?.focus();
                }
              }}
              className="relative mt-1.5 h-9 w-full cursor-pointer rounded-[6px] border border-[#DCE2F0]"
            >
              <div className="absolute left-0 top-0 h-9 w-9 flex items-center justify-center rounded-l-[6px] bg-[#0404040A] border-r border-[#DCE2F0] z-10">
                {Icons.Clock ? (
                  <Icons.Clock />
                ) : (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#374151"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="9" />
                    <path
                      d="M12 7v5l3 3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </div>
              <input
                ref={meetingTimeInputRef}
                type="time"
                value={meetingMinuteDraft.time}
                onChange={(e) =>
                  updateMeetingMinuteDraft("time", e.target.value)
                }
                className="meeting-time-input pointer-events-none h-9 w-full rounded-[6px] border-none bg-transparent pl-11 pr-2 text-[13px] font-normal outline-none"
              />
            </div>
          </div>

          <div className="text-[12px] font-medium text-[#43506B]">
            Meeting type
            <div className="relative mt-1.5" ref={meetingTypeDropdownRef}>
              <div
                tabIndex={0}
                aria-expanded={showMeetingTypeDropdown}
                onClick={() => {
                  setMeetingTypeHighlight(
                    Math.max(
                      0,
                      MEETING_TYPE_OPTIONS.indexOf(meetingMinuteDraft.type),
                    ),
                  );
                  setShowMeetingTypeDropdown((prev) => !prev);
                }}
                onKeyDown={(e) => {
                  if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                    e.preventDefault();
                    if (!showMeetingTypeDropdown) {
                      setMeetingTypeHighlight(
                        Math.max(
                          0,
                          MEETING_TYPE_OPTIONS.indexOf(meetingMinuteDraft.type),
                        ),
                      );
                      setShowMeetingTypeDropdown(true);
                      return;
                    }
                    setMeetingTypeHighlight((i) => {
                      const next = e.key === "ArrowDown" ? i + 1 : i - 1;
                      return (
                        (next + MEETING_TYPE_OPTIONS.length) %
                        MEETING_TYPE_OPTIONS.length
                      );
                    });
                  } else if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (showMeetingTypeDropdown) {
                      updateMeetingMinuteDraft(
                        "type",
                        MEETING_TYPE_OPTIONS[meetingTypeHighlight],
                      );
                      setShowMeetingTypeDropdown(false);
                    } else {
                      setShowMeetingTypeDropdown(true);
                    }
                  } else if (e.key === "Escape") {
                    setShowMeetingTypeDropdown(false);
                  }
                }}
                className="w-full h-9 border border-gray-300 rounded-lg pl-3 pr-8 text-[12px] font-normal flex items-center cursor-pointer bg-white outline-none focus:border-[#4253C7]"
              >
                <span className="text-black">{meetingMinuteDraft.type}</span>
              </div>

              <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                <svg
                  className={`w-4 h-4 text-gray-500 transition-transform ${
                    showMeetingTypeDropdown ? "rotate-180" : ""
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>

              {showMeetingTypeDropdown && (
                <div className="absolute top-[46px] left-0 w-full bg-white border border-gray-200 rounded-lg shadow-[0_8px_24px_rgba(0,0,0,0.12)] z-50 max-h-[220px] overflow-y-auto py-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                  {MEETING_TYPE_OPTIONS.map((option, index) => (
                    <div
                      key={option}
                      onMouseEnter={() => setMeetingTypeHighlight(index)}
                      onClick={() => {
                        updateMeetingMinuteDraft("type", option);
                        setShowMeetingTypeDropdown(false);
                      }}
                      className={`px-4 py-3 text-[12px] font-normal cursor-pointer border-b border-gray-100 last:border-b-0 ${
                        meetingTypeHighlight === index
                          ? "bg-[#EEF1FF] text-[#4253C7]"
                          : "text-black"
                      }`}
                    >
                      {option}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="text-[12px] font-medium text-[#43506B]">
            Task ({totalTaskItems} in this project)
            <select
              value={meetingMinuteDraft.taskId}
              onChange={(e) =>
                updateMeetingMinuteDraft("taskId", e.target.value)
              }
              className="mt-1.5 h-9 w-full rounded-[6px] border border-[#DCE2F0] bg-white px-2 text-[13px] font-normal outline-none"
            >
              <option value="">Select a task</option>
              {allTasks.map((task) => (
                <option key={task._id} value={task._id}>
                  {task.name}
                </option>
              ))}
            </select>
          </div>
        </div>{" "}
        <div className="border-t border-[#E8ECF5] p-5">
          <p className="text-[12px] font-medium text-[#43506B]">
            Attendees{" "}
            <span className="font-normal text-[#7D89A3]">
              ({resources.length} project resources)
            </span>
          </p>
          <div className="meeting-minute-attendees mt-3 flex flex-wrap gap-2">
            <style>{`.meeting-minute-attendees .border-\\[\\#4253C7\\].bg-\\[\\#EEF1FF\\].text-\\[\\#4253C7\\] { border-color: #22C55E !important; background-color: #ECFDF3 !important; color: #15803D !important; }`}</style>
            {resources.map(getMemberInfo).map((member) => (
              <button
                key={member._id}
                type="button"
                onClick={() => toggleMeetingAttendee(member._id)}
                className={`rounded-full border px-3 py-1.5 text-[12px] ${meetingMinuteDraft.attendeeIds.includes(member._id) ? "border-[#22A06B] bg-[#EAF8F0] text-[#168353]" : "border-[#DCE2F0] text-[#667085]"}`}
              >
                {meetingMinuteDraft.attendeeIds.includes(member._id)
                  ? "✓ "
                  : ""}
                {member.name}
              </button>
            ))}
            {resources.length === 0 && (
              <p className="text-[12px] text-[#7D89A3]">
                No resources are assigned to this project.
              </p>
            )}
          </div>
          {/* Auto Fill sits directly above the two fields it writes, so it
                reads as a shortcut into them. Hidden when no key is set. */}
          <div className="mt-5 flex items-center justify-between">
            <label className="text-[12px] font-medium text-[#43506B]">
              Discussion &amp; agenda notes
            </label>
            {transcriptDraftAvailable && (
              <button
                type="button"
                onClick={() => {
                  setShowTranscriptBox(true);
                  setTranscriptMode("paste");
                  setTranscriptError("");
                  setDraftNotice("");
                }}
                className="rounded-[7px] border border-[#4253C7] bg-[#F4F6FF] px-3 py-1.5 text-[11px] font-medium text-[#4253C7]"
              >
                Auto Fill
              </button>
            )}
          </div>
          {draftNotice && (
            <p className="mt-2 rounded-[7px] bg-[#FFF8E6] px-3 py-2 text-[11px] leading-[16px] text-[#8A6100]">
              {draftNotice}
            </p>
          )}
          <label className="mt-2 block text-[12px] font-medium text-[#43506B]">
            <textarea
              value={meetingMinuteDraft.notes}
              onChange={(e) =>
                updateMeetingMinuteDraft("notes", e.target.value)
              }
              rows="5"
              className="w-full rounded-[7px] border border-[#DCE2F0] p-3 text-[13px] font-normal outline-none"
              placeholder="What was discussed, agreed and decided?"
            />
          </label>
          <div
            ref={decisionsSectionRef}
            className="mt-5 border-t border-[#E8ECF5] pt-5"
          >
            <div className="flex items-center justify-between">
              <p className="text-[12px] font-medium text-[#151A2D]">
                Decisions made
              </p>
              <button
                type="button"
                onClick={() => setMeetingDecisions((items) => [...items, ""])}
                className="rounded-[7px] border border-[#4253C7] bg-[#F4F6FF] px-3 py-1.5 text-[11px] font-medium text-[#4253C7]"
              >
                + Add decision
              </button>
            </div>
            <div className="mt-3 space-y-2">
              {meetingDecisions.map((decision, index) => (
                <div
                  key={index}
                  onClickCapture={(event) => {
                    if (event.target?.textContent === "×")
                      setMeetingDecisions((items) =>
                        items.filter((_, itemIndex) => itemIndex !== index),
                      );
                  }}
                  className="flex items-center gap-2"
                >
                  <span className="w-4 text-[10px] text-[#4253C7]">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <div className="relative flex-1">
                    <input
                      value={decision}
                      readOnly={confirmedDecisionIndexes.includes(index)}
                      onChange={(e) => {
                        setMeetingDecisions((items) =>
                          items.map((item, itemIndex) =>
                            itemIndex === index ? e.target.value : item,
                          ),
                        );
                        setConfirmedDecisionIndexes((indexes) =>
                          indexes.filter((itemIndex) => itemIndex !== index),
                        );
                      }}
                      className={`h-9 w-full rounded-[7px] border border-[#DCE2F0] bg-white px-3 text-[12px] text-[#151A2D] outline-none ${confirmedDecisionIndexes.includes(index) ? "" : "pr-9"}`}
                      placeholder="Add a decision"
                    />
                    {!confirmedDecisionIndexes.includes(index) && (
                      <button
                        type="button"
                        aria-label="Save decision"
                        disabled={!decision.trim()}
                        onClick={() =>
                          setConfirmedDecisionIndexes((indexes) => [
                            ...indexes,
                            index,
                          ])
                        }
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[#4253C7] disabled:text-[#B8C0CC]"
                      >
                        <FiCheck size={16} />
                      </button>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setMeetingDecisions((items) =>
                        items.length === 1
                          ? [""]
                          : items.filter((_, itemIndex) => itemIndex !== index),
                      )
                    }
                    className="px-1 text-[13px] text-[#60709C]"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
          {/* Whichever tab the form is open in, its own error shows here. */}
          {(meetingMinuteError ||
            (showNewRequestForm && changeRequestError)) && (
            <p className="mt-4 rounded-[7px] bg-[#FEF3F2] px-3 py-2 text-[12px] text-[#B42318]">
              {meetingMinuteError || changeRequestError}
            </p>
          )}
        </div>
      </div>
      {/* Lives with the form, so both tabs get Auto Fill from one place. */}
      {renderAutoFillDialog()}
    </>
  );

  const saveMeetingMinutes = async () => {
    if (!isMeetingMinuteComplete || !selectedProject?._id) return;
    setIsSavingMinutes(true);
    setMeetingMinuteError("");

    try {
      // Every project resource is sent with its own attendance flag, so an
      // unticked name is recorded as absent rather than dropped.
      const payload = meetingMinuteService.toServerPayload(meetingMinuteDraft, {
        decisions: savedDecisions,
        memberIds: resources.map(getMemberInfo).map((member) => member._id),
      });

      // The same form both adds and edits: an id in editingMinuteId means
      // update that record rather than create a new one.
      const response = editingMinuteId
        ? await meetingMinuteService.updateMinute(
            selectedProject._id,
            editingMinuteId,
            payload,
          )
        : await meetingMinuteService.createMinute(selectedProject._id, payload);
      const saved = response.data;

      // On create, attachments are picked before the meeting exists, so they
      // upload once its id is known. On edit, only newly picked files carry a
      // `file` handle, so existing uploads are never re-sent. A failure here
      // is reported but does not discard the saved minutes.
      const files = meetingAttachments.map((item) => item.file).filter(Boolean);
      if (files.length && saved?._id) {
        try {
          await meetingMinuteService.uploadMinuteAttachments(
            selectedProject._id,
            saved._id,
            files,
          );
        } catch (uploadError) {
          setMeetingMinuteError(
            uploadError.response?.data?.message ||
              "Minutes were saved, but the attachments could not be uploaded.",
          );
        }
      }

      // Changes raised while writing the minutes become rows in the Client
      // Change Request table, each carrying this meeting's date, time,
      // attendees and decision count.
      //
      // This runs on Save Minutes AND Save Changes, but only for entries not
      // already raised: the ones loaded back when editing are marked
      // `raised`, so re-saving cannot duplicate their rows. A change added
      // during the edit is unraised, so it gets its own new CCR number and
      // its own row.
      // `hasRaisedChange` is the button having been pressed: raising is an
      // explicit act, so a change merely typed into the list is not turned
      // into a row until it is asked for.
      const unraised = hasRaisedChange
        ? meetingChangeRequests.filter((r) => !r.raised)
        : [];
      let raisedAny = false;
      if (saved?._id && unraised.length) {
        try {
          // Sequential, not Promise.all: the CCR numbers are allocated per
          // request and should follow the order they were entered in.
          for (const request of [...unraised].reverse()) {
            await changeRequestService.createChangeRequest(
              selectedProject._id,
              { title: request.text, sourceMinuteId: saved._id },
            );
          }
          raisedAny = true;
        } catch (requestError) {
          setMeetingMinuteError(
            requestError.response?.data?.message ||
              "Minutes were saved, but a change request could not be raised.",
          );
        }
      }

      setSelectedMeetingMinuteId(saved?._id || null);
      resetMeetingMinuteForm();
      setShowMeetingMinuteForm(false);
      await loadMeetingMinutes(selectedProject._id);
      await loadChangeRequests(selectedProject._id);
      // Land on the table when a row was actually just added, so the result
      // of raising the change is visible without hunting for the tab.
      if (raisedAny) setActiveProjectTab("client-change-request");
    } catch (error) {
      setMeetingMinuteError(
        error.response?.data?.message ||
          error.message ||
          (editingMinuteId
            ? "Could not update the meeting minutes."
            : "Could not save the meeting minutes."),
      );
    } finally {
      setIsSavingMinutes(false);
    }
  };

  const saveMeetingChangeRequest = () => {
    const request = meetingMinuteDraft.changeRequest.trim();
    if (!request) return;
    // raised: false — no row exists for this yet. It gets one on the next
    // save, which is also what stops an edit re-raising the older entries.
    setMeetingChangeRequests((requests) => [
      { id: Date.now(), text: request, raised: false },
      ...requests,
    ]);
    updateMeetingMinuteDraft("changeRequest", "");
    setShowChangeRequestInput(false);
    // A change typed after the button was pressed still needs raising, so the
    // green state resets until it is pressed again.
    setHasRaisedChange(false);
  };

  const selectMeetingAttachments = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length) {
      const attachments = await Promise.all(
        files.map(
          (file) =>
            new Promise((resolve) => {
              const reader = new FileReader();
              // `url` is the local preview shown before saving; `file` is the
              // real File, kept so it can be uploaded once the meeting has an
              // id to attach it to.
              reader.onload = () =>
                resolve({
                  name: file.name,
                  type: file.type,
                  url: reader.result,
                  file,
                });
              reader.readAsDataURL(file);
            }),
        ),
      );
      setMeetingAttachments((current) => [...current, ...attachments]);
    }
    event.target.value = "";
  };

  useEffect(() => {
    const handleOutsideMeetingInputClick = (event) => {
      const outsideChangeRequest =
        changeRequestCardRef.current &&
        !changeRequestCardRef.current.contains(event.target);
      const outsideDecisions =
        decisionsSectionRef.current &&
        !decisionsSectionRef.current.contains(event.target);

      if (
        (outsideChangeRequest || outsideDecisions) &&
        document.activeElement instanceof HTMLElement
      ) {
        document.activeElement.blur();
      }
      if (outsideDecisions) {
        setMeetingDecisions((items) => items.filter((item) => item.trim()));
      }
      if (outsideChangeRequest) {
        setShowChangeRequestInput(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideMeetingInputClick);
    return () =>
      document.removeEventListener("mousedown", handleOutsideMeetingInputClick);
  }, []);

  useEffect(() => {
    if (!selectedProject) return;
    // The store can still hold the project from an earlier visit while the
    // URL already names a different one; only load the one being opened.
    if (projectId && selectedProject._id !== projectId) return;
    loadProjectLists(selectedProject._id);
    loadProjectSummary(selectedProject._id);
  }, [selectedProject?._id, projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sprints are fetched when the Timeline tab is opened, for the same reason
  // the minutes are: the other tabs should cost nothing extra.
  useEffect(() => {
    if (activeProjectTab !== "timeline" || !selectedProject?._id) return;
    loadSprints(selectedProject._id);
    // Also tells us whether automatic import is configured, so the Add
    // Sprint chooser knows before it opens.
    loadAvailableLists();
  }, [activeProjectTab, selectedProject?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Minutes are fetched when the tab is actually opened rather than with the
  // project, so the other tabs cost nothing extra.
  useEffect(() => {
    if (activeProjectTab !== "meeting-minutes" || !selectedProject?._id) return;
    loadMeetingMinutes(selectedProject._id);
  }, [activeProjectTab, selectedProject?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Whether the server has a Gemini key configured. Asked once per project:
  // the form hides "Draft from transcript" rather than offering a button that
  // could only fail. Never carries the key itself, just a boolean.
  useEffect(() => {
    if (!selectedProject?._id || !canManage) return;
    let cancelled = false;
    meetingMinuteService
      .getMinuteOptions(selectedProject._id)
      .then((response) => {
        if (!cancelled) {
          setTranscriptDraftAvailable(
            Boolean(response.data?.transcriptDraftAvailable),
          );
        }
      })
      .catch(() => {
        // Not fatal — the form simply does not offer the drafting step.
        if (!cancelled) setTranscriptDraftAvailable(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProject?._id, canManage]);

  useEffect(() => {
    if (activeProjectTab !== "client-change-request" || !selectedProject?._id) {
      return;
    }
    loadChangeRequests(selectedProject._id);
  }, [activeProjectTab, selectedProject?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Switching project must not leave the previous one's minutes on screen.
  useEffect(() => {
    setMeetingMinutes([]);
    setSelectedMeetingMinuteId(null);
    setShowMeetingMinuteForm(false);
    resetMeetingMinuteForm();
    setChangeRequests([]);
    setShowNewRequestForm(false);
    setChangeRequestError("");
    setOpenRequestId(null);
    setRequestDetail(null);
    setPendingRequests([]);
    setPendingMinutes([]);
    setReviewingId(null);
  }, [selectedProject?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const getProjectTaskCount = (p) => {
    // Prefer the live Kanban data (same source Kanban.js uses) so the
    // count always matches what's shown on the board.
    const kanbanMatch = (kanbanProjects || []).find((kp) => kp._id === p._id);
    if (kanbanMatch && Array.isArray(kanbanMatch.lists)) {
      return kanbanMatch.lists.reduce(
        (sum, l) => sum + (l?.tasks?.length || 0),
        0,
      );
    }
    // Fallback if the kanban list hasn't loaded yet
    return Array.isArray(p.lists)
      ? p.lists.reduce((sum, l) => sum + (l?.tasks?.length || 0), 0)
      : (p.taskCount ?? p.totalTasks ?? 0);
  };
  const getTimeAgo = (date) => {
    if (!date) return "Just now";
    const diff = Math.floor((new Date() - new Date(date)) / 1000);
    const minutes = Math.floor(diff / 60);
    const hours = Math.floor(diff / 3600);
    const days = Math.floor(diff / 86400);
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  // ── Create task (replaces authFetch inline) ───────────────────────
  // The project's own date range, which every task must sit inside — the
  // same bound Kanban's Add Card applies through its date pickers and checks.
  const toValidTaskDate = (v) => {
    if (!v) return null;
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  };
  const taskProjectStart = toValidTaskDate(selectedProject?.startDate);
  const taskProjectEnd = toValidTaskDate(selectedProject?.deadline);
  // Compare by calendar day, ignoring time-of-day.
  const taskDayValue = (v) => {
    const d = new Date(v);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  };
  const taskMaxHours =
    taskForm.startDate && taskForm.endDate
      ? getMaxEstimatedHours(taskForm.startDate, taskForm.endDate)
      : null;

  // One reset for every way the popup closes, so a cancelled or saved task
  // never leaves its values behind for the next one.
  const resetTaskForm = () => {
    setTaskForm({
      listId: "",
      taskName: "",
      type: "design",
      priority: "high",
      startDate: "",
      endDate: "",
      assignees: [],
      hours: "",
      description: "",
      color: null,
    });
    setSelectedAssignees([]);
    setAssigneeSearch("");
    setTaskColorAnchor(null);
    setTaskFormError("");
  };

  const handleCreateTask = async () => {
    // All Add Task fields are mandatory — errors show inside the card. The
    // checks and their order mirror Kanban's Add Card, so a task is accepted
    // or refused identically whichever screen it is created from.
    const err = (() => {
      if (!selectedProject) return "*Select project";
      if (!taskForm.listId) return "*List name is required.";
      if (!taskForm.taskName.trim()) return "*Task name is required.";
      if (!taskForm.type) return "*Task type is required.";
      if (!taskForm.priority) return "*Priority is required.";
      if (!taskForm.startDate) return "*Start date is required.";
      if (!taskForm.endDate) return "*End date is required.";
      if (
        taskProjectStart &&
        taskDayValue(taskForm.startDate) < taskDayValue(taskProjectStart)
      )
        return "*Start date cannot be before the project start date.";
      if (
        taskProjectEnd &&
        taskDayValue(taskForm.endDate) > taskDayValue(taskProjectEnd)
      )
        return "*End date cannot be after the project end date.";
      if (taskDayValue(taskForm.endDate) < taskDayValue(taskForm.startDate))
        return "*End date cannot be before the start date.";
      if (!taskForm.assignees || taskForm.assignees.length === 0)
        return "*At least one task assignee is required.";
      if (!taskForm.hours || parseInt(taskForm.hours) <= 0)
        return "*Estimated hours is required.";
      const maxHours = getMaxEstimatedHours(
        taskForm.startDate,
        taskForm.endDate,
      );
      if (parseInt(taskForm.hours) > maxHours)
        return `*Estimated hours cannot exceed ${maxHours} (max 8 hours/day for the selected dates).`;
      if (!taskForm.description.trim()) return "*Description is required.";

      // Prevent duplicate task names within the project (case-insensitive)
      const nameToCheck = taskForm.taskName.trim().toLowerCase();
      const isDuplicate = allTasks.some(
        (t) => (t.name || "").trim().toLowerCase() === nameToCheck,
      );
      if (isDuplicate) return "*A task with this name already exists.";

      return "";
    })();
    if (err) {
      setTaskFormError(err);
      return;
    }
    setTaskFormError("");

    try {
      const data = await kanbanService.createTask(
        selectedProject._id,
        taskForm.listId, // ← was: list._id after find()
        {
          name: taskForm.taskName,
          taskType: taskForm.type,
          assignees: taskForm.assignees || [],
          estimatedHours: taskForm.hours ? parseInt(taskForm.hours) : 0,
          taskStartDate: toDateOnlyISO(taskForm.startDate),
          taskDeadline: toDateOnlyISO(taskForm.endDate),
          description: taskForm.description,
          priority: taskForm.priority.toLowerCase(),
          color: taskForm.color,
        },
      );
      pushNotification({
        title: "Task created successfully",
        lines: [`Task: ${taskForm.taskName}`],
      });
      updateColumns(
        columns.map((col) =>
          col._id === taskForm.listId
            ? { ...col, tasks: [...col.tasks, data.data] }
            : col,
        ),
      );
      resetTaskForm();
      setShowTaskModal(false);
    } catch (err) {
      setTaskFormError(
        err?.response?.data?.message || "*Failed to create task.",
      );
    }
  };
  const handleExportCSV = () => {
    if (!selectedProject?._id) return;
    exportCSV(selectedProject._id);
  };

  // Holds the member pending removal — drives the confirmation popup
  const [confirmRemoveMember, setConfirmRemoveMember] = useState(null);
  const [cannotRemoveAdmin, setCannotRemoveAdmin] = useState(null);
  const [meetingTypeHighlight, setMeetingTypeHighlight] = useState(0);

  const handleRemoveMember = async (memberId) => {
    if (!selectedProject?._id || !memberId) return;
    try {
      await removeMember(selectedProject._id, memberId);
      await loadProjectSummary(selectedProject._id);
      setShowMenu(null);
      setConfirmRemoveMember(null);
      pushNotification({
        title: "Member removed successfully",
        lines: [],
      });
    } catch (err) {
      setConfirmRemoveMember(null);
      alert(err?.response?.data?.message || "Failed to remove member.");
    }
  };

  const handleAddMembers = async () => {
    if (!selectedProject?._id) return;

    const existingMemberIds = new Set(resources.map((member) => member._id));
    const membersToAdd = selectedMembers.filter(
      (memberId) => !existingMemberIds.has(memberId),
    );

    if (membersToAdd.length === 0) {
      setShowModal(false);
      return;
    }

    try {
      for (const memberId of membersToAdd) {
        await addMember(selectedProject._id, memberId);
      }
      await loadProjectSummary(selectedProject._id);
      setSelectedMembers([]);
      setSearch("");
      setShowModal(false);
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to add member.");
    }
  };
  // ── Inline edit: project name/description ──
  const startEditProjectInfo = () => {
    setEditName(selectedProject?.name || "");
    setEditDescription(selectedProject?.description || "");
    setIsEditingProjectInfo(true);
  };

  const cancelEditProjectInfo = () => {
    setEditName(selectedProject?.name || "");
    setEditDescription(selectedProject?.description || "");
    setIsEditingProjectInfo(false);
  };

  const resetInviteClientForm = () => {
    setShowInviteClientModal(false);
    setClientEmail("");
    setClientDescription("");
    setClientStartDate("");
    setClientEmailError("");
  };

  // ── Add Client chooser ────────────────────────────────────────────
  // Viewers skip the chooser entirely and go straight to the invite form:
  // picking an "existing client" means browsing every client account in the
  // system, which is an admin/manager view. A viewer can only invite someone
  // new to a project they already belong to.
  const openAddClient = async () => {
    if (!selectedProject?._id) return;
    setAddClientError("");

    if (isViewerRole) {
      setShowInviteClientModal(true);
      return;
    }

    setShowAddClientModal(true);
    setIsLoadingClients(true);
    try {
      const res = await projectService.getAvailableClients(selectedProject._id);
      setAvailableClients(res?.data || []);
    } catch (err) {
      setAvailableClients([]);
      setAddClientError(
        err?.response?.data?.message || "Failed to load client accounts.",
      );
    } finally {
      setIsLoadingClients(false);
    }
  };

  // Attaching an existing viewer reuses the invite endpoint — the backend
  // detects the account already exists and simply grants project access.
  const handleAddExistingClient = async (client) => {
    if (!selectedProject?._id) return;
    setIsAddingClient(true);
    setAddClientError("");
    try {
      await projectService.inviteClient(selectedProject._id, {
        email: client.email,
        dateOfJoining: new Date().toISOString().split("T")[0],
      });
      setShowAddClientModal(false);
      await loadProject(selectedProject._id);
    } catch (err) {
      setAddClientError(
        err?.response?.data?.message || "Failed to add this client.",
      );
    } finally {
      setIsAddingClient(false);
    }
  };

  const handleInviteClient = async () => {
    const email = clientEmail.trim();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setClientEmailError("Enter a valid client email address.");
      return;
    }
    if (!clientStartDate) {
      setClientEmailError("Start date is required.");
      return;
    }
    if (!selectedProject?._id) return;

    // Creates the client account, emails the invite, and grants access to
    // this project — replaces the old mailto: link, which sent a message but
    // never created an account.
    try {
      await projectService.inviteClient(selectedProject._id, {
        email,
        description: clientDescription.trim(),
        dateOfJoining: clientStartDate,
      });
      resetInviteClientForm();
      await loadProject(selectedProject._id);
    } catch (err) {
      setClientEmailError(
        err?.response?.data?.message || "Failed to invite this client.",
      );
    }
  };

  const saveEditProjectInfo = async () => {
    if (!selectedProject?._id) return;
    try {
      await updateProject(selectedProject._id, {
        name: editName,
        description: editDescription,
      });
      setIsEditingProjectInfo(false);
      void loadProjects();
    } catch (err) {
      alert(
        typeof err === "string"
          ? err
          : err?.response?.data?.message || "Unable to save project changes.",
      );
    }
  };

  const handleToggleDisable = async () => {
    if (!selectedProject?._id) return;
    try {
      if (selectedProject.isDisabled) {
        await enableProject(selectedProject._id).unwrap();
      } else {
        await disableProject(selectedProject._id).unwrap();
      }
      await loadProject(selectedProject._id);
      await loadProjects();
      await loadDisabledProjects();
      setShowDisableConfirm(false);
    } catch (err) {
      alert(
        typeof err === "string"
          ? err
          : err?.response?.data?.message || "Unable to update project status.",
      );
    }
  };
  // ── Inline edit: start date / deadline ──
  const startEditDates = () => {
    setEditStartDate(
      selectedProject?.startDate ? new Date(selectedProject.startDate) : null,
    );
    setEditDeadline(
      selectedProject?.deadline ? new Date(selectedProject.deadline) : null,
    );
    setEditPriority(selectedProject?.priority || "high");
    setIsEditingDates(true);
  };

  const cancelEditDates = () => {
    setIsEditingDates(false);
  };

  const saveEditDates = async () => {
    if (!selectedProject?._id) return;
    try {
      await updateProject(selectedProject._id, {
        startDate: editStartDate,
        deadline: editDeadline,
        priority: editPriority,
      });
      setIsEditingDates(false);
      void loadProjects();
    } catch (err) {
      alert(
        typeof err === "string"
          ? err
          : err?.response?.data?.message || "Unable to save project dates.",
      );
    }
  };
  // ═══════════════════════════════════════════════════════════════════
  // JSX
  // ═══════════════════════════════════════════════════════════════════
  const getPriorityBadge = (priority) => {
    const p = (priority || "").toUpperCase();
    if (p === "LOW") return "bg-[#CFFFDE] text-[#003C18]";
    if (p === "MEDIUM") return "bg-[#FFE8B6] text-[#B76E00]";
    if (p === "HIGH") return "bg-[#FFCA75CC] text-[#FF1A00]";
    if (p === "CRITICAL") return "bg-[#FFD6D6] text-[#D60000]";
    return "bg-blue-100 text-blue-700";
  };

  const [assigneeSearch, setAssigneeSearch] = useState("");
  const [showAssigneeDropdown, setShowAssigneeDropdown] = useState(false);
  const [selectedAssignees, setSelectedAssignees] = useState([]);
  const assigneeDropdownRef = useRef(null);
  const [openResourceRow, setOpenResourceRow] = useState(null);
  const resourcePopupRef = useRef(null);
  const [showManagerBlocked, setShowManagerBlocked] = useState(false);
  const removeMenuRef = useRef(null);
  // Resources card list, driven by its hover scrollbar.
  const resourcesScrollRef = useRef(null);
  const taskDescPopupRef = useRef(null);
  // Ref for the "Project Information" card — used to detect outside
  // clicks while the start-date/deadline/priority fields are in edit mode.
  const projectInfoCardRef = useRef(null);

  useEffect(() => {
    const handleClickOutsideResource = (e) => {
      if (
        resourcePopupRef.current &&
        !resourcePopupRef.current.contains(e.target)
      ) {
        setOpenResourceRow(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideResource);
    return () =>
      document.removeEventListener("mousedown", handleClickOutsideResource);
  }, []);

  useEffect(() => {
    const handleClickOutsideRemoveMenu = (e) => {
      if (removeMenuRef.current && !removeMenuRef.current.contains(e.target)) {
        setShowMenu(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideRemoveMenu);
    return () =>
      document.removeEventListener("mousedown", handleClickOutsideRemoveMenu);
  }, []);

  useEffect(() => {
    const handleClickOutsideTaskDesc = (e) => {
      if (
        taskDescPopupRef.current &&
        !taskDescPopupRef.current.contains(e.target)
      ) {
        setSelectedTaskDesc(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideTaskDesc);
    return () =>
      document.removeEventListener("mousedown", handleClickOutsideTaskDesc);
  }, []);

  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [showPriorityDropdown, setShowPriorityDropdown] = useState(false);
  const [showMeetingTypeDropdown, setShowMeetingTypeDropdown] = useState(false);
  const typeDropdownRef = useRef(null);
  const priorityDropdownRef = useRef(null);
  const meetingTypeDropdownRef = useRef(null);

  const TASK_TYPE_OPTIONS = [
    "requirements",
    "design",
    "others",
    "frontend",
    "backend",
    "testing",
    "deployment",
  ];
  const PRIORITY_OPTIONS = ["high", "medium", "low", "critical"];
  const MEETING_TYPE_OPTIONS = [
    "Client Review",
    "Internal Standup",
    "Kickoff",
    "Requirement Walkthrough",
    "Escalation",
  ];

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        assigneeDropdownRef.current &&
        !assigneeDropdownRef.current.contains(e.target)
      ) {
        setShowAssigneeDropdown(false);
      }
      if (
        typeDropdownRef.current &&
        !typeDropdownRef.current.contains(e.target)
      ) {
        setShowTypeDropdown(false);
      }
      if (
        priorityDropdownRef.current &&
        !priorityDropdownRef.current.contains(e.target)
      ) {
        setShowPriorityDropdown(false);
      }
      if (
        meetingTypeDropdownRef.current &&
        !meetingTypeDropdownRef.current.contains(e.target)
      ) {
        setShowMeetingTypeDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  // (Modal edit handlers removed — inline editing is used elsewhere)

  // Inline edit: project name + description
  const [isEditingProjectInfo, setIsEditingProjectInfo] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  // Inline edit: start date + deadline + priority
  const [isEditingDates, setIsEditingDates] = useState(false);
  const [editStartDate, setEditStartDate] = useState(null);
  const [editDeadline, setEditDeadline] = useState(null);
  const [editPriority, setEditPriority] = useState("high");

  // Close the Project Information edit fields (start date / deadline /
  // priority) when clicking anywhere outside that card. This only cancels
  // the edit (like the ✗ button) — it does not save changes.
  useEffect(() => {
    const handleClickOutsideProjectInfo = (e) => {
      if (
        isEditingDates &&
        projectInfoCardRef.current &&
        !projectInfoCardRef.current.contains(e.target)
      ) {
        setIsEditingDates(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideProjectInfo);
    return () =>
      document.removeEventListener("mousedown", handleClickOutsideProjectInfo);
  }, [isEditingDates]);

  // (Pagination component and modal save handler removed — inline controls are used)

  return (
    <div
      className="min-h-screen w-full min-w-0 font-[Poppins] bg-transparent px-3 sm:px-4 md:px-6 py-5"
      style={{ fontFamily: "Poppins, sans-serif" }}
    >
      <style>{DATEPICKER_ALIGN_STYLES}</style>
      <style>{MEETING_DATETIME_STYLES}</style>
      <style>{`.resources-scroll-list, .project-activity-scroll { scrollbar-width: none; -ms-overflow-style: none; } .resources-scroll-list::-webkit-scrollbar, .project-activity-scroll::-webkit-scrollbar { display: none !important; width: 0 !important; height: 0 !important; }`}</style>
      {/* ── ADD TASK MODAL ── */}
      {/* Colour popover for Add Task — same component as Kanban's Add Card. */}
      {showTaskModal && taskColorAnchor && (
        <ColorPicker
          anchorRect={taskColorAnchor}
          value={taskForm.color}
          onSelect={(hex) => {
            setTaskForm((prev) => ({ ...prev, color: hex }));
            setTaskColorAnchor(null);
          }}
          onClose={() => setTaskColorAnchor(null)}
        />
      )}
      {showTaskModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-3 py-4">
          <div
            className="bg-white w-full max-w-[780px] rounded-2xl shadow-xl mx-4 max-h-[90vh] overflow-y-auto"
            style={{ padding: "30px" }}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-[22px] font-semibold">Add Task</h2>
                <p className="text-sm text-gray-400 mt-1">
                  Define tasks for your project workflow.
                </p>
              </div>
              <button
                onClick={() => {
                  setShowTaskModal(false);
                  resetTaskForm();
                }}
                className="text-gray-400 hover:text-gray-600 text-lg"
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[16px] font-normal text-black">
                  List Name
                </label>
                <div className="relative mt-1">
                  <select
                    className="w-full h-[40px] border border-gray-300 rounded-lg pl-3 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 appearance-none bg-white"
                    value={taskForm.listId}
                    onChange={(e) =>
                      setTaskForm({ ...taskForm, listId: e.target.value })
                    }
                  >
                    <option value="">Select a list</option>
                    {columns.map((col) => (
                      <option key={col._id} value={col._id}>
                        {col.name}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    <svg
                      className="w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                </div>
              </div>
              <div>
                <label className="text-[16px] font-normal text-black">
                  Task Name
                </label>
                <input
                  className="w-full mt-1 h-[40px] border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Enter your task name"
                  value={taskForm.taskName}
                  onChange={(e) =>
                    setTaskForm({ ...taskForm, taskName: e.target.value })
                  }
                />
              </div>
              <div>
                <label className="text-[16px] font-normal text-black">
                  Task Type
                </label>
                <div className="relative mt-1" ref={typeDropdownRef}>
                  <div
                    className="w-full h-[40px] border border-gray-300 rounded-lg pl-3 pr-8 text-sm flex items-center cursor-pointer bg-white"
                    onClick={() => setShowTypeDropdown((prev) => !prev)}
                  >
                    <span className="capitalize text-black">
                      {taskForm.type}
                    </span>
                  </div>
                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    <svg
                      className={`w-4 h-4 text-gray-500 transition-transform ${
                        showTypeDropdown ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                  {showTypeDropdown && (
                    <div className="absolute top-[44px] left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[200px] overflow-y-auto">
                      {TASK_TYPE_OPTIONS.map((option) => (
                        <div
                          key={option}
                          className="px-3 py-2.5 hover:bg-[#F5F7FF] cursor-pointer flex items-center justify-between border-b border-gray-100 last:border-b-0"
                          onClick={() => {
                            setTaskForm({ ...taskForm, type: option });
                            setShowTypeDropdown(false);
                          }}
                        >
                          <span className="text-sm font-medium text-black capitalize">
                            {option}
                          </span>
                          {taskForm.type === option && (
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#3B82F6"
                              strokeWidth="3"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <label className="text-[16px] font-normal text-black">
                  Priority
                </label>
                <div className="relative mt-1" ref={priorityDropdownRef}>
                  <div
                    className="w-full h-[40px] border border-gray-300 rounded-lg pl-3 pr-8 text-sm flex items-center cursor-pointer bg-white"
                    onClick={() => setShowPriorityDropdown((prev) => !prev)}
                  >
                    <span className="capitalize text-black">
                      {taskForm.priority}
                    </span>
                  </div>
                  <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                    <svg
                      className={`w-4 h-4 text-gray-500 transition-transform ${showPriorityDropdown ? "rotate-180" : ""}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </div>
                  {showPriorityDropdown && (
                    <div className="absolute top-[44px] left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[200px] overflow-y-auto">
                      {PRIORITY_OPTIONS.map((option) => (
                        <div
                          key={option}
                          className="px-3 py-2.5 hover:bg-[#F5F7FF] cursor-pointer flex items-center justify-between border-b border-gray-100 last:border-b-0"
                          onClick={() => {
                            setTaskForm({ ...taskForm, priority: option });
                            setShowPriorityDropdown(false);
                          }}
                        >
                          <span className="text-sm font-medium text-black capitalize">
                            {option}
                          </span>
                          {taskForm.priority === option && (
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="#3B82F6"
                              strokeWidth="3"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="text-[16px] font-normal text-black">
                  Start date
                </label>
                <div className="relative mt-1 w-full">
                  <div
                    className="absolute left-0 top-0 h-[40px] w-[42px] flex items-center justify-center rounded-l-[8px] bg-[#0404040A] border border-gray-300 border-r-0 cursor-pointer z-10"
                    onClick={() => taskStartDateRef.current.setFocus()}
                  >
                    <Icons.Calendar />
                  </div>
                  <DatePicker
                    ref={taskStartDateRef}
                    selected={
                      taskForm.startDate ? new Date(taskForm.startDate) : null
                    }
                    onChange={(date) =>
                      setTaskForm({ ...taskForm, startDate: date })
                    }
                    placeholderText="Select start date"
                    className="w-full h-[40px] border border-gray-300 rounded-[8px] pl-[50px] pr-3 text-sm"
                    wrapperClassName="w-full block"
                    dateFormat="dd MMM yyyy"
                    minDate={taskProjectStart}
                    maxDate={taskProjectEnd}
                  />
                </div>
              </div>

              <div>
                <label className="text-[16px] font-normal text-black">
                  End date
                </label>
                <div className="relative mt-1 w-full">
                  <div
                    className="absolute left-0 top-0 h-[40px] w-[42px] flex items-center justify-center rounded-l-[8px] bg-[#0404040A] border border-gray-300 border-r-0 cursor-pointer z-10"
                    onClick={() => taskEndDateRef.current.setFocus()}
                  >
                    <Icons.Calendar />
                  </div>
                  <DatePicker
                    ref={taskEndDateRef}
                    selected={
                      taskForm.endDate ? new Date(taskForm.endDate) : null
                    }
                    onChange={(date) =>
                      setTaskForm({ ...taskForm, endDate: date })
                    }
                    placeholderText="Select end date"
                    className="w-full h-[40px] border border-gray-300 rounded-[8px] pl-[50px] pr-3 text-sm"
                    wrapperClassName="w-full block"
                    dateFormat="dd MMM yyyy"
                    minDate={
                      taskForm.startDate
                        ? new Date(taskForm.startDate)
                        : taskProjectStart
                    }
                    maxDate={taskProjectEnd}
                  />
                </div>
              </div>

              <div>
                <label className="text-[16px] font-normal text-black">
                  Task Assignee
                </label>
                <div className="relative mt-1" ref={assigneeDropdownRef}>
                  <div className="absolute left-3 top-[20px] -translate-y-1/2 text-gray-400 z-10">
                    <Icons.Search className="w-4 h-4" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search Members"
                    className="w-full h-[40px] border border-gray-300 rounded-lg pl-9 pr-3 text-sm"
                    value={assigneeSearch}
                    onClick={() => setShowAssigneeDropdown(true)}
                    onChange={(e) => {
                      setAssigneeSearch(e.target.value);
                      setShowAssigneeDropdown(true);
                    }}
                  />
                  {showAssigneeDropdown && (
                    <div className="absolute top-[44px] left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-[200px] overflow-y-auto">
                      {resources
                        .filter((m) =>
                          m.name
                            ?.toLowerCase()
                            .includes(assigneeSearch.toLowerCase()),
                        )
                        .map((member) => {
                          const isSelected = selectedAssignees.some(
                            (m) => m._id === member._id,
                          );
                          return (
                            <div
                              key={member._id}
                              className="px-3 py-2.5 hover:bg-[#F5F7FF] cursor-pointer flex items-center justify-between border-b border-gray-100"
                              onClick={() => {
                                setSelectedAssignees((prev) =>
                                  isSelected
                                    ? prev.filter((m) => m._id !== member._id)
                                    : [...prev, member],
                                );
                                setTaskForm({
                                  ...taskForm,
                                  assignees: isSelected
                                    ? taskForm.assignees.filter(
                                        (id) => id !== member._id,
                                      )
                                    : [
                                        ...(taskForm.assignees || []),
                                        member._id,
                                      ],
                                });
                              }}
                            >
                              <div className="flex items-center gap-2">
                                <div className="w-[24px] h-[24px] rounded-full border border-gray-400 flex items-center justify-center">
                                  <Icons.User />
                                </div>
                                <p className="text-sm font-medium text-black">
                                  {member.name}
                                </p>
                              </div>
                              <div
                                className={`w-[16px] h-[16px] rounded-[4px] border flex items-center justify-center ${isSelected ? "bg-blue-500 border-blue-500" : "border-gray-300"}`}
                              >
                                {isSelected && (
                                  <svg
                                    width="10"
                                    height="10"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="white"
                                    strokeWidth="3"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  >
                                    <polyline points="20 6 9 17 4 12" />
                                  </svg>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      {resources.filter((m) =>
                        m.name
                          ?.toLowerCase()
                          .includes(assigneeSearch.toLowerCase()),
                      ).length === 0 && (
                        <div className="px-3 py-3 text-gray-400 text-sm">
                          No members found
                        </div>
                      )}
                    </div>
                  )}
                  {selectedAssignees.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedAssignees.map((member) => (
                        <div
                          key={member._id}
                          className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm flex items-center gap-2"
                        >
                          <span>{member.name}</span>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedAssignees((prev) =>
                                prev.filter((m) => m._id !== member._id),
                              );
                              setTaskForm({
                                ...taskForm,
                                assignees: taskForm.assignees.filter(
                                  (id) => id !== member._id,
                                ),
                              });
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="text-[16px] font-normal text-black">
                  Estimated Hours
                </label>
                {/* Same cap as Kanban's Add Card: 8 hours per working day in
                    the chosen range, enforced as the user types. */}
                <input
                  type="number"
                  min={1}
                  max={taskMaxHours ?? undefined}
                  className="w-full mt-1 h-[40px] border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="e.g., 8"
                  value={taskForm.hours}
                  onChange={(e) => {
                    const raw = e.target.value;
                    const capped =
                      taskMaxHours !== null &&
                      raw !== "" &&
                      Number(raw) > taskMaxHours
                        ? String(taskMaxHours)
                        : raw;
                    setTaskForm({ ...taskForm, hours: capped });
                  }}
                />
                {taskMaxHours !== null && (
                  <p className="text-xs text-gray-400 mt-1">
                    Max {taskMaxHours} hours (8 hours/day) for the selected
                    dates.
                  </p>
                )}
              </div>

              {/* Add Colour — the same picker as Kanban's Add Card. */}
              <div>
                <label className="text-[16px] font-normal text-black">
                  Add Colour
                </label>
                <div
                  className="w-full h-[40px] mt-1 border border-gray-300 rounded-lg px-3 flex items-center justify-between cursor-pointer"
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setTaskColorAnchor((prev) => (prev ? null : rect));
                  }}
                >
                  <span className="text-sm text-gray-500">
                    {taskForm.color
                      ? CARD_COLORS.find((c) => c.hex === taskForm.color)
                          ?.name || taskForm.color
                      : "Select Colour"}
                  </span>
                  <span
                    className="h-5 w-5 rounded ring-1 ring-black/10 shrink-0"
                    style={{ backgroundColor: taskForm.color || "#ffffff" }}
                  />
                </div>
              </div>

              <div className="col-span-1 sm:col-span-2">
                <label className="text-[16px] font-normal text-black">
                  Description
                </label>
                <textarea
                  rows="3"
                  className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  placeholder="Enter task description"
                  value={taskForm.description}
                  onChange={(e) =>
                    setTaskForm({ ...taskForm, description: e.target.value })
                  }
                />
              </div>
            </div>
            {taskFormError && (
              <div className="mt-4 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-600">
                {taskFormError}
              </div>
            )}
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => {
                  setShowTaskModal(false);
                  resetTaskForm();
                }}
                className="px-5 py-2 w-[163px] border rounded-lg text-gray-600 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTask}
                className="px-5 py-2 w-[163px] bg-[#4253C7] hover:bg-[#2f63e0] text-white rounded-lg"
              >
                Create Task
              </button>
            </div>
          </div>
        </div>
      )}

      {isResolvingProject && (
        <div className="flex items-center justify-center py-24 text-gray-400 text-sm">
          Loading project...
        </div>
      )}

      {/* ── PROJECT LIST ── */}
      {showProjectList && (
        <>
          {/* Title */}
          {/* Title */}
          <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-[26px] md:text-[34px] font-semibold text-black">
                Projects
              </h1>
              <p className="text-[14px] text-[#7E7E7E] mt-1">
                Manage And Track All Your Projects In One Piece
              </p>
            </div>

            {isAdmin && (
              <button
                onClick={() => {
                  if (!showDisabledProjects) loadDisabledProjects();
                  setShowDisabledProjects((prev) => !prev);
                }}
                className={
                  showDisabledProjects
                    ? "w-[50px] h-[37px] p-[8px] flex items-center justify-center gap-[10px] rounded-[8px] text-[#4253C7] text-[14px] leading-[21px] font-normal underline capitalize hover:text-[#2f3fa8] transition whitespace-nowrap"
                    : "px-[16px] py-[8px] h-[37px] border border-[#4253C7] rounded-[8px] text-[#4253C7] text-[14px] font-normal hover:bg-blue-50 transition whitespace-nowrap"
                }
              >
                {showDisabledProjects ? "Back" : "View Disable Projects"}
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {isDisabledProjectsLoading && showDisabledProjects ? (
              <p className="text-gray-400">Loading disabled projects...</p>
            ) : visibleProjects.length === 0 ? (
              <p className="text-gray-400">
                {showDisabledProjects
                  ? "No disabled projects 🚀"
                  : "No Projects Found 🚀"}
              </p>
            ) : (
              visibleProjects.map((p, i) => {
                const statusKey = resolveProjectStatus(p);
                const cfg = PROJECT_STATUS_CONFIG[statusKey];
                const progress = Math.max(
                  0,
                  Math.min(100, Number(p.progress) || 0),
                );
                const kanbanMembers =
                  kanbanProjects
                    .find((project) => String(project._id) === String(p._id))
                    ?.members?.map(getMemberInfo) || [];
                const memberList =
                  Array.isArray(p.members) && p.members.length > 0
                    ? p.members.map((rawMember) => {
                        const member = getMemberInfo(rawMember);
                        const kanbanMember = kanbanMembers.find(
                          (candidate) =>
                            String(candidate._id) === String(member._id),
                        );
                        return {
                          ...member,
                          profilePicture:
                            member.profilePicture ||
                            kanbanMember?.profilePicture,
                        };
                      })
                    : kanbanMembers;
                const totalMembers = p.totalMembers ?? p.members?.length ?? 0;
                const extra = Math.max(0, totalMembers - 3);

                return (
                  <div
                    key={p._id || i}
                    className="flex flex-col overflow-hidden rounded-[16px] border border-[#EEF0F6] bg-white shadow-sm"
                  >
                    <div className="flex-1 px-5 pt-5">
                      {/* Header */}
                      <div className="flex items-start gap-3">
                        <div className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-[16px] bg-[#E7EEFF]">
                          <Icons.KanbanPic />
                        </div>

                        <div className="min-w-0 flex-1">
                          <h2 className="truncate text-[20px] font-semibold leading-[24px] text-[#151A2D]">
                            {p.name}
                          </h2>
                          <p className="mt-0.5 line-clamp-2 text-[14px] leading-[20px] text-[#6B7A99]">
                            {p.description || "No description available"}
                          </p>
                        </div>

                        {p.isDisabled ? (
                          <span className="shrink-0 rounded-full bg-[#E5E7EB] px-3 py-1 text-[12px] font-semibold text-black/50">
                            DISABLED
                          </span>
                        ) : (
                          <span
                            className={`flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-[12px] font-semibold ${cfg.pill}`}
                          >
                            <span
                              className={`h-1.5 w-1.5 rounded-full ${cfg.dot}`}
                            />
                            {cfg.label}
                          </span>
                        )}
                      </div>

                      {/* Milestone progress */}
                      <div className="mt-5">
                        <div className="mb-1.5 flex items-center justify-between text-[14px]">
                          <span className="text-[#465675]">
                            Milestone Progress
                          </span>
                          <span
                            className="font-semibold"
                            style={{ color: cfg.bar }}
                          >
                            {progress}%
                          </span>
                        </div>
                        <div className="h-[6px] overflow-hidden rounded-full bg-[#EEF1F7]">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{
                              width: `${progress}%`,
                              backgroundColor: cfg.bar,
                            }}
                          />
                        </div>
                      </div>

                      {/* Stat tiles */}
                      <div className="mt-4 grid grid-cols-3 gap-2.5 pb-5">
                        <div className="flex items-center gap-2 rounded-[10px] bg-[#F8F9FC] p-2.5">
                          <div className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[8px] bg-[#DBE8FF] text-[#3B6FE0]">
                            <ClipboardList size={15} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-[15px] font-semibold text-[#151A2D]">
                              {getProjectTaskCount(p)}
                            </p>
                            <p className="truncate text-[12px] text-[#8A97AB]">
                              Total Tasks
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 rounded-[10px] bg-[#F8F9FC] p-2.5">
                          <div className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[8px] bg-[#EFE3FF] text-[#8B5CF6]">
                            <Users size={15} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-[15px] font-semibold text-[#151A2D]">
                              {totalMembers}
                            </p>
                            <p className="truncate text-[12px] text-[#8A97AB]">
                              Members
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 rounded-[10px] bg-[#F8F9FC] p-2.5">
                          <div className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[8px] bg-[#FFF0C9] text-[#D99A00]">
                            <Clock size={15} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-[15px] font-semibold text-[#151A2D]">
                              {getTimeAgo(p.updatedAt)}
                            </p>
                            <p className="truncate text-[12px] text-[#8A97AB]">
                              Activity
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between border-t border-[#EEF0F6] bg-[#FAFBFE] px-5 py-3.5">
                      <div className="flex items-center">
                        {memberList.slice(0, 3).map((m, idx) => (
                          <div
                            key={m._id || idx}
                            title={m.name}
                            className="flex h-[28px] w-[28px] items-center justify-center overflow-hidden rounded-full border-2 border-white text-[11px] font-semibold text-white"
                            style={{
                              marginLeft: idx === 0 ? 0 : -8,
                              backgroundColor:
                                AVATAR_COLORS[idx % AVATAR_COLORS.length],
                            }}
                          >
                            {getProfileImageUrl(m) ? (
                              <img
                                src={getProfileImageUrl(m)}
                                alt={m.name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              (m.name || "?").trim().charAt(0).toUpperCase()
                            )}
                          </div>
                        ))}
                        {extra > 0 && (
                          <div className="-ml-2 flex h-[28px] w-[28px] items-center justify-center rounded-full border-2 border-white bg-[#CBD5E1] text-[11px] font-semibold text-[#475569]">
                            +{extra}
                          </div>
                        )}
                      </div>

                      <button
                        onClick={() => {
                          selectProject(p);
                          navigate(`/dashboard/projects/${p._id}`);
                        }}
                        className="flex h-[35px] items-center gap-2 rounded-[8px] bg-[#4253C7] px-5 text-[15px] font-medium text-white hover:bg-[#3547B8]"
                      >
                        Open Project <span aria-hidden>→</span>
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}

      {/* ── PROJECT DASHBOARD ── */}
      {showProjectDashboard && (
        <>
          <button
            onClick={() => {
              deselectProject();
              navigate("/dashboard/projects");
            }}
            className="mb-4 text-blue-500 text-sm hover:underline"
          >
            Back
          </button>

          {/* Header remains usable for admins and managers when the project is disabled. */}
          <div className="relative overflow-hidden bg-white rounded-[8px] shadow-[0px_0px_4px_rgba(0,0,0,0.05)] px-4 sm:px-6 py-4 sm:py-6 flex flex-col lg:flex-row justify-between lg:items-start gap-4 mb-4">
            <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-[#4F46E5] via-[#A855F7] to-[#EC4899]" />
            <div className="flex-1 min-w-0">
              <div className="flex w-full flex-wrap items-center gap-3 mb-2">
                <span
                  className={`flex items-center gap-1.5 text-[11px] px-[10px] py-[4px] rounded-full font-semibold ${
                    selectedProject?.isDisabled
                      ? "bg-[#E5E7EB] text-black/50"
                      : PROJECT_STATUS_CONFIG[status].pill
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      selectedProject?.isDisabled
                        ? "bg-black/40"
                        : PROJECT_STATUS_CONFIG[status].dot
                    }`}
                  />
                  {selectedProject?.isDisabled
                    ? "Disabled"
                    : PROJECT_STATUS_CONFIG[status].label}
                </span>
                <span className="text-[12px] text-black/60 font-normal">
                  Updated {getTimeAgo(selectedProject?.updatedAt)}
                </span>
              </div>
              {isEditingProjectInfo ? (
                <div className="grid w-full max-w-[680px] grid-cols-1 gap-2 sm:grid-cols-2">
                  <input
                    className="h-12 w-full border-0 bg-transparent px-2 py-1 text-[18px] font-medium outline-none sm:text-[22px]"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                  />
                  <div className="relative w-full sm:col-start-2 sm:row-start-1 sm:before:absolute sm:before:-left-1 sm:before:top-2 sm:before:h-8 sm:before:w-px sm:before:bg-gray-300 sm:before:content-['']">
                    <div className="absolute -top-9 right-0 flex items-center gap-2">
                      <button
                        onClick={saveEditProjectInfo}
                        disabled={isUpdatingProject}
                        className="px-3 h-[28px] flex items-center justify-center rounded bg-[#4253C7] text-xs text-white disabled:opacity-60"
                      >
                        {isUpdatingProject ? "Saving..." : "Save"}
                      </button>
                      <button
                        onClick={cancelEditProjectInfo}
                        disabled={isUpdatingProject}
                        className="px-3 h-[28px] flex items-center justify-center rounded border border-gray-300 text-xs text-gray-500 disabled:opacity-60"
                      >
                        Cancel
                      </button>
                      {canManage && (
                        <button
                          onClick={() => {
                            if (!isAdmin) {
                              setShowManagerBlocked(true);
                              return;
                            }
                            if (selectedProject?.isDisabled) {
                              handleToggleDisable();
                            } else {
                              setShowDisableConfirm(true);
                            }
                          }}
                          className="px-3 h-[28px] flex items-center justify-center rounded border border-red-300 text-xs text-red-600 hover:bg-red-50 disabled:opacity-60"
                        >
                          {selectedProject?.isDisabled ? "Enable" : "Disable"}
                        </button>
                      )}
                    </div>
                    <textarea
                      rows="2"
                      className="h-12 w-full resize-y border-0 bg-transparent px-2 py-1 pb-5 text-[12px] outline-none"
                      value={editDescription}
                      maxLength={100}
                      placeholder="Add a project description"
                      onChange={(e) => setEditDescription(e.target.value)}
                    />
                    <span className="pointer-events-none absolute bottom-1.5 left-2 text-xs text-black/50">
                      {editDescription.length}/100
                    </span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3">
                    <h2
                      className={`truncate text-[20px] sm:text-[26px] font-medium leading-[36px] text-black ${
                        selectedProject?.isDisabled
                          ? "bg-[#F5F5F5] rounded-[8px] px-2"
                          : ""
                      }`}
                    >
                      {selectedProject?.name || "Untitled Project"}
                    </h2>
                    {canManage && (
                      <div className="relative shrink-0">
                        <button
                          onClick={startEditProjectInfo}
                          aria-label="Edit project name and description"
                          className="flex h-5 w-5 items-center justify-center rounded hover:bg-black/10"
                        >
                          <Icons.BPencil className="h-4 w-4 text-black" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="mt-1 max-w-[500px]">
                    <p className="text-[12px] leading-[18px] text-black/50">
                      {selectedProject?.description ||
                        "No description available"}
                    </p>
                  </div>
                </>
              )}
            </div>
            {/* Completion: the number and its label sit on one line, with a
                slim bar beneath, as in the design. */}
            <div className="flex flex-col items-start lg:items-end w-full lg:w-auto mt-4 lg:mt-0">
              <div className="flex items-baseline gap-2">
                <h2 className="text-[28px] sm:text-[32px] font-bold text-[#1F2937]">
                  {selectedProject?.progress || 0}%
                </h2>
                <span className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[#9AA3B5]">
                  Completion
                </span>
              </div>
              <div className="mt-2 w-[260px] sm:w-[340px] lg:w-[426px] h-[6px] bg-[#EDF0F7] rounded-full overflow-hidden">
                <div
                  className="h-full bg-[#4F46E5] rounded-full transition-all duration-500"
                  style={{ width: `${selectedProject?.progress || 0}%` }}
                />
              </div>
            </div>
          </div>

          {/* ── EVERYTHING BELOW THE HEADER IS LOCKED WHEN THE PROJECT IS DISABLED ── */}
          <div className="relative">
            {isBlockedByDisable && (
              <div className="absolute inset-0 z-40 flex items-start justify-center pt-24 pointer-events-none">
                <div className="bg-white rounded-[8px] shadow-lg px-8 py-6 text-center pointer-events-auto">
                  <p className="text-[18px] font-medium text-black mb-1">
                    This project has been disabled
                  </p>
                  <p className="text-[14px] text-black/50">
                    Contact your admin to regain access.
                  </p>
                </div>
              </div>
            )}
            <div
              className={
                isProjectDisabled
                  ? "opacity-50 pointer-events-none select-none"
                  : ""
              }
            >
              <div className="mb-4 flex gap-1 overflow-x-auto border-b border-[#E5E7EB]">
                {[
                  ["overview", "Overview"],
                  ["timeline", "Timeline"],
                  ["meeting-minutes", "Meeting Minutes"],
                  ["client-change-request", "Client Change Request"],
                ].map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setActiveProjectTab(id)}
                    className={`shrink-0 px-4 py-2.5 text-[16px] font-medium transition-colors border-b-2 ${
                      activeProjectTab === id
                        ? "border-[#4F46E5] text-[#4F46E5]"
                        : "border-transparent text-[#8A94A6] hover:text-[#4F46E5]"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {activeProjectTab === "meeting-minutes" && (
                <section className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(420px,1fr)]">
                  <div>
                    <div
                      className={`mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${showMeetingMinuteForm ? "hidden" : ""}`}
                    >
                      <div>
                        <h3 className="text-[26px] font-medium text-[#151A2D]">
                          Meeting Minutes
                        </h3>
                        <p className="mt-1 text-[14px] text-[#667085]">
                          {meetingMinutes.length}{" "}
                          {meetingMinutes.length === 1 ? "record" : "records"} ·
                          newest first
                        </p>
                      </div>
                      {/* Members are read-only here; the backend agrees. */}
                      {canSubmitMinutes && (
                        <button
                          type="button"
                          onClick={() => setShowMeetingMinuteForm(true)}
                          className="h-10 rounded-[9px] bg-[#4253C7] px-5 text-[14px] font-medium text-white"
                        >
                          + Add Minutes
                        </button>
                      )}
                    </div>

                    {showMeetingMinuteForm ? (
                      renderMinutesForm({
                        eyebrow: editingMinuteId
                          ? "Edit minutes entry"
                          : "New minutes entry",
                        saveLabel: editingMinuteId
                          ? "Save Changes"
                          : "Save Minutes",
                        onSave: saveMeetingMinutes,
                        onCancel: () => {
                          setShowMeetingMinuteForm(false);
                          resetMeetingMinuteForm();
                        },
                      })
                    ) : isLoadingMinutes ? (
                      <div className="flex min-h-[260px] items-center justify-center rounded-[14px] bg-white p-6 text-center shadow-[0_0_4px_rgba(0,0,0,0.06)]">
                        <p className="text-[13px] text-[#667085]">
                          Loading meeting minutes…
                        </p>
                      </div>
                    ) : meetingMinutes.length === 0 &&
                      pendingMinutes.length === 0 ? (
                      <div className="flex min-h-[260px] items-center justify-center rounded-[14px] bg-white p-6 text-center shadow-[0_0_4px_rgba(0,0,0,0.06)]">
                        <div>
                          <p className="text-[16px] font-medium text-[#151A2D]">
                            No meeting minutes yet
                          </p>
                          <p className="mt-2 text-[13px] text-[#667085]">
                            {meetingMinuteError ||
                              "Add minutes to record this project’s meetings, tasks and attendees."}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div
                        onClick={(event) => {
                          const card = event.target.closest("article");
                          if (card && event.currentTarget.contains(card)) {
                            const index = Array.from(
                              event.currentTarget.children,
                            ).indexOf(card);
                            if (meetingMinutes[index])
                              setSelectedMeetingMinuteId(
                                meetingMinutes[index].id,
                              );
                          }
                        }}
                        className="space-y-4"
                      >
                        {meetingMinutes.map((minute) => (
                          <article
                            key={minute.id}
                            className="rounded-[14px] bg-white p-5 shadow-[0_0_4px_rgba(0,0,0,0.06)]"
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div>
                                <div className="flex items-center gap-2">
                                  <h4 className="text-[19px] font-medium text-[#151A2D]">
                                    {minute.title}
                                  </h4>
                                  {minute.isPending && (
                                    <span className="rounded-full bg-[#FFF1D6] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#8A6100]">
                                      Pending
                                    </span>
                                  )}
                                  {isAdmin && (
                                    <button
                                      type="button"
                                      aria-label="Edit meeting minutes"
                                      title="Edit meeting minutes"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        startEditMinute(minute);
                                      }}
                                      className="text-[#60709C] transition-colors hover:text-[#4253C7]"
                                    >
                                      <FiEdit2 size={14} />
                                    </button>
                                  )}
                                </div>
                                <p className="mt-2 text-[13px] text-[#667085]">
                                  {minute.date || "No date"}
                                  {minute.time ? ` · ${minute.time}` : ""}
                                </p>
                                <p className="mt-1 text-[13px] text-[#465675]">
                                  {minute.taskName}
                                </p>
                              </div>
                              <span className="rounded-full bg-[#EEF1FF] px-3 py-1 text-[12px] font-medium text-[#4253C7]">
                                {minute.type}
                              </span>
                            </div>
                            <div className="mt-4 flex items-end justify-between border-t border-[#E8ECF5] pt-4">
                              <div>
                                <p className="text-[11px] uppercase tracking-wide text-[#60709C]">
                                  Decisions
                                </p>
                                <p className="mt-1 text-[16px] font-medium text-[#151A2D]">
                                  {minute.decisions?.filter(Boolean).length ||
                                    0}
                                </p>
                              </div>
                              <div className="max-w-[62%] text-right">
                                <p className="text-[11px] uppercase tracking-wide text-[#60709C]">
                                  Attendees
                                </p>
                                <p className="mt-1 text-[13px] text-[#465675]">
                                  {minute.attendees.length
                                    ? minute.attendees
                                        .map((member) => member.name)
                                        .join(", ")
                                    : "None selected"}
                                </p>
                              </div>
                            </div>
                            {/* Review controls for a client's submission. Approving also
    releases any change requests raised from this meeting. */}
                            {minute.isPending && canReviewSubmissions && (
                              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-[8px] bg-[#FFFBF3] px-3 py-2">
                                <p className="text-[12px] text-[#8A6100]">
                                  Submitted by {minute.createdByName} · awaiting
                                  your approval
                                </p>
                                <div className="flex shrink-0 gap-2">
                                  <button
                                    type="button"
                                    disabled={reviewingId === minute._id}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      reviewSubmission(
                                        "minute",
                                        minute._id,
                                        "rejected",
                                      );
                                    }}
                                    className="rounded-[7px] border border-[#F0C2C2] px-3 py-1.5 text-[11px] text-[#B42318] disabled:opacity-50"
                                  >
                                    Reject
                                  </button>
                                  <button
                                    type="button"
                                    disabled={reviewingId === minute._id}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      reviewSubmission(
                                        "minute",
                                        minute._id,
                                        "approved",
                                      );
                                    }}
                                    className="rounded-[7px] bg-[#22A06B] px-3 py-1.5 text-[11px] font-medium text-white disabled:opacity-50"
                                  >
                                    {reviewingId === minute._id
                                      ? "…"
                                      : "Approve"}
                                  </button>
                                </div>
                              </div>
                            )}
                            {minute.isPending && !canReviewSubmissions && (
                              <p className="mt-4 rounded-[8px] bg-[#FFFBF3] px-3 py-2 text-[12px] text-[#8A6100]">
                                Waiting for an admin or manager to approve these
                                minutes.
                              </p>
                            )}
                          </article>
                        ))}
                      </div>
                    )}
                  </div>
                  {showMeetingMinuteForm ? (
                    renderMinutesSidebar()
                  ) : (
                    <aside className="h-fit rounded-[14px] bg-white p-5 shadow-[0_0_4px_rgba(0,0,0,0.06)]">
                      <h4 className="text-[19px] font-medium text-[#151A2D]">
                        Open actions from minutes
                      </h4>
                      {meetingMinutes.length === 0 ? (
                        <div className="mt-5 border-t border-[#E8ECF5] pt-5 text-[13px] leading-5 text-[#667085]">
                          No saved meeting minutes yet.
                        </div>
                      ) : (
                        <div className="mt-4 divide-y divide-[#E8ECF5] border-t border-[#E8ECF5]">
                          {meetingMinutes
                            .filter(
                              (minute) =>
                                !selectedMeetingMinuteId ||
                                minute.id === selectedMeetingMinuteId,
                            )
                            .map((minute) => (
                              <div key={minute.id} className="py-4">
                                <p className="text-[14px] font-medium text-[#27324C]">
                                  {minute.taskName}
                                </p>
                                <p className="mt-1 text-[12px] text-[#667085]">
                                  {minute.title} · {minute.attendees.length}{" "}
                                  attendee
                                  {minute.attendees.length === 1 ? "" : "s"}
                                </p>
                                {minute.decisions?.length > 0 && (
                                  <p className="mt-2 text-[12px] leading-5 text-[#4253C7]">
                                    {minute.decisions.join(" · ")}
                                  </p>
                                )}
                                {minute.notes && (
                                  <p className="mt-2 text-[12px] leading-5 text-[#667085]">
                                    {minute.notes}
                                  </p>
                                )}
                                {minute.changeRequests?.length > 0 && (
                                  <div className="mt-3">
                                    <p className="text-[12px] font-medium text-[#4253C7]">
                                      Change requests
                                    </p>
                                    <div className="mt-1 space-y-1">
                                      {minute.changeRequests.map((request) => (
                                        <p
                                          key={request.id}
                                          className="text-[12px] leading-5 text-[#465675]"
                                        >
                                          • {request.text}
                                        </p>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {minute.attachments?.length > 0 && (
                                  <div className="mt-3">
                                    <p className="text-[12px] font-medium text-[#4253C7]">
                                      Attachments
                                    </p>
                                    <div className="mt-2 flex flex-wrap gap-2">
                                      {minute.attachments.map((file, index) =>
                                        file.type?.startsWith("image/") ? (
                                          <a
                                            key={`${file.name}-${index}`}
                                            href={file.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            title={file.name}
                                            className="block h-14 w-14 overflow-hidden rounded-[6px] border border-[#DCE2F0]"
                                          >
                                            <img
                                              src={file.url}
                                              alt={file.name}
                                              className="h-full w-full object-cover"
                                            />
                                          </a>
                                        ) : (
                                          <a
                                            key={`${file.name}-${index}`}
                                            href={file.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="max-w-[180px] truncate rounded-[6px] border border-[#DCE2F0] px-2 py-1 text-[12px] text-[#4253C7]"
                                          >
                                            {file.name}
                                          </a>
                                        ),
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>
                      )}
                      <p className="mt-5 border-t border-[#E8ECF5] pt-4 text-[12px] text-[#7D89A3]">
                        Meeting minutes are linked only to this project’s tasks
                        and resources.
                      </p>
                    </aside>
                  )}
                </section>
              )}

              {activeProjectTab === "client-change-request" && (
                <section>
                  {/* Header and button mirror the Meeting Minutes tab exactly:
                      same classes, same placement, and the header hides while
                      its form is open, the way Add Minutes does. */}
                  <div
                    className={`mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between ${showNewRequestForm ? "hidden" : ""}`}
                  >
                    <div>
                      <h3 className="text-[26px] font-medium text-[#151A2D]">
                        Client Change Request
                      </h3>
                      <p className="mt-1 text-[14px] text-[#667085]">
                        {changeRequests.length}{" "}
                        {changeRequests.length === 1 ? "request" : "requests"} ·
                        newest first
                      </p>
                    </div>
                    {canSubmitMinutes && (
                      <button
                        type="button"
                        onClick={() => {
                          resetMeetingMinuteForm();
                          setChangeRequestError("");
                          setShowNewRequestForm(true);
                        }}
                        className="h-10 rounded-[9px] bg-[#4253C7] px-5 text-[14px] font-medium text-white"
                      >
                        + New Request
                      </button>
                    )}
                  </div>

                  {/* New Request opens the very same Add Minutes form — same
                      component, so the two views cannot diverge. Saving it
                      records the meeting and raises its change request, which
                      is why both tabs update. The sidebar sits beside it just
                      as it does on the Meeting Minutes tab. */}
                  {showNewRequestForm && (
                    <div className="mb-4 grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(420px,1fr)]">
                      {renderMinutesForm({
                        eyebrow: "New change request",
                        saveLabel: "Save Request",
                        onSave: saveNewChangeRequest,
                        onCancel: () => {
                          setShowNewRequestForm(false);
                          resetMeetingMinuteForm();
                        },
                      })}
                      {renderMinutesSidebar()}
                    </div>
                  )}

                  {changeRequestError && (
                    <p className="mb-4 rounded-[7px] bg-[#FEF3F2] px-3 py-2 text-[12px] text-[#B42318]">
                      {changeRequestError}
                    </p>
                  )}

                  {/* Pending approval. Reviewers get Approve / Reject; the
                      client who raised it sees the same list read-only, so
                      they can tell their request is still waiting. */}
                  {pendingRequests.length > 0 && (
                    <div className="mb-4 rounded-[14px] border border-[#FFE2B8] bg-[#FFFBF3] p-5">
                      <div className="flex items-center justify-between">
                        <h4 className="text-[16px] font-medium text-[#8A6100]">
                          {canReviewSubmissions
                            ? "Pending approval"
                            : "Waiting for approval"}{" "}
                          ({pendingRequests.length})
                        </h4>
                        {canReviewSubmissions && (
                          <span className="text-[11px] text-[#8A6100]">
                            Raised by a client · not yet in the table
                          </span>
                        )}
                      </div>
                      <div className="mt-3 space-y-2">
                        {pendingRequests.map((row) => (
                          <div
                            key={row._id}
                            className="rounded-[8px] border border-[#F0E0C6] bg-white p-3"
                          >
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <p className="text-[13px] font-medium text-[#151A2D]">
                                  {row.title}
                                </p>
                                <p className="mt-1 text-[12px] text-[#667085]">
                                  by {row.createdBy} · {row.date} · {row.time}
                                  {row.isFromMeeting
                                    ? ` · ${row.decisionCount} decision${row.decisionCount === 1 ? "" : "s"}`
                                    : " · raised directly"}
                                </p>
                                {row.attendeeNames.length > 0 && (
                                  <p
                                    className="mt-1 truncate text-[12px] text-[#7D89A3]"
                                    title={row.attendeeNames.join(", ")}
                                  >
                                    Attendees: {row.attendeeNames.join(", ")}
                                  </p>
                                )}
                              </div>
                              {canReviewSubmissions ? (
                                <div className="flex shrink-0 gap-2">
                                  <button
                                    type="button"
                                    onClick={() => toggleRequestDetail(row._id)}
                                    className="rounded-[7px] border border-[#DCE2F0] px-3 py-1.5 text-[11px] text-[#4253C7]"
                                  >
                                    {openRequestId === row._id
                                      ? "Hide"
                                      : "View"}
                                  </button>
                                  <button
                                    type="button"
                                    disabled={reviewingId === row._id}
                                    onClick={() =>
                                      reviewSubmission(
                                        "request",
                                        row._id,
                                        "rejected",
                                      )
                                    }
                                    className="rounded-[7px] border border-[#F0C2C2] px-3 py-1.5 text-[11px] text-[#B42318] disabled:opacity-50"
                                  >
                                    Reject
                                  </button>
                                  <button
                                    type="button"
                                    disabled={reviewingId === row._id}
                                    onClick={() =>
                                      reviewSubmission(
                                        "request",
                                        row._id,
                                        "approved",
                                      )
                                    }
                                    className="rounded-[7px] bg-[#22A06B] px-3 py-1.5 text-[11px] font-medium text-white disabled:opacity-50"
                                  >
                                    {reviewingId === row._id ? "…" : "Approve"}
                                  </button>
                                </div>
                              ) : (
                                <span className="shrink-0 rounded-full bg-[#FFF1D6] px-3 py-1 text-[11px] font-medium text-[#8A6100]">
                                  Pending
                                </span>
                              )}
                            </div>
                            {/* The full contents, so a reviewer can decide on
                                what was actually said rather than the title. */}
                            {openRequestId === row._id && requestDetail && (
                              <div className="mt-3 border-t border-[#F0E0C6] pt-3">
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                                  <div>
                                    <p className="text-[10px] uppercase tracking-wide text-[#60709C]">
                                      Meeting type
                                    </p>
                                    <p className="mt-0.5 text-[12px] text-[#27324C]">
                                      {requestDetail.meetingType}
                                    </p>
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-[10px] uppercase tracking-wide text-[#60709C]">
                                      Task
                                    </p>
                                    <p
                                      className="mt-0.5 truncate text-[12px] text-[#27324C]"
                                      title={requestDetail.taskName}
                                    >
                                      {requestDetail.taskName}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-[10px] uppercase tracking-wide text-[#60709C]">
                                      Decisions
                                    </p>
                                    <p className="mt-0.5 text-[12px] text-[#27324C]">
                                      {requestDetail.decisions.length}
                                    </p>
                                  </div>
                                </div>
                                {requestDetail.notes && (
                                  <div className="mt-3">
                                    <p className="text-[10px] uppercase tracking-wide text-[#60709C]">
                                      Discussion &amp; agenda notes
                                    </p>
                                    <p className="mt-1 whitespace-pre-wrap text-[12px] leading-5 text-[#465675]">
                                      {requestDetail.notes}
                                    </p>
                                  </div>
                                )}
                                {requestDetail.decisions.length > 0 && (
                                  <div className="mt-3">
                                    <p className="text-[10px] uppercase tracking-wide text-[#60709C]">
                                      Decisions made
                                    </p>
                                    <div className="mt-1 space-y-1">
                                      {requestDetail.decisions.map((d, i) => (
                                        <p
                                          key={i}
                                          className="text-[12px] leading-5 text-[#465675]"
                                        >
                                          {String(i + 1).padStart(2, "0")} · {d}
                                        </p>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                {requestDetail.attachments.length > 0 && (
                                  <div className="mt-3">
                                    <p className="text-[10px] uppercase tracking-wide text-[#60709C]">
                                      Attachments
                                    </p>
                                    <div className="mt-1 flex flex-wrap gap-2">
                                      {requestDetail.attachments.map((f) => (
                                        <a
                                          key={f._id}
                                          href={f.url}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="max-w-[200px] truncate rounded-[6px] border border-[#DCE2F0] px-2 py-1 text-[11px] text-[#4253C7]"
                                          title={f.name}
                                        >
                                          {f.name}
                                        </a>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Styled like the Contacts table in Settings: percentage
                      column widths, a light header, and a fixed row height so
                      every row sits uniformly however long the attendee list
                      is. Scroll is confined to the table, so the page body
                      never moves sideways on a smaller laptop. */}
                  <div
                    ref={changeRequestTableRef}
                    className="rounded-[14px] bg-white px-5 py-4 shadow-[0_0_4px_rgba(0,0,0,0.06)]"
                  >
                    <div className="overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
                      {isLoadingChangeRequests ? (
                        <div className="flex justify-center py-16">
                          <Loader2
                            className="animate-spin text-[#4253C7]"
                            size={28}
                          />
                        </div>
                      ) : changeRequests.length === 0 ? (
                        <p className="text-center text-gray-400 py-16">
                          No change requests found.
                        </p>
                      ) : (
                        <table className="w-full min-w-[640px] lg:min-w-0">
                          <thead>
                            <tr className="text-gray-400 text-xs border-b">
                              <th className="pb-3 font-normal text-left w-[12%]">
                                CCR No.
                              </th>
                              <th className="pb-3 font-normal text-center w-[12%]">
                                Time
                              </th>
                              <th className="pb-3 font-normal text-center w-[18%]">
                                Date
                              </th>
                              <th className="pb-3 font-normal text-center w-[20%]">
                                Created By
                              </th>
                              <th className="pb-3 font-normal text-center w-[24%]">
                                Attendees
                              </th>
                              <th className="pb-3 font-normal text-center w-[14%]">
                                Decisions Made
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {changeRequests.map((row) => (
                              <React.Fragment key={row._id}>
                                <tr
                                  onClick={() => toggleRequestDetail(row._id)}
                                  className={`border-b text-sm h-[49px] cursor-pointer transition-colors hover:bg-[#F7F8FC] ${openRequestId === row._id ? "bg-[#F4F6FF]" : ""}`}
                                >
                                  <td className="py-[14px] text-xs text-left font-medium text-[#4253C7]">
                                    {row.ccrNo}
                                  </td>
                                  <td className="text-xs text-center">
                                    {row.time}
                                  </td>
                                  <td className="text-xs text-center">
                                    {row.date}
                                  </td>
                                  <td className="text-xs text-center capitalize">
                                    {row.createdBy}
                                  </td>
                                  {/* Truncated rather than wrapped: a long attendee
                                    list would otherwise make this row taller than
                                    the rest and break the uniform height. The
                                    max-width is what gives truncate something to
                                    clip against in an auto-layout table; the full
                                    list stays available on hover. */}
                                  <td className="text-xs text-center">
                                    <span
                                      className="mx-auto block max-w-[180px] truncate"
                                      title={row.attendeeNames.join(", ")}
                                    >
                                      {row.attendeeNames.length
                                        ? row.attendeeNames.join(", ")
                                        : "—"}
                                    </span>
                                  </td>
                                  <td className="text-center">
                                    <span className="text-xs px-3 py-1 rounded-full font-normal bg-[#CDDDFA] text-[#4379FE]">
                                      {row.decisionCount}
                                    </span>
                                  </td>
                                </tr>
                                {/* Detail card. It sits in its own row spanning
                                  every column, which is what makes it exactly
                                  as wide as the table. */}
                                {openRequestId === row._id && (
                                  <tr className="border-b">
                                    <td colSpan={6} className="p-0">
                                      <div className="my-3 rounded-[12px] border border-[#E8ECF5] bg-[#FBFCFF] p-5">
                                        {isLoadingRequestDetail ||
                                        !requestDetail ? (
                                          <div className="flex justify-center py-10">
                                            <Loader2
                                              className="animate-spin text-[#4253C7]"
                                              size={24}
                                            />
                                          </div>
                                        ) : (
                                          <>
                                            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-[#E8ECF5] pb-4">
                                              <div className="min-w-0">
                                                <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#60709C]">
                                                  CCR {requestDetail.ccrNo}
                                                </p>
                                                <h4 className="mt-1 text-[18px] font-medium text-[#151A2D]">
                                                  {requestDetail.title}
                                                </h4>
                                                {requestDetail.meetingTitle && (
                                                  <p className="mt-1 text-[12px] text-[#667085]">
                                                    From “
                                                    {requestDetail.meetingTitle}
                                                    ”
                                                  </p>
                                                )}
                                              </div>
                                              <span className="rounded-full bg-[#EEF1FF] px-3 py-1 text-[12px] font-medium text-[#4253C7]">
                                                {requestDetail.meetingType}
                                              </span>
                                            </div>

                                            {/* Date / meeting type / task, laid out
                                              side by side like the task popup. */}
                                            <div className="grid grid-cols-1 gap-4 border-b border-[#E8ECF5] py-4 sm:grid-cols-3">
                                              <div>
                                                <p className="text-[11px] uppercase tracking-wide text-[#60709C]">
                                                  Date
                                                </p>
                                                <p className="mt-1 text-[13px] text-[#27324C]">
                                                  {requestDetail.date} ·{" "}
                                                  {requestDetail.time}
                                                </p>
                                              </div>
                                              <div>
                                                <p className="text-[11px] uppercase tracking-wide text-[#60709C]">
                                                  Meeting type
                                                </p>
                                                <p className="mt-1 text-[13px] text-[#27324C]">
                                                  {requestDetail.meetingType}
                                                </p>
                                              </div>
                                              <div className="min-w-0">
                                                <p className="text-[11px] uppercase tracking-wide text-[#60709C]">
                                                  Task
                                                </p>
                                                <p
                                                  className="mt-1 truncate text-[13px] text-[#27324C]"
                                                  title={requestDetail.taskName}
                                                >
                                                  {requestDetail.taskName}
                                                </p>
                                              </div>
                                            </div>

                                            <div className="border-b border-[#E8ECF5] py-4">
                                              <p className="text-[11px] uppercase tracking-wide text-[#60709C]">
                                                Discussion &amp; agenda notes
                                              </p>
                                              <p className="mt-2 whitespace-pre-wrap text-[13px] leading-5 text-[#465675]">
                                                {requestDetail.notes ||
                                                  "No notes recorded."}
                                              </p>
                                            </div>

                                            <div className="border-b border-[#E8ECF5] py-4">
                                              <p className="text-[11px] uppercase tracking-wide text-[#60709C]">
                                                Decisions made (
                                                {requestDetail.decisions.length}
                                                )
                                              </p>
                                              {requestDetail.decisions
                                                .length === 0 ? (
                                                <p className="mt-2 text-[13px] text-[#7D89A3]">
                                                  No decisions recorded.
                                                </p>
                                              ) : (
                                                <div className="mt-2 space-y-1.5">
                                                  {requestDetail.decisions.map(
                                                    (decision, index) => (
                                                      <p
                                                        key={index}
                                                        className="flex gap-2 text-[13px] leading-5 text-[#465675]"
                                                      >
                                                        <span className="text-[11px] text-[#4253C7]">
                                                          {String(
                                                            index + 1,
                                                          ).padStart(2, "0")}
                                                        </span>
                                                        <span>{decision}</span>
                                                      </p>
                                                    ),
                                                  )}
                                                </div>
                                              )}
                                            </div>

                                            <div
                                              className={
                                                requestDetail.attachments.length
                                                  ? "border-b border-[#E8ECF5] py-4"
                                                  : "pt-4"
                                              }
                                            >
                                              <p className="text-[11px] uppercase tracking-wide text-[#60709C]">
                                                Change raised
                                              </p>
                                              <div className="mt-2 space-y-1.5">
                                                {requestDetail.raisedChanges
                                                  .length === 0 ? (
                                                  <p className="text-[13px] text-[#7D89A3]">
                                                    Raised directly, not from a
                                                    meeting.
                                                  </p>
                                                ) : (
                                                  requestDetail.raisedChanges.map(
                                                    (change) => (
                                                      <p
                                                        key={change._id}
                                                        className={`text-[13px] leading-5 ${change.isCurrent ? "font-medium text-[#168353]" : "text-[#465675]"}`}
                                                      >
                                                        • CCR {change.ccrNo} —{" "}
                                                        {change.title}
                                                        {change.isCurrent
                                                          ? " (this request)"
                                                          : ""}
                                                      </p>
                                                    ),
                                                  )
                                                )}
                                              </div>
                                            </div>

                                            {requestDetail.attachments.length >
                                              0 && (
                                              <div className="pt-4">
                                                <p className="text-[11px] uppercase tracking-wide text-[#60709C]">
                                                  Attachments (
                                                  {
                                                    requestDetail.attachments
                                                      .length
                                                  }
                                                  )
                                                </p>
                                                <div className="mt-2 flex flex-wrap gap-2">
                                                  {requestDetail.attachments.map(
                                                    (file) =>
                                                      file.isImage ? (
                                                        <a
                                                          key={file._id}
                                                          href={file.url}
                                                          target="_blank"
                                                          rel="noreferrer"
                                                          title={file.name}
                                                          className="block h-14 w-14 overflow-hidden rounded-[6px] border border-[#DCE2F0]"
                                                        >
                                                          <img
                                                            src={file.url}
                                                            alt={file.name}
                                                            className="h-full w-full object-cover"
                                                          />
                                                        </a>
                                                      ) : (
                                                        <a
                                                          key={file._id}
                                                          href={file.url}
                                                          target="_blank"
                                                          rel="noreferrer"
                                                          className="max-w-[200px] truncate rounded-[6px] border border-[#DCE2F0] bg-white px-2 py-1 text-[12px] text-[#4253C7] hover:bg-[#F7F8FC]"
                                                          title={file.name}
                                                        >
                                                          {file.name}
                                                        </a>
                                                      ),
                                                  )}
                                                </div>
                                              </div>
                                            )}
                                          </>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </section>
              )}

              <div
                className={`grid gap-4 items-stretch ${
                  activeProjectTab === "timeline"
                    ? "grid-cols-1"
                    : "grid-cols-1 lg:grid-cols-2"
                } ${
                  activeProjectTab === "overview" ||
                  activeProjectTab === "timeline"
                    ? ""
                    : "hidden"
                }`}
              >
                {" "}
                {/* ════ LEFT ════ */}
                <div
                  className={`space-y-4 min-w-0 w-full overflow-hidden ${
                    activeProjectTab === "overview" ? "md:col-span-2" : ""
                  }`}
                >
                  {/* 3 Stat cards */}
                  <div
                    className={
                      activeProjectTab === "overview"
                        ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3"
                        : "hidden"
                    }
                  >
                    {/* Three summary tiles. The design puts the label above
                        the value, with a tinted icon tile on the left, so
                        each card reads as one line rather than two rows. */}
                    {[
                      {
                        key: "members",
                        label: "Members",
                        icon: <Icons.Members />,
                        value: `${resources.length} Resource${resources.length === 1 ? "" : "s"}`,
                        accent: "#10B981",
                        tint: "#E6F8F0",
                        badge: selectedProject?.isDisabled
                          ? "Disabled"
                          : "Active",
                      },
                      {
                        key: "created",
                        label: "Created By",
                        icon: <Icons.User />,
                        value: selectedProject?.createdBy?.name || "N/A",
                        accent: "#8B5CF6",
                        tint: "#F1EAFF",
                      },
                      {
                        key: "progress",
                        label: "Progress",
                        icon: <Icons.Progress />,
                        value: `${selectedProject?.progress || 0}%`,
                        accent: "#3B82F6",
                        tint: "#E4EEFF",
                      },
                    ].map((tile) => (
                      <div
                        key={tile.key}
                        className="relative overflow-hidden bg-white rounded-[12px] w-full px-5 py-4 border border-[#EDF0F7] shadow-[0_1px_3px_rgba(16,24,40,0.04)] flex items-center gap-3.5"
                      >
                        <span
                          className="absolute inset-x-0 top-0 h-[3px]"
                          style={{ backgroundColor: tile.accent }}
                        />
                        <span
                          className="shrink-0 w-10 h-10 rounded-[10px] flex items-center justify-center"
                          style={{
                            backgroundColor: tile.tint,
                            color: tile.accent,
                          }}
                        >
                          {tile.icon}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-[10px] font-semibold uppercase tracking-[0.08em] text-[#9AA3B5]">
                            {tile.label}
                          </span>
                          <span className="mt-0.5 flex items-center gap-2">
                            <span
                              className="block truncate text-[17px] font-semibold text-[#1F2937]"
                              title={tile.value}
                            >
                              {tile.value}
                            </span>
                            {tile.badge && (
                              <span className="shrink-0 rounded-full bg-[#E6F8F0] px-2 py-0.5 text-[10px] font-semibold text-[#059669]">
                                {tile.badge}
                              </span>
                            )}
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* ── BIG TIMELINE CARD (matches design image) ── */}
                  <section
                    className={
                      activeProjectTab === "timeline"
                        ? "w-full overflow-hidden rounded-[14px] border border-[#E3E8F2] bg-white shadow-[0_2px_7px_rgba(17,24,39,0.03)]"
                        : "hidden"
                    }
                    aria-label="Workstream progress summary"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setIsTimelineCollapsed((collapsed) => !collapsed)
                      }
                      aria-expanded={!isTimelineCollapsed}
                      className="flex w-full items-center gap-3 px-5 py-4 text-left sm:px-6"
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#F0F3FF] text-[#4F46E5]">
                        <ChevronDown
                          className={`h-5 w-5 transition-transform duration-200 ${isTimelineCollapsed ? "-rotate-90" : ""}`}
                        />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="text-[16px] font-semibold text-[#111827]">
                          Project Timeline
                        </span>
                        {!isTimelineCollapsed && (
                          <span className="mt-0.5 block text-[12px] text-[#7C8DB5]">
                            Progress across all seven project types
                          </span>
                        )}
                      </span>
                      {!isTimelineCollapsed && (
                        <span className="shrink-0 rounded-lg bg-[#F1F5F9] px-3 py-1 text-[12px] text-[#334155]">
                          Overall:{" "}
                          <strong className="text-[#4338CA]">
                            {selectedProject?.progress || 0}%
                          </strong>
                        </span>
                      )}
                    </button>
                    {!isTimelineCollapsed && (
                      <div className="grid grid-cols-1 gap-x-6 gap-y-4 border-t border-[#EEF2F7] px-5 py-4 sm:grid-cols-2 sm:px-6 lg:grid-cols-3">
                        {timelineData.map((item) => (
                          <div key={item.name}>
                            <div className="mb-1.5 flex items-center justify-between gap-2 text-[12px] font-medium">
                              <span className="flex min-w-0 items-center gap-2 text-[#172554]">
                                <span
                                  className="h-2 w-2 shrink-0 rounded-full"
                                  style={{ backgroundColor: item.color }}
                                />
                                <span className="truncate">{item.label}</span>
                              </span>
                              <span style={{ color: item.color }}>
                                {item.percent}%
                              </span>
                            </div>
                            <div className="h-2 overflow-hidden rounded-full bg-[#EDF1F7]">
                              <div
                                className="h-full rounded-full transition-all duration-700"
                                style={{
                                  width: `${item.percent}%`,
                                  backgroundColor: item.color,
                                }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>

                  <section
                    className={
                      activeProjectTab === "timeline"
                        ? "mt-0 w-full overflow-hidden rounded-[10px] border border-[#D9E1EE] bg-white font-[Poppins] shadow-[0_1px_4px_rgba(15,23,42,0.06)]"
                        : "hidden"
                    }
                    aria-label="Sprint plan"
                  >
                    <div className="border-b border-[#E7EDF5] px-4 py-0 sm:px-5">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
                          <h3 className="text-[18px] font-semibold text-[#172554]">
                            Sprint Schedule
                          </h3>
                          <span className="text-[13px] text-[#8A9AB5]">
                            • {sprintRows.length}{" "}
                            {sprintRows.length === 1 ? "Sprint" : "Sprints"}
                          </span>
                        </div>
                        <div className="flex shrink-0 translate-y-2 flex-col items-stretch gap-1">
                          {/* Planning the schedule is an admin/manager job —
                              the backend agrees, so a viewer would only get a
                              403 from this button. The Gantt toggle below is
                              a view control and stays for everyone. */}
                          {canManage && (
                            <button
                              type="button"
                              onClick={() => {
                                setSprintImportError("");
                                autoImportAvailable
                                  ? setShowAddSprintChooser(true)
                                  : openManualSprintForm();
                              }}
                              className="rounded-md bg-[#4F46E5] px-3 py-1.5 text-[12px] font-semibold text-white transition-colors hover:bg-[#4338CA]"
                            >
                              + New Sprint
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() =>
                              setShowGanttChart((visible) => !visible)
                            }
                            className="rounded-md border border-[#C7D2FE] bg-[#EEF2FF] px-3 py-1.5 text-[12px] font-semibold text-[#4F46E5] transition-colors hover:bg-[#E0E7FF]"
                          >
                            {showGanttChart
                              ? "Hide Gantt Chart"
                              : "Gantt Chart"}
                          </button>
                        </div>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-x-5 gap-y-1 text-[12px] text-[#63738F]">
                        <span className="flex items-center gap-2">
                          <span className="h-3 w-3 rounded-full bg-[#10B981]" />
                          Completed ({sprintStatusCounts.Completed})
                        </span>
                        <span className="flex items-center gap-2">
                          <span className="h-3 w-3 rounded-full bg-[#4F46E5]" />
                          Active ({sprintStatusCounts.Active})
                        </span>
                        <span className="flex items-center gap-2">
                          <span className="h-3 w-3 rounded-full bg-[#CBD5E1]" />
                          Scheduled ({sprintStatusCounts.Scheduled})
                        </span>
                      </div>
                    </div>
                    {showGanttChart && (
                      <div className="border-b border-[#DCE5F0] bg-[#F8FAFD] p-4">
                        <div className="mb-3 flex items-center justify-center gap-3">
                          <button
                            type="button"
                            aria-label="Previous month"
                            onClick={() =>
                              setGanttMonth(
                                new Date(
                                  visibleGanttMonth.getFullYear(),
                                  visibleGanttMonth.getMonth() - 1,
                                  1,
                                ),
                              )
                            }
                            className="rounded-md p-1 text-[#52627C] hover:bg-[#E8EEF8] hover:text-[#4F46E5]"
                          >
                            <ChevronLeft size={18} />
                          </button>
                          <span className="min-w-[150px] text-center text-[13px] font-semibold text-[#253858]">
                            {visibleGanttMonth.toLocaleDateString("en-US", {
                              month: "long",
                              year: "numeric",
                            })}
                          </span>
                          <button
                            type="button"
                            aria-label="Next month"
                            onClick={() =>
                              setGanttMonth(
                                new Date(
                                  visibleGanttMonth.getFullYear(),
                                  visibleGanttMonth.getMonth() + 1,
                                  1,
                                ),
                              )
                            }
                            className="rounded-md p-1 text-[#52627C] hover:bg-[#E8EEF8] hover:text-[#4F46E5]"
                          >
                            <ChevronRight size={18} />
                          </button>
                        </div>
                        <div className="overflow-x-auto">
                          <div className="min-w-[720px] overflow-hidden rounded-lg border border-[#DDE5EF] bg-white">
                            <div
                              className="grid border-b border-[#DDE5EF] bg-[#F3F6FA]"
                              style={{
                                gridTemplateColumns: `190px repeat(${ganttWeeks.length || 1}, minmax(110px, 1fr))`,
                              }}
                            >
                              <div className="px-3 py-2 text-[11px] font-semibold text-[#52627C]">
                                SPRINT
                              </div>
                              {ganttWeeks.map((week, index) => (
                                <div
                                  key={week.toISOString()}
                                  className="border-l border-[#E4EAF2] px-2 py-2 text-center text-[11px] font-semibold text-[#52627C]"
                                >
                                  W{index + 1} (
                                  {week.toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                  })}
                                  )
                                </div>
                              ))}
                            </div>
                            {visibleGanttSprints.length ? (
                              visibleGanttSprints.map((sprint) => {
                                const barClass =
                                  sprint.status === "Completed"
                                    ? "bg-[#10B981]"
                                    : sprint.status === "Active"
                                      ? "bg-[#4F46E5"
                                      : "bg-[#94A3B8]";
                                return (
                                  <div
                                    key={`gantt-${sprint.id}`}
                                    className="grid min-h-[62px] border-b border-[#EDF1F5] last:border-b-0"
                                    style={{
                                      gridTemplateColumns: `190px repeat(${ganttWeeks.length}, minmax(110px, 1fr))`,
                                    }}
                                  >
                                    <div
                                      className="flex items-center gap-2 px-3 text-[12px] font-medium text-[#334155] cursor-pointer"
                                      onClick={() => {
                                        if (!sprint.start) return;
                                        setGanttMonthState(
                                          new Date(
                                            `${String(sprint.start).slice(0, 7)}-01T12:00:00`,
                                          ),
                                        );
                                        setShowGanttChart(true);
                                      }}
                                    >
                                      <span
                                        className={`h-2.5 w-2.5 shrink-0 rounded-full ${barClass}`}
                                      />
                                      <span className="truncate">
                                        {sprint.sprint}
                                      </span>
                                    </div>
                                    <div
                                      className="relative col-span-full"
                                      style={{
                                        gridColumn: `2 / span ${ganttWeeks.length}`,
                                      }}
                                    >
                                      <div
                                        className="absolute inset-0"
                                        style={{
                                          backgroundImage:
                                            "linear-gradient(to right, #E4EAF2 1px, transparent 1px)",
                                          backgroundSize: `${100 / ganttWeeks.length}% 100%`,
                                        }}
                                      />
                                      <div
                                        className={`absolute top-3 h-9 rounded-md px-2 text-[11px] font-semibold leading-9 text-white shadow-sm cursor-pointer ${barClass}`}
                                        style={getGanttBarStyle(sprint)}
                                        title={sprint.sprint}
                                        onClick={() =>
                                          setSelectedSprint(sprint)
                                        }
                                      >
                                        {sprint.scope && sprint.scope !== "—"
                                          ? sprint.scope
                                          : "Goal not set"}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })
                            ) : (
                              <div className="px-4 py-8 text-center text-[12px] text-[#8A97AB]">
                                No dated sprints are available for this month.
                              </div>
                            )}{" "}
                          </div>
                        </div>
                      </div>
                    )}
                    <div className={showGanttChart ? "hidden" : ""}>
                      {/* No min-width: the table fits its container at every
                          size instead of scrolling sideways. Long values wrap
                          or truncate within their own cells. */}
                      <table className="w-full table-fixed border-collapse text-left text-[12px] text-[#34425C]">
                        {/* Six columns, matching the header and body rows.
                            Goal gives up room to Progress, which needs space
                            for its bar and percentage. */}
                        {/* The Action column exists only for admins and
                            managers, so its 10% is shared out among the rest
                            when it is absent — otherwise the table would
                            leave a gap where it used to be. */}
                        <colgroup>
                          {canManage ? (
                            <>
                              <col className="w-[16%]" />
                              <col className="w-[22%]" />
                              <col className="w-[16%]" />
                              <col className="w-[12%]" />
                              <col className="w-[24%]" />
                              <col className="w-[10%]" />
                            </>
                          ) : (
                            <>
                              <col className="w-[18%]" />
                              <col className="w-[25%]" />
                              <col className="w-[17%]" />
                              <col className="w-[13%]" />
                              <col className="w-[27%]" />
                            </>
                          )}
                        </colgroup>
                        <thead className="bg-[#F2F6FB] text-[13px] font-semibold uppercase text-[#6D7E98]">
                          <tr>
                            <th className="border-b border-[#DCE5F0] px-4 py-3">
                              Sprint
                            </th>
                            <th className="border-b border-[#DCE5F0] px-4 py-3">
                              Goal
                            </th>
                            <th className="border-b border-[#DCE5F0] px-4 py-3">
                              Timeline
                            </th>
                            <th className="border-b border-[#DCE5F0] px-4 py-3">
                              Status
                            </th>
                            <th className="border-b border-[#DCE5F0] px-4 py-3">
                              Progress
                            </th>
                            {canManage && (
                              <th className="border-b border-[#DCE5F0] px-4 py-3">
                                Action
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {sprintRows.map((sprint) => (
                            <tr
                              key={sprint.id}
                              className="border-b border-[#E7EDF5] text-[#34425C] last:border-b-0"
                            >
                              <td className="px-4 py-3 font-semibold text-[#253858]">
                                <button
                                  type="button"
                                  onClick={() => setSelectedSprint(sprint)}
                                  title={sprint.sprint}
                                  className="block w-full truncate text-left hover:text-[#4F46E5]"
                                >
                                  {sprint.sprint}
                                </button>
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className="block truncate"
                                  title={
                                    sprint.scope && sprint.scope !== "—"
                                      ? sprint.scope
                                      : undefined
                                  }
                                >
                                  {sprint.scope}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                {sprint.start && sprint.end
                                  ? formatSprintDateRange(
                                      sprint.start,
                                      sprint.end,
                                    )
                                  : sprint.duration ||
                                    formatSprintWeeks(
                                      sprint.start,
                                      sprint.end,
                                      sprint.duration,
                                    )}
                              </td>
                              <td className="px-4 py-3">
                                <span
                                  className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${getSprintStatusClass(sprint.status)}`}
                                >
                                  {formatSprintStatus(sprint.status)}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                {renderSprintProgress(sprint)}
                              </td>
                              {canManage && (
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-3">
                                    <button
                                      type="button"
                                      onClick={() => deleteSprint(sprint)}
                                      aria-label={`Delete ${sprint.sprint}`}
                                      className="text-[#EF4444] transition-colors hover:text-[#B91C1C]"
                                    >
                                      <Trash2 size={17} />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => editSprint(sprint)}
                                      aria-label={`Edit ${sprint.sprint}`}
                                      className="text-[#4F46E5] transition-colors hover:text-[#3730A3]"
                                    >
                                      <Icons.BPencil className="h-[17px] w-[17px]" />
                                    </button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                          {!sprintRows.length && (
                            <tr>
                              <td
                                colSpan={canManage ? 6 : 5}
                                className="px-4 py-8 text-center text-[#8A97AB]"
                              >
                                {isLoadingSprints
                                  ? "Loading sprints…"
                                  : newSprintError ||
                                    (canManage
                                      ? "No sprints yet. Select “+ New Sprint” to add one."
                                      : "No sprints have been scheduled for this project yet.")}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </section>
                  {/* ── END TIMELINE ── */}
                </div>
                {/* ════ END LEFT ════ */}
                {/* "+ New Sprint" asks how first: read a plan from a file,
                      or fill the form in by hand. */}
                {showAddSprintChooser && (
                  <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="add-sprint-choice-title"
                  >
                    <div className="w-full max-w-[520px] rounded-xl bg-white p-6 font-[Poppins] shadow-2xl">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h2
                            id="add-sprint-choice-title"
                            className="text-[30px] font-semibold leading-[36px] text-[#172554]"
                          >
                            Add Sprints
                          </h2>
                          <p className="mt-1 text-[16px] font-normal text-[#63738F]">
                            How would you like to add them?
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowAddSprintChooser(false)}
                          aria-label="Close"
                          className="rounded-md p-1 text-[#64748B] hover:bg-[#F1F5F9]"
                        >
                          <FiX size={20} />
                        </button>
                      </div>
                      {/* Three ways in: a file from this device, a file
                            from Drive, or the form by hand. The two automatic
                            options go straight to their source rather than
                            making the user choose twice. */}
                      <div className="mt-6 space-y-3">
                        <button
                          type="button"
                          onClick={openSprintImportFromDevice}
                          className="flex w-full items-start gap-3 rounded-lg border border-[#CBD5E1] p-4 text-left transition-colors hover:border-[#4F46E5] hover:bg-[#F5F6FF]"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                            className="mt-0.5 h-5 w-5 shrink-0 text-[#4F46E5]"
                          >
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="17 8 12 3 7 8" />
                            <line x1="12" y1="3" x2="12" y2="15" />
                          </svg>
                          <span className="min-w-0">
                            <span className="block text-[16px] font-semibold text-[#172554]">
                              Upload from this device
                            </span>
                            <span className="mt-1 block text-[13px] leading-[18px] text-[#63738F]">
                              Pick a sprint plan — Excel, PDF or CSV — and its
                              table is read for you.
                            </span>
                          </span>
                        </button>
                        {driveConfig.available && (
                          <button
                            type="button"
                            onClick={openSprintImportFromDrive}
                            disabled={isOpeningDrive}
                            className="flex w-full items-start gap-3 rounded-lg border border-[#CBD5E1] p-4 text-left transition-colors hover:border-[#4F46E5] hover:bg-[#F5F6FF] disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            <svg
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                              className="mt-0.5 h-5 w-5 shrink-0"
                            >
                              <path
                                fill="#0066DA"
                                d="M3.4 17.2 1.8 14.4c-.2-.3-.2-.7 0-1l5-8.7 3.2 5.6-5 8.7c-.3.5-1.1.6-1.6.2z"
                              />
                              <path
                                fill="#00AC47"
                                d="M12 3.1h5.8c.4 0 .7.2.9.5l5 8.7c.2.3.2.7 0 1l-1.6 2.8-8.9-.1L12 3.1z"
                              />
                              <path
                                fill="#EA4335"
                                d="M4.9 20.9c-.5 0-.9-.3-1.1-.7L3.4 17.2h17.3l-1.6 2.8c-.2.4-.6.6-1 .6H4.9z"
                              />
                              <path
                                fill="#FFBA00"
                                d="M15.3 10.3 10 1.6c-.2-.3-.5-.5-.9-.5H6.8L15.3 10.3z"
                                opacity=".0"
                              />
                            </svg>
                            <span className="min-w-0">
                              <span className="block text-[16px] font-semibold text-[#172554]">
                                {isOpeningDrive
                                  ? "Opening Google Drive…"
                                  : "Upload from Google Drive"}
                              </span>
                              <span className="mt-1 block text-[13px] leading-[18px] text-[#63738F]">
                                Choose a Sheet, Doc or PDF from your Drive. Only
                                the file you pick is shared.
                              </span>
                            </span>
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={openManualSprintForm}
                          className="flex w-full items-start gap-3 rounded-lg border border-[#CBD5E1] p-4 text-left transition-colors hover:border-[#4F46E5] hover:bg-[#F5F6FF]"
                        >
                          <Icons.BPencil className="mt-0.5 h-5 w-5 shrink-0 text-[#4F46E5]" />
                          <span className="min-w-0">
                            <span className="block text-[16px] font-semibold text-[#172554]">
                              Add manually
                            </span>
                            <span className="mt-1 block text-[13px] leading-[18px] text-[#63738F]">
                              Fill in one sprint's name, goal, dates and status
                              yourself.
                            </span>
                          </span>
                        </button>
                      </div>
                      {/* A Drive failure happens before the import dialog
                            opens, so it has to be reported here. */}
                      {sprintImportError && (
                        <p className="mt-4 rounded-md bg-[#FEF3F2] px-3 py-2 text-[13px] text-[#B42318]">
                          {sprintImportError}
                        </p>
                      )}
                    </div>
                  </div>
                )}
                {/* Automatic import: choose a file, then review what was
                      read before it is created. */}
                {showSprintImport && (
                  <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="import-sprint-title"
                  >
                    <div className="flex max-h-[90vh] w-full max-w-[760px] flex-col rounded-xl bg-white p-6 font-[Poppins] shadow-2xl">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h2
                            id="import-sprint-title"
                            className="text-[30px] font-semibold leading-[36px] text-[#172554]"
                          >
                            Import sprints
                          </h2>
                          <p className="mt-1 text-[16px] font-normal text-[#63738F]">
                            {sprintImportRows.length
                              ? "Check each row, then import the ones you want."
                              : "Upload a sprint plan and its table will be read for you."}
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={closeSprintImport}
                          aria-label="Close"
                          className="rounded-md p-1 text-[#64748B] hover:bg-[#F1F5F9]"
                        >
                          <FiX size={20} />
                        </button>
                      </div>

                      {sprintImportRows.length === 0 ? (
                        <div className="mt-5">
                          <input
                            ref={sprintFileInputRef}
                            type="file"
                            accept=".xlsx,.xlsm,.csv,.pdf,.txt,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv,text/plain"
                            className="hidden"
                            onChange={(event) => {
                              setSprintImportFile(
                                event.target.files?.[0] || null,
                              );
                              setDriveFile(null);
                              setSprintImportError("");
                              event.target.value = "";
                            }}
                          />
                          {/* Two sources. Drive is hidden entirely until
                                its credentials are configured. */}
                          <div
                            className={`grid gap-3 ${driveConfig.available ? "sm:grid-cols-2" : "grid-cols-1"}`}
                          >
                            <button
                              type="button"
                              onClick={() =>
                                sprintFileInputRef.current?.click()
                              }
                              className="flex min-h-[140px] flex-col items-center justify-center rounded-lg border border-dashed border-[#B8C0CC] bg-[#FBFCFF] px-5 text-center hover:bg-[#F5F6FF]"
                            >
                              <span className="text-[15px] font-semibold text-[#4F46E5]">
                                From this device
                              </span>
                              <span className="mt-1 text-[12px] text-[#7D89A3]">
                                Excel (.xlsx), PDF, or CSV · up to 10 MB
                              </span>
                            </button>
                            {driveConfig.available && (
                              <button
                                type="button"
                                onClick={chooseFromGoogleDrive}
                                disabled={isOpeningDrive}
                                className="flex min-h-[140px] flex-col items-center justify-center rounded-lg border border-dashed border-[#B8C0CC] bg-[#FBFCFF] px-5 text-center hover:bg-[#F5F6FF] disabled:cursor-not-allowed disabled:opacity-60"
                              >
                                <span className="text-[15px] font-semibold text-[#4F46E5]">
                                  {isOpeningDrive
                                    ? "Opening Drive…"
                                    : "From Google Drive"}
                                </span>
                                <span className="mt-1 text-[12px] text-[#7D89A3]">
                                  Sheets, Docs, PDFs and uploaded files
                                </span>
                              </button>
                            )}
                          </div>
                          {(sprintImportFile || driveFile) && (
                            <p
                              className="mt-3 truncate text-[13px] text-[#465675]"
                              title={driveFile?.name || sprintImportFile?.name}
                            >
                              Selected:{" "}
                              {driveFile?.name || sprintImportFile?.name}
                              {driveFile && (
                                <span className="ml-1.5 text-[12px] text-[#7D89A3]">
                                  (Google Drive)
                                </span>
                              )}
                            </p>
                          )}
                          <p className="mt-3 text-[12px] leading-[17px] text-[#7D89A3]">
                            The file is read for its sprint table and then
                            discarded — it is not stored. Nothing is saved until
                            you confirm.
                            {driveConfig.available &&
                              " QwickHub only sees the one file you pick from Drive."}
                          </p>
                        </div>
                      ) : (
                        <div className="mt-5 min-h-0 flex-1 overflow-y-auto">
                          <table className="w-full min-w-[620px]">
                            <thead>
                              <tr className="text-gray-400 text-xs border-b">
                                <th className="pb-3 font-normal text-left w-[6%]">
                                  {" "}
                                </th>
                                <th className="pb-3 font-normal text-left w-[18%]">
                                  Sprint
                                </th>
                                <th className="pb-3 font-normal text-left w-[30%]">
                                  Scope
                                </th>
                                <th className="pb-3 font-normal text-center w-[16%]">
                                  Duration
                                </th>
                                <th className="pb-3 font-normal text-center w-[18%]">
                                  Dates
                                </th>
                                <th className="pb-3 font-normal text-center w-[12%]">
                                  Status
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {sprintImportRows.map((row, index) => (
                                <tr
                                  key={`${row.name}-${index}`}
                                  className={`border-b text-sm h-[49px] ${row.include ? "" : "opacity-50"}`}
                                >
                                  <td className="py-[14px]">
                                    <input
                                      type="checkbox"
                                      checked={row.include}
                                      onChange={() =>
                                        setSprintImportRows((rows) =>
                                          rows.map((r, i) =>
                                            i === index
                                              ? { ...r, include: !r.include }
                                              : r,
                                          ),
                                        )
                                      }
                                      className="h-4 w-4 accent-[#4F46E5]"
                                    />
                                  </td>
                                  <td className="text-xs font-medium text-[#172554]">
                                    {row.name}
                                    {row.duplicate && (
                                      <span className="ml-1.5 rounded-full bg-[#FFF1D6] px-2 py-0.5 text-[10px] font-semibold text-[#8A6100]">
                                        exists
                                      </span>
                                    )}
                                  </td>
                                  <td className="text-xs text-[#465675]">
                                    <span
                                      className="block max-w-[220px] truncate"
                                      title={row.scope}
                                    >
                                      {row.scope || "—"}
                                    </span>
                                  </td>
                                  <td className="text-xs text-center text-[#465675]">
                                    {row.duration || "—"}
                                  </td>
                                  <td className="text-xs text-center text-[#465675]">
                                    {row.startDate && row.endDate
                                      ? `${row.startDate} → ${row.endDate}`
                                      : "—"}
                                  </td>
                                  <td className="text-center">
                                    <span
                                      className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${getSprintStatusClass(row.status)}`}
                                    >
                                      {row.status}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {sprintImportError && (
                        <p className="mt-3 rounded-md bg-[#FEF3F2] px-3 py-2 text-[13px] text-[#B42318]">
                          {sprintImportError}
                        </p>
                      )}

                      <div className="mt-6 flex items-center justify-between gap-3 border-t border-[#E8ECF5] pt-4">
                        <p className="text-[12px] text-[#7D89A3]">
                          {sprintImportRows.length
                            ? `${sprintImportRows.filter((r) => r.include).length} of ${sprintImportRows.length} selected`
                            : "Rows marked “exists” start unticked."}
                        </p>
                        <div className="flex shrink-0 gap-3">
                          <button
                            type="button"
                            onClick={closeSprintImport}
                            className="h-10 min-w-[110px] rounded-md border border-[#CBD5E1] px-4 text-[14px] font-normal text-[#475569]"
                          >
                            Cancel
                          </button>
                          {sprintImportRows.length === 0 ? (
                            <button
                              type="button"
                              onClick={parseSprintFile}
                              disabled={
                                (!sprintImportFile && !driveFile) ||
                                isParsingSprintFile
                              }
                              className="h-10 min-w-[110px] rounded-md bg-[#4F46E5] px-5 text-[14px] font-normal text-white hover:bg-[#4338CA] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {isParsingSprintFile ? "Reading..." : "Read file"}
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={confirmSprintImport}
                              disabled={
                                isImportingSprints ||
                                !sprintImportRows.some((r) => r.include)
                              }
                              className="h-10 min-w-[110px] rounded-md bg-[#4F46E5] px-5 text-[14px] font-normal text-white hover:bg-[#4338CA] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              {isImportingSprints ? "Importing..." : "Import"}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {showNewSprintForm && (
                  <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="new-sprint-title"
                  >
                    <form
                      onSubmit={(event) => {
                        event.preventDefault();
                        saveNewSprint();
                      }}
                      className="w-full max-w-[520px] rounded-xl bg-white p-6 font-[Poppins] shadow-2xl"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h2
                            id="new-sprint-title"
                            className="text-[30px] font-semibold leading-[36px] text-[#172554]"
                          >
                            {editingSprintId ? "Edit Sprint" : "New Sprint"}
                          </h2>
                          <p className="mt-1 text-[16px] font-normal text-[#63738F]">
                            Add the sprint details to this project.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setShowNewSprintForm(false);
                            setEditingSprintId(null);
                            setNewSprintError("");
                          }}
                          aria-label="Close new sprint form"
                          className="rounded-md p-1 text-[#64748B] hover:bg-[#F1F5F9]"
                        >
                          <FiX size={20} />
                        </button>
                      </div>
                      <div className="mt-5 grid gap-4 sm:grid-cols-2">
                        <label className="sm:col-span-2 text-[16px] font-normal leading-[100%] text-black">
                          Name<span className="text-[#DC2626]"> *</span>
                          <input
                            autoFocus
                            required
                            value={newSprintDraft.name}
                            onChange={(event) =>
                              setNewSprintDraft((draft) => ({
                                ...draft,
                                name: event.target.value,
                              }))
                            }
                            placeholder="e.g. Sprint 3"
                            className="mt-1.5 h-10 w-full rounded-md border border-[#CBD5E1] px-3 text-[14px] font-normal text-[#172554] outline-none focus:border-[#4F46E5]"
                          />
                        </label>
                        <div className="sm:col-span-2">
                          <div className="grid gap-4 sm:grid-cols-2">
                            <label className="text-[16px] font-normal leading-[100%] text-black">
                              Start date
                              <span className="text-[#DC2626]"> *</span>
                              <div className="relative mt-[6px]">
                                <div
                                  className="absolute left-0 top-0 z-10 flex h-[42px] w-[42px] cursor-pointer items-center justify-center rounded-l-[8px] border border-gray-300 border-r-0 bg-[#0404040A]"
                                  onClick={() =>
                                    document
                                      .getElementById("sprintStartDatePicker")
                                      ?.focus()
                                  }
                                >
                                  <Icons.Calendar />
                                </div>
                                <DatePicker
                                  id="sprintStartDatePicker"
                                  selected={
                                    newSprintDraft.startDate
                                      ? new Date(
                                          `${newSprintDraft.startDate}T12:00:00`,
                                        )
                                      : null
                                  }
                                  onChange={(date) =>
                                    setNewSprintDraft((draft) => ({
                                      ...draft,
                                      startDate: date
                                        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
                                        : "",
                                    }))
                                  }
                                  placeholderText="Select start date"
                                  className="h-[42px] w-full rounded-[8px] border border-gray-300 pl-[50px] pr-3 text-[14px]"
                                  dateFormat="dd MMM yyyy"
                                  popperPlacement="bottom-start"
                                  popperClassName="z-[60]"
                                  showPopperArrow={false}
                                  showMonthDropdown
                                  showYearDropdown
                                  dropdownMode="select"
                                />
                              </div>
                            </label>
                            <label className="text-[16px] font-normal leading-[100%] text-black">
                              End date<span className="text-[#DC2626]"> *</span>
                              <div className="relative mt-[6px]">
                                <div
                                  className="absolute left-0 top-0 z-10 flex h-[42px] w-[42px] cursor-pointer items-center justify-center rounded-l-[8px] border border-gray-300 border-r-0 bg-[#0404040A]"
                                  onClick={() =>
                                    document
                                      .getElementById("sprintEndDatePicker")
                                      ?.focus()
                                  }
                                >
                                  <Icons.Calendar />
                                </div>
                                <DatePicker
                                  id="sprintEndDatePicker"
                                  selected={
                                    newSprintDraft.endDate
                                      ? new Date(
                                          `${newSprintDraft.endDate}T12:00:00`,
                                        )
                                      : null
                                  }
                                  onChange={(date) =>
                                    setNewSprintDraft((draft) => ({
                                      ...draft,
                                      endDate: date
                                        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`
                                        : "",
                                    }))
                                  }
                                  placeholderText="Select end date"
                                  minDate={
                                    newSprintDraft.startDate
                                      ? new Date(
                                          `${newSprintDraft.startDate}T12:00:00`,
                                        )
                                      : null
                                  }
                                  className="h-[42px] w-full rounded-[8px] border border-gray-300 pl-[50px] pr-3 text-[14px]"
                                  dateFormat="dd MMM yyyy"
                                  popperPlacement="bottom-start"
                                  popperClassName="z-[60]"
                                  showPopperArrow={false}
                                  showMonthDropdown
                                  showYearDropdown
                                  dropdownMode="select"
                                />
                              </div>
                            </label>
                          </div>
                        </div>
                        <label className="sm:col-span-2 text-[16px] font-normal leading-[100%] text-black">
                          Goal
                          <textarea
                            value={newSprintDraft.scope}
                            onChange={(event) =>
                              setNewSprintDraft((draft) => ({
                                ...draft,
                                scope: event.target.value,
                              }))
                            }
                            placeholder="What is the goal of this sprint?"
                            rows={3}
                            className="mt-1.5 block w-full resize-y rounded-md border border-[#CBD5E1] px-3 py-2 text-[14px] font-normal text-[#172554] outline-none focus:border-[#4F46E5]"
                          />
                        </label>
                        <label className="sm:col-span-2 text-[16px] font-normal leading-[100%] text-black">
                          Status
                          <select
                            value={newSprintDraft.status}
                            onChange={(event) =>
                              setNewSprintDraft((draft) => ({
                                ...draft,
                                status: event.target.value,
                              }))
                            }
                            className="mt-1.5 h-10 w-full rounded-md border border-[#CBD5E1] bg-white px-3 text-[14px] font-normal text-[#172554] outline-none focus:border-[#4F46E5]"
                          >
                            <option>Scheduled</option>
                            <option>Active</option>
                            <option>Completed</option>
                          </select>
                        </label>
                        {/* Kanban lists under this sprint. A list belongs
                              to one sprint at a time, so any already taken is
                              shown disabled with the sprint that holds it. */}
                        <div className="sm:col-span-2">
                          <p className="text-[16px] font-normal leading-[100%] text-black">
                            Kanban lists under this sprint
                          </p>
                          {availableLists.length === 0 ? (
                            <p className="mt-1.5 rounded-md border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2.5 text-[13px] text-[#8A97AB]">
                              This project has no Kanban lists yet.
                            </p>
                          ) : (
                            <div className="mt-1.5 max-h-[168px] space-y-1 overflow-y-auto rounded-md border border-[#CBD5E1] p-2">
                              {availableLists.map((list) => {
                                const checked = (
                                  newSprintDraft.lists || []
                                ).includes(list._id);
                                const taken =
                                  Boolean(list.assignedTo) && !checked;
                                return (
                                  <label
                                    key={list._id}
                                    className={`flex items-center gap-2 rounded px-2 py-1.5 text-[14px] ${taken ? "cursor-not-allowed text-[#A0AAB8]" : "cursor-pointer text-[#172554] hover:bg-[#F1F5F9]"}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      disabled={taken}
                                      onChange={() =>
                                        toggleSprintList(list._id)
                                      }
                                      className="h-4 w-4 accent-[#4F46E5]"
                                    />
                                    <span className="flex-1 truncate">
                                      {list.name}
                                    </span>
                                    {taken && (
                                      <span className="shrink-0 text-[11px] text-[#A0AAB8]">
                                        in {list.assignedTo.name}
                                      </span>
                                    )}
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                      {newSprintError && (
                        <p className="mt-4 text-[12px] text-[#DC2626]">
                          {newSprintError}
                        </p>
                      )}
                      <div className="mt-6 flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            setShowNewSprintForm(false);
                            setNewSprintError("");
                          }}
                          className="h-10 min-w-[120px] rounded-md border border-[#CBD5E1] px-4 text-[14px] font-normal text-[#475569]"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={
                            !newSprintDraft.name.trim() || isSavingSprint
                          }
                          className="h-10 min-w-[120px] rounded-md bg-[#4F46E5] px-5 text-[14px] font-normal text-white hover:bg-[#4338CA] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isSavingSprint ? "Saving..." : "Save"}
                        </button>
                      </div>
                    </form>
                  </div>
                )}
                {selectedSprint && (
                  <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="sprint-details-title"
                    onMouseDown={(event) => {
                      if (event.target === event.currentTarget)
                        setSelectedSprint(null);
                    }}
                  >
                    <div className="w-full max-w-[460px] rounded-xl bg-white p-6 font-[Poppins] shadow-2xl">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h2
                            id="sprint-details-title"
                            className="text-[26px] font-semibold leading-[32px] text-[#172554]"
                          >
                            {selectedSprint.sprint}
                          </h2>
                          <p className="mt-1 text-[16px] font-normal text-[#63738F]">
                            Saved sprint details
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSelectedSprint(null)}
                          aria-label="Close sprint details"
                          className="rounded-md p-1 text-[#64748B] hover:bg-[#F1F5F9]"
                        >
                          <FiX size={22} />
                        </button>
                      </div>
                      <div className="mt-5 space-y-4 text-[16px] text-[#34425C]">
                        <div>
                          <p className="text-[13px] font-semibold uppercase text-[#8A9AB5]">
                            Goal
                          </p>
                          <p className="mt-1">{selectedSprint.scope}</p>
                        </div>
                        <div>
                          <p className="text-[13px] font-semibold uppercase text-[#8A9AB5]">
                            Timeline
                          </p>
                          <p className="mt-1">
                            {selectedSprint.start && selectedSprint.end
                              ? formatSprintDateRange(
                                  selectedSprint.start,
                                  selectedSprint.end,
                                )
                              : selectedSprint.duration || "Dates not set"}
                          </p>
                        </div>
                        <div>
                          <p className="text-[13px] font-semibold uppercase text-[#8A9AB5]">
                            Status
                          </p>
                          <span
                            className={`mt-1 inline-flex rounded-full px-2.5 py-1 text-[13px] font-semibold ${getSprintStatusClass(selectedSprint.status)}`}
                          >
                            {formatSprintStatus(selectedSprint.status)}
                          </span>
                        </div>
                        <div>
                          <p className="text-[13px] font-semibold uppercase text-[#8A9AB5]">
                            Kanban lists
                          </p>
                          {selectedSprint.listNames?.length ? (
                            <div className="mt-1.5 flex flex-wrap gap-2">
                              {selectedSprint.listNames.map((listName) => (
                                <span
                                  key={listName}
                                  className="rounded-full bg-[#EEF2FF] px-2.5 py-1 text-[13px] font-medium text-[#4338CA]"
                                >
                                  {listName}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="mt-1 text-[#8A9AB5]">
                              No lists assigned
                            </p>
                          )}
                        </div>
                        <div>
                          <p className="text-[13px] font-semibold uppercase text-[#8A9AB5]">
                            Progress
                          </p>
                          <div className="mt-2">
                            {renderSprintProgress(selectedSprint, "lg")}
                          </div>
                        </div>
                      </div>
                      <div className="mt-6 flex justify-end">
                        <button
                          type="button"
                          onClick={() => setSelectedSprint(null)}
                          className="h-10 min-w-[120px] rounded-md border border-[#4F46E5] px-4 text-[14px] font-normal text-[#4F46E5]"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                {deleteConfirmSprint && (
                  <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="delete-sprint-title"
                  >
                    <div className="w-full max-w-[420px] rounded-xl bg-white p-6 font-[Poppins] shadow-2xl">
                      <h2
                        id="delete-sprint-title"
                        className="text-[22px] font-semibold text-[#172554]"
                      >
                        Delete sprint?
                      </h2>
                      <p className="mt-2 text-[14px] text-[#63738F]">
                        Are you sure you want to delete “
                        {deleteConfirmSprint.sprint}”?
                      </p>
                      <div className="mt-6 flex justify-end gap-3">
                        <button
                          type="button"
                          onClick={() => setDeleteConfirmSprint(null)}
                          className="h-10 min-w-[120px] rounded-md border border-[#4F46E5] px-4 text-[14px] font-normal text-[#4F46E5]"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={confirmDeleteSprint}
                          className="h-10 min-w-[120px] rounded-md bg-[#4F46E5] px-4 text-[14px] font-normal text-white hover:bg-[#4338CA]"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}
                <div
                  className={
                    activeProjectTab === "overview" ? "contents" : "hidden"
                  }
                >
                  <div
                    ref={projectInfoCardRef}
                    className="relative h-[320px] min-h-[320px] max-h-[320px] overflow-visible bg-white p-4 sm:p-6 rounded-[12px] border border-[#EDF0F7] shadow-[0_1px_3px_rgba(16,24,40,0.04)] w-full"
                  >
                    <div className="absolute inset-x-0 top-0 h-[3px] bg-[#4F46E5]" />
                    <div className="flex items-center justify-between mb-5 pb-3 border-b border-[#F1F3F8]">
                      <h3 className="flex items-center gap-2 text-[18px] font-semibold text-[#1F2937]">
                        <span className="h-4 w-1 rounded-full bg-[#4F46E5]" />
                        Project Information
                      </h3>
                      {canManage &&
                        (!isEditingDates ? (
                          <button
                            onClick={startEditDates}
                            className="text-[#9AA3B5] hover:text-[#4F46E5] transition-colors"
                          >
                            <FiEdit2 size={18} />
                          </button>
                        ) : (
                          <div className="flex gap-2">
                            <button
                              onClick={saveEditDates}
                              disabled={isUpdatingProject}
                              className="w-[26px] h-[26px] flex items-center justify-center rounded bg-[#4253C7] text-white"
                            >
                              <FiCheck size={13} />
                            </button>
                            <button
                              onClick={cancelEditDates}
                              className="w-[26px] h-[26px] flex items-center justify-center rounded border border-gray-300 text-gray-500"
                            >
                              <FiX size={13} />
                            </button>
                          </div>
                        ))}
                    </div>
                    {/* Same spacing and row heights in view and edit mode, so
                        clicking edit does not shift the rows. Each DatePicker
                        sits in its own box: when open it inserts an extra
                        element, which would otherwise become a third flex
                        item and push the input to the middle of the row. */}
                    <div className="space-y-2">
                      <div className="flex justify-between items-center h-8">
                        <p className="text-[13px] font-normal text-[#6B7280]">
                          Start Date
                        </p>
                        {isEditingDates ? (
                          <div className="shrink-0">
                            <DatePicker
                              selected={editStartDate}
                              onChange={(date) => setEditStartDate(date)}
                              className="w-[140px] h-[32px] border border-gray-300 rounded-lg px-2 text-[13px]"
                              dateFormat="dd MMM yyyy"
                              placeholderText="Select date"
                              popperPlacement="bottom-end"
                              popperClassName="z-[60]"
                            />
                          </div>
                        ) : (
                          <p className="text-[13px] font-semibold text-[#1F2937]">
                            {selectedProject?.startDate
                              ? new Date(
                                  selectedProject.startDate,
                                ).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })
                              : "N/A"}
                          </p>
                        )}
                      </div>

                      <div className="flex justify-between items-center h-8">
                        <p className="text-[13px] font-normal text-[#6B7280]">
                          Deadline
                        </p>
                        {isEditingDates ? (
                          <div className="shrink-0">
                            <DatePicker
                              selected={editDeadline}
                              onChange={(date) => setEditDeadline(date)}
                              className="w-[140px] h-[32px] border border-gray-300 rounded-lg px-2 text-[13px]"
                              dateFormat="dd MMM yyyy"
                              placeholderText="Select date"
                              minDate={editStartDate || null}
                              popperPlacement="bottom-end"
                              popperClassName="z-[60]"
                            />
                          </div>
                        ) : (
                          <p className="text-[13px] font-semibold text-[#1F2937]">
                            {selectedProject?.deadline
                              ? new Date(
                                  selectedProject.deadline,
                                ).toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })
                              : "N/A"}
                          </p>
                        )}
                      </div>
                      <div className="flex justify-between items-center h-8">
                        <p className="text-[13px] font-normal text-[#6B7280]">
                          Priority
                        </p>
                        {isEditingDates ? (
                          <select
                            value={editPriority}
                            onChange={(e) => setEditPriority(e.target.value)}
                            className="w-[140px] h-[32px] border border-gray-300 rounded-lg px-2 text-[13px]"
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                            <option value="critical">Critical</option>
                          </select>
                        ) : (
                          <span
                            className={`px-3 py-1 text-[12px] font-normal rounded-full ${getPriorityBadge(selectedProject?.priority)}`}
                          >
                            {selectedProject?.priority?.toUpperCase() || "N/A"}
                          </span>
                        )}
                      </div>

                      {/* Status */}
                      <div className="flex justify-between items-center">
                        <p className="text-[13px] font-normal text-[#6B7280]">
                          Status
                        </p>
                        <div className="flex items-center gap-2">
                          <span
                            className={`w-[8px] h-[8px] rounded-full ${PROJECT_STATUS_CONFIG[status].dot}`}
                          />
                          <span className="text-[13px] font-semibold text-[#1F2937] capitalize">
                            {selectedProject?.trackingStatus || "N/A"}
                          </span>
                        </div>
                      </div>

                      {/* Created By */}
                      <div className="flex justify-between items-center">
                        <p className="text-[13px] font-normal text-[#6B7280]">
                          Created By
                        </p>
                        <p className="text-[13px] font-semibold text-[#1F2937] capitalize">
                          {selectedProject?.createdBy?.name || "N/A"}
                        </p>
                      </div>
                      <div className="flex justify-between items-start">
                        <p className="text-[13px] font-normal text-[#6B7280]">
                          Created At
                        </p>
                        <p className="text-[13px] font-semibold text-[#1F2937] text-right break-words max-w-[200px]">
                          {selectedProject?.createdAt
                            ? `${new Date(selectedProject.createdAt).getDate()} ${new Date(selectedProject.createdAt).toLocaleString("en-US", { month: "short" })} , ${new Date(selectedProject.createdAt).getFullYear()}-${new Date(selectedProject.createdAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })}`
                            : "N/A"}
                        </p>
                      </div>
                      <div className="flex justify-between items-start">
                        <p className="text-[13px] font-normal text-[#6B7280]">
                          Updated At
                        </p>
                        <p className="text-[13px] font-semibold text-[#1F2937] text-right break-words max-w-[200px]">
                          {" "}
                          {selectedProject?.updatedAt
                            ? `${new Date(selectedProject.updatedAt).getDate()} ${new Date(selectedProject.updatedAt).toLocaleString("en-US", { month: "short" })} , ${new Date(selectedProject.updatedAt).getFullYear()}-${new Date(selectedProject.updatedAt).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })}`
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Resources */}
                  {showResources && (
                    <div className="relative h-[320px] min-h-[320px] max-h-[320px] self-start overflow-hidden bg-white rounded-[12px] border border-[#EDF0F7] shadow-[0_1px_3px_rgba(16,24,40,0.04)] px-4 sm:px-6 pt-4 sm:pt-6 pb-4 w-full flex flex-col">
                      <div className="absolute inset-x-0 top-0 h-[3px] bg-[#4F46E5]" />
                      <div className="flex flex-wrap justify-between items-center gap-2 mb-4 pb-3 border-b border-[#F1F3F8]">
                        <h3 className="flex items-center gap-2 text-[16px] font-semibold text-[#1F2937]">
                          <span className="h-4 w-1 rounded-full bg-[#4F46E5]" />
                          Resources
                        </h3>
                        {/* Add Client is available to viewers too; Add Member
                            stays admin/manager-only, so the wrapper can no
                            longer be gated on canManage alone. */}
                        {(canManage || canInviteClient) && (
                          <div className="flex flex-wrap items-center justify-end gap-2 sm:gap-4">
                            {canInviteClient && (
                              <button
                                onClick={openAddClient}
                                className="text-[#4F46E5] text-[13px] font-medium hover:underline"
                              >
                                Add Client
                              </button>
                            )}
                            {canManage && (
                              <button
                                onClick={() => {
                                  setShowModal(true);
                                  setSelectedMembers(
                                    resources.map((r) => r._id),
                                  );
                                }}
                                className="rounded-[7px] bg-[#4F46E5] px-3 py-1.5 text-[12px] font-medium text-white hover:bg-[#4338CA]"
                              >
                                Add Member
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="relative flex-none">
                      <div
                        ref={resourcesScrollRef}
                        className="resources-scroll-list h-[212px] max-h-[212px] flex-none overflow-y-auto"
                      >
                        {isFetchingSummary && resources.length === 0 ? (
                          <div className="py-2 text-center text-sm text-gray-500">
                            Loading resources...
                          </div>
                        ) : resources.length === 0 ? (
                          <div className="h-10" aria-hidden="true" />
                        ) : (
                          resources.map((raw, i) => {
                            const item = getMemberInfo(raw);
                            // Protected resource roles: these members can never be removed from a
                            // project (admin/owner is mandatory, and managers are also protected).
                            // If your backend uses different labels for these roles, add them here.
                            const isProtectedResource = [
                              "admin",
                              "owner",
                              "manager",
                            ].includes(item.role?.toLowerCase());
                            return (
                              <div
                                key={i}
                                className="flex items-center gap-2 py-2 border-b border-black/10 last:border-none"
                              >
                                <div className="flex items-center gap-3 min-w-0 flex-1">
                                  <div
                                    className="w-[34px] h-[34px] rounded-full flex items-center justify-center shrink-0 overflow-hidden text-[13px] font-semibold text-white"
                                    style={{
                                      backgroundColor:
                                        AVATAR_COLORS[i % AVATAR_COLORS.length],
                                    }}
                                  >
                                    {getProfileImageUrl(item) ? (
                                      <img
                                        src={getProfileImageUrl(item)}
                                        alt={item.name}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      getInitials(item.name)
                                    )}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <p className="truncate text-[13px] font-semibold text-[#1F2937] capitalize">
                                      {item.name}
                                    </p>
                                    {item.email && (
                                      <p className="truncate text-[11px] text-[#9AA3B5]">
                                        {item.email}
                                      </p>
                                    )}
                                  </div>
                                  <span
                                    className={`shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-medium capitalize ${getRoleBadge(item.role)}`}
                                  >
                                    {item.role
                                      ? item.role.toLowerCase()
                                      : "Member"}
                                  </span>
                                </div>
                                {canManage && (
                                  <div
                                    className="relative shrink-0"
                                    ref={showMenu === i ? removeMenuRef : null}
                                  >
                                    <div
                                      onClick={() =>
                                        setShowMenu(showMenu === i ? null : i)
                                      }
                                      className={`w-[20px] h-[20px] rounded flex items-center justify-center cursor-pointer ${showMenu === i ? "bg-black/10" : ""}`}
                                    >
                                      <FiMoreHorizontal
                                        className="text-black"
                                        size={16}
                                      />
                                    </div>
                                    {showMenu === i && (
                                      <div className="absolute right-[28px] top-1/2 -translate-y-1/2 h-[32px] bg-white shadow-lg rounded z-10 w-[88px] flex items-center">
                                        <button
                                          className="w-full h-full px-4 text-left text-red-500 text-sm hover:bg-gray-100 whitespace-nowrap"
                                          onClick={() => {
                                            setShowMenu(null);
                                            // Admins/owners: block with the
                                            // "cannot remove" popup instead of
                                            // the normal remove-confirmation.
                                            if (isProtectedResource) {
                                              setCannotRemoveAdmin(item);
                                            } else {
                                              setConfirmRemoveMember(item);
                                            }
                                          }}
                                        >
                                          Remove
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                      <HoverScrollbar
                        targetRef={resourcesScrollRef}
                        watch={resources.length}
                        orientation="vertical"
                        // Sits in the card's right padding (16px / 24px on
                        // sm+), so it never covers the ⋯ menu icons.
                        edgeClassName="-right-4 sm:-right-6"
                      />
                      </div>
                    </div>
                  )}
                </div>
                {/* ════ END RIGHT ════ */}
              </div>
              {/* ── END 2-COLUMN GRID ── */}

              {/* ══════════════════════════════════════════════
                  PROJECT ACTIVITY TABLE
                  Full-width below the 2-column grid (matches design)
              ══════════════════════════════════════════════ */}
              <div
                className={
                  activeProjectTab === "overview"
                    ? "relative overflow-hidden bg-white rounded-[12px] border border-[#EDF0F7] shadow-[0_1px_3px_rgba(16,24,40,0.04)] px-3 sm:px-6 pt-4 sm:pt-6 w-full mt-4"
                    : "hidden"
                }
              >
                {" "}
                <div className="absolute inset-x-0 top-0 h-[3px] bg-[#4F46E5]" />
                {/* Activity header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-5 gap-3">
                  {" "}
                  <div>
                    <h3 className="flex items-center gap-2 text-[18px] font-semibold text-[#1F2937]">
                      <span className="h-4 w-1 rounded-full bg-[#4F46E5]" />
                      Project Activity
                    </h3>
                    <p className="text-[11px] font-normal text-[#9AA3B5] mt-0.5">
                      Track tasks, resources and updates in one place
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2 items-center w-full sm:w-auto justify-start sm:justify-end">
                    {" "}
                    {canManage && (
                      <button
                        onClick={handleExportCSV}
                        className="w-full sm:w-auto h-[36px] text-[13px] font-medium px-4 border border-[#E4E7EC] rounded-[8px] text-[#475467] hover:bg-[#F7F8FC] transition"
                      >
                        Export CSV
                      </button>
                    )}
                    {canManage && (
                      <button
                        onClick={() => {
                          setTaskFormError("");
                          setShowTaskModal(true);
                        }}
                        className="w-full sm:w-auto h-[36px] text-[13px] font-medium px-4 bg-[#4F46E5] hover:bg-[#4338CA] text-white rounded-[8px] transition"
                      >
                        Add Task
                      </button>
                    )}
                  </div>
                </div>
                <div className="project-activity-scroll -mx-3 overflow-x-auto px-3 sm:-mx-6 sm:px-6">
                  <div
                    className="grid gap-4 items-center text-[10px] font-semibold uppercase tracking-[0.08em] py-3 border-b border-[#EDF0F7] text-[#9AA3B5] min-w-[760px]"
                    style={{
                      gridTemplateColumns:
                        "1fr 1.5fr 1fr 1fr minmax(120px, 1fr) 96px",
                    }}
                  >
                    {" "}
                    <p>LIST</p>
                    <p>TASK</p>
                    <p>TYPE</p>
                    <p>PRIORITY</p>
                    <p>RESOURCE</p>
                    <p className="justify-self-end">EST. HOURS</p>
                  </div>
                  {currentTasks.length === 0 ? (
                    <div className="flex items-center justify-center text-gray-400 text-sm min-w-[760px] py-10">
                      No tasks found
                    </div>
                  ) : (
                    <>
                      {currentTasks.map((task, index) => (
                        <div
                          key={index}
                          className="grid gap-4 items-center text-[12px] py-3 border-b border-[#F4F6FA] last:border-b-0 min-w-[760px] min-h-[71px] hover:bg-[#FAFBFD] transition-colors"
                          style={{
                            gridTemplateColumns:
                              "1fr 1.5fr 1fr 1fr minmax(120px, 1fr) 96px",
                          }}
                        >
                          <div className="min-w-0">
                            <span
                              className="inline-block max-w-full truncate rounded-[6px] bg-[#F1F3F8] px-2.5 py-1 text-[11px] font-medium text-[#475467]"
                              title={task.listName}
                            >
                              {task.listName}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 relative min-w-0">
                            <p
                              className="text-[12px] font-medium text-[#1F2937] truncate"
                              title={task.name}
                            >
                              {task.name}
                            </p>
                            <div
                              className="relative"
                              ref={
                                selectedTaskDesc?.name === task.name
                                  ? taskDescPopupRef
                                  : null
                              }
                            >
                              <button
                                onClick={() =>
                                  setSelectedTaskDesc(
                                    selectedTaskDesc?.name === task.name
                                      ? null
                                      : task,
                                  )
                                }
                                className="shrink-0 w-[18px] h-[18px] rounded-full border border-[#D0D5DD] flex items-center justify-center text-[10px] text-[#9AA3B5] hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors"
                              >
                                i
                              </button>
                              {selectedTaskDesc?.name === task.name && (
                                <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 w-[260px] min-h-[66px] bg-white border border-[#CFCFCF] rounded-[8px] px-4 py-3 shadow-md z-20">
                                  <p className="text-[12px] leading-[22px] text-[#4A4A4A]">
                                    {selectedTaskDesc.description ||
                                      "No Description"}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                          <div>
                            <span
                              className="rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase"
                              style={{
                                backgroundColor: `${getTimelineTypeColor(task.taskType)}1A`,
                                color: getTimelineTypeColor(task.taskType),
                              }}
                            >
                              {task.taskType}
                            </span>
                          </div>
                          <div>
                            <span
                              className={`text-[12px] font-normal px-3 py-1 rounded-full ${getPriorityBadge(task.priority)}`}
                            >
                              {task.priority?.toUpperCase()}
                            </span>
                          </div>
                          <div
                            className="flex min-w-[120px] items-center relative"
                            onMouseEnter={() => setOpenResourceRow(index)}
                            onMouseLeave={() => setOpenResourceRow(null)}
                          >
                            <div className="flex items-center relative">
                              {(task.assignees || []).length === 0 && (
                                <span className="text-[#9AA3B5]">-</span>
                              )}
                              {(task.assignees || [])
                                .slice(0, 2)
                                .map((a, ai) => {
                                  const assignee = getMemberInfo(a);
                                  const resourceIndex = resources.findIndex(
                                    (resource) => {
                                      const member = getMemberInfo(resource);
                                      return (
                                        String(member._id) ===
                                          String(assignee._id) ||
                                        (assignee.email &&
                                          member.email === assignee.email)
                                      );
                                    },
                                  );
                                  const projectResource =
                                    resourceIndex >= 0
                                      ? getMemberInfo(resources[resourceIndex])
                                      : null;
                                  const profileImageUrl =
                                    getProfileImageUrl(assignee) ||
                                    getProfileImageUrl(projectResource);
                                  return (
                                    <div
                                      key={a._id}
                                      className="w-[40px] h-[40px] rounded-full border-2 border-white flex items-center justify-center overflow-hidden text-[14px] font-semibold text-white"
                                      style={{
                                        marginLeft: ai === 0 ? 0 : -8,
                                        backgroundColor:
                                          AVATAR_COLORS[
                                            (resourceIndex >= 0
                                              ? resourceIndex
                                              : ai) % AVATAR_COLORS.length
                                          ],
                                      }}
                                    >
                                      {profileImageUrl ? (
                                        <img
                                          src={profileImageUrl}
                                          alt={assignee.name}
                                          className="w-full h-full object-cover"
                                        />
                                      ) : (
                                        getInitials(assignee.name)
                                      )}
                                    </div>
                                  );
                                })}

                              {(task.assignees || []).length > 2 && (
                                <div className="w-[34px] h-[34px] rounded-full bg-[#E8E8E8] border-2 border-white flex items-center justify-center -ml-2 text-[12px] font-semibold text-[#6B7280]">
                                  +{(task.assignees || []).length - 2}
                                </div>
                              )}

                              {openResourceRow === index &&
                                (task.assignees || []).length > 0 && (
                                  <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 w-[180px] min-h-[66px] bg-white border border-[#CFCFCF] rounded-[8px] px-4 py-3 shadow-md z-30 max-h-[240px] overflow-y-auto">
                                    {task.assignees.map((a) => (
                                      <div
                                        key={a._id}
                                        className="flex items-center gap-3 py-1.5"
                                      >
                                        <div className="w-[32px] h-[32px] rounded-full flex items-center justify-center overflow-hidden text-[12px] font-semibold bg-[#F3F4F6] text-[#7F7F7F] shrink-0">
                                          {getProfileImageUrl(a) ? (
                                            <img
                                              src={getProfileImageUrl(a)}
                                              alt={a.name}
                                              className="w-full h-full object-cover"
                                            />
                                          ) : (
                                            getInitials(a.name)
                                          )}
                                        </div>
                                        <p className="text-[12px] leading-[22px] text-[#4A4A4A] truncate">
                                          {a.name}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                )}
                            </div>
                          </div>{" "}
                          <div className="justify-self-end">
                            <span className="inline-flex min-w-[80px] justify-center rounded-[7px] bg-[#EEF1FF] px-3 py-1.5 text-[10px] font-semibold text-[#4253C7]">
                              {task.estimatedHours || 0} HOURS
                            </span>
                          </div>
                        </div>
                      ))}

                      {Array.from({
                        length: tasksPerPage - currentTasks.length,
                      }).map((_, i) => (
                        <div
                          key={`filler-${i}`}
                          className="min-w-[760px] min-h-[70px] border-b border-gray-100 last:border-none"
                        />
                      ))}
                    </>
                  )}

                  {/* Pagination */}
                  <div
                    className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-4 bg-[#F3F4F6] rounded-b-[16px] px-6 py-4 -mx-6 -mb-4"
                    style={{ width: "calc(100% + 48px)" }}
                  >
                    {" "}
                    <p className="text-[12px] font-medium leading-[18px] text-[#6B7280] capitalize">
                      {totalTaskItems === 0
                        ? "No tasks found"
                        : `Showing ${taskStartIndex + 1} To ${Math.min(taskEndIndex, totalTaskItems)} Of ${totalTaskItems} Results`}
                    </p>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setTaskPage((p) => Math.max(p - 1, 1))}
                        disabled={taskPage === 1}
                        className="w-10 h-10 border border-[#ECEFF5] rounded-lg text-[0px] bg-[#F5F5F5] flex items-center justify-center disabled:opacity-60 before:block before:w-3 before:h-3 before:border-l-2 before:border-b-2 before:border-gray-700 before:rotate-45"
                      >
                        ‹
                      </button>

                      {visibleTaskPages.map((page) => (
                        <button
                          key={page}
                          onClick={() => setTaskPage(page)}
                          className={`w-8 h-8 rounded-lg text-xs ${
                            taskPage === page
                              ? "bg-[#4C5CE7] text-white"
                              : "text-black"
                          }`}
                        >
                          {page}
                        </button>
                      ))}

                      <button
                        onClick={() =>
                          setTaskPage((p) => Math.min(p + 1, totalTaskPages))
                        }
                        disabled={taskPage === totalTaskPages}
                        className="w-10 h-10 border border-[#ECEFF5] rounded-lg text-[0px] bg-[#F5F5F5] flex items-center justify-center disabled:opacity-60 before:block before:w-3 before:h-3 before:border-r-2 before:border-t-2 before:border-gray-700 before:rotate-45"
                      >
                        ›
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              {/* ── END ACTIVITY TABLE ── */}

              {/* Kanban button */}
              {/* Kanban button */}
              <div className="flex justify-end mt-10">
                <button
                  onClick={() => navigate(`/dashboard/kanban/${projectRef}`)}
                  className="w-full sm:w-auto bg-[#4F46E5] hover:bg-[#4338CA] text-white text-[13px] px-5 py-2.5 rounded-[8px] font-medium transition-colors shadow-[0_1px_3px_rgba(16,24,40,0.10)]"
                >
                  Open Kanban Board
                </button>
              </div>

              {/* ── CONFIRM REMOVE MEMBER MODAL ── */}
              {confirmRemoveMember && (
                <div
                  className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 px-3"
                  onClick={() => setConfirmRemoveMember(null)}
                >
                  <div
                    className="bg-white w-full max-w-[400px] rounded-xl shadow-2xl p-6"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h2 className="text-[20px] font-semibold text-gray-900 mb-2">
                      Remove Member
                    </h2>
                    <p className="text-sm text-gray-600 mb-6">
                      Are you sure you want to remove{" "}
                      <span className="font-semibold">
                        {confirmRemoveMember.name || "this member"}
                      </span>{" "}
                      from this project?
                    </p>
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={() => setConfirmRemoveMember(null)}
                        className="px-5 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() =>
                          handleRemoveMember(confirmRemoveMember._id)
                        }
                        className="px-5 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── CANNOT REMOVE ADMIN / MANAGER MODAL ── */}
              {cannotRemoveAdmin && (
                <div
                  className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 px-3"
                  onClick={() => setCannotRemoveAdmin(null)}
                >
                  <div
                    className="bg-white w-full max-w-[400px] rounded-xl shadow-2xl p-6 text-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="w-[63px] h-[63px] mx-auto rounded-full bg-[#FFF0F0] flex items-center justify-center mb-5">
                      <AlertTriangle className="text-red-500" size={22} />
                    </div>

                    {(() => {
                      // Show role-specific copy: admins/owners get the
                      // "mandatory for every project" reasoning, managers
                      // get a simpler "cannot be removed" message.
                      const blockedRole =
                        cannotRemoveAdmin?.role?.toLowerCase();
                      const isManagerBlocked = blockedRole === "manager";
                      const roleLabel = isManagerBlocked ? "Manager" : "Admin";
                      const message = isManagerBlocked
                        ? "The project manager cannot be removed."
                        : "The project admin cannot be removed. An admin is mandatory for every project.";
                      return (
                        <>
                          <h2 className="text-[20px] font-semibold text-gray-900 mb-2">
                            Cannot Remove {roleLabel}
                          </h2>
                          <p className="text-sm text-gray-600 mb-6">
                            {message}
                          </p>
                        </>
                      );
                    })()}

                    <div className="flex justify-center">
                      <button
                        onClick={() => setCannotRemoveAdmin(null)}
                        className="px-6 py-2 bg-[#4253C7] text-white rounded-lg"
                      >
                        Okay
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── CONFIRM DISABLE PROJECT MODAL ── */}
              {showDisableConfirm && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 px-3">
                  <div className="bg-white w-full max-w-[453px] rounded-[12px] shadow-2xl px-8 py-10 text-center relative">
                    <button
                      onClick={() => setShowDisableConfirm(false)}
                      className="absolute right-5 top-5 text-black/50 hover:text-black"
                    >
                      <FiX size={22} />
                    </button>

                    <div className="w-[63px] h-[63px] mx-auto rounded-full bg-[#FFF0F0] flex items-center justify-center mb-5">
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#EF4444"
                        strokeWidth="2"
                      >
                        <path d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                      </svg>
                    </div>

                    <h2 className="text-[30px] leading-[42px] font-semibold text-black mb-3">
                      Disable Project?
                    </h2>
                    <p className="text-[16px] leading-[24px] text-black mb-8">
                      Disabling the project will pause all ongoing activities
                      and features. Are you sure you want to disable this
                      project?
                    </p>

                    <div className="flex justify-center gap-3">
                      <button
                        onClick={() => setShowDisableConfirm(false)}
                        className="w-[197px] h-[40px] border border-[#FF0000] rounded-[8px] text-[#FF0000] text-[14px] hover:bg-red-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleToggleDisable}
                        className="w-[197px] h-[40px] bg-[#EF4444] rounded-[8px] text-white text-[14px] hover:bg-red-600"
                      >
                        Disable
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {/* ── MANAGER BLOCKED POPUP ── */}
              {showManagerBlocked && (
                <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 px-3">
                  <div className="bg-white w-full max-w-[400px] rounded-xl shadow-2xl p-6 text-center">
                    <p className="text-[16px] font-medium text-black mb-6">
                      Only admins can disable a project
                    </p>
                    <button
                      onClick={() => setShowManagerBlocked(false)}
                      className="px-6 py-2 bg-[#4253C7] text-white rounded-lg text-sm font-medium hover:bg-[#2f63e0]"
                    >
                      Okay
                    </button>
                  </div>
                </div>
              )}

              {/* ── ADD CLIENT CHOOSER ── */}
              <AddClientQuestionModal
                open={showAddClientModal && canInviteClient && !isViewerRole}
                onClose={() => setShowAddClientModal(false)}
                onInviteNew={() => setShowInviteClientModal(true)}
                onAddExisting={handleAddExistingClient}
                availableClients={availableClients}
                isLoadingClients={isLoadingClients}
                isAdding={isAddingClient}
                error={addClientError}
              />

              {/* ── INVITE CLIENT MODAL ── */}
              {showInviteClientModal && canInviteClient && (
                <>
                  <div
                    className="fixed inset-0 z-50 bg-black/40"
                    onClick={resetInviteClientForm}
                  />
                  <div
                    className="fixed left-1/2 top-1/2 z-50 w-[600px] max-w-[calc(100%-32px)] -translate-x-1/2 -translate-y-1/2 rounded-[12px] bg-white px-8 py-8 shadow-2xl"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="invite-client-title"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h2
                          id="invite-client-title"
                          className="text-[24px] font-semibold leading-7 text-[#111111]"
                        >
                          Invite Client
                        </h2>
                        <p className="mt-1 text-sm leading-5 text-[#737B8E]">
                          Invite a client contact to view this organisation's
                          work.
                        </p>
                      </div>
                      <button
                        onClick={resetInviteClientForm}
                        className="-mr-1 -mt-1 p-1 text-[#777777] transition hover:text-[#333333]"
                        aria-label="Close invite client dialog"
                      >
                        ×
                      </button>
                    </div>
                    <hr className="my-5 border-[#E8EAF0]" />

                    <div className="mb-4">
                      <label className="mb-1.5 block text-sm font-medium text-[#414141]">
                        Email
                      </label>
                      <input
                        type="email"
                        placeholder="client@company.com"
                        value={clientEmail}
                        onChange={(e) => {
                          setClientEmail(e.target.value);
                          setClientEmailError("");
                        }}
                        className="h-[42px] w-full rounded-lg border border-[#CFD4DE] px-4 text-sm text-[#333] outline-none placeholder:text-[#9BA4B5] focus:border-[#4253C7]"
                      />
                    </div>
                    {clientEmailError && (
                      <p className="-mt-3 mb-4 text-[13px] text-red-500">
                        {clientEmailError}
                      </p>
                    )}

                    <div className="mb-4">
                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-[#414141]">
                          Description
                        </label>
                        <textarea
                          value={clientDescription}
                          maxLength={100}
                          rows={1}
                          onChange={(e) => setClientDescription(e.target.value)}
                          placeholder="e.g. Primary contact for billing"
                          className="h-[42px] w-full resize-none rounded-lg border border-[#CFD4DE] px-4 py-[10px] text-sm text-[#333] outline-none placeholder:text-[#9BA4B5] focus:border-[#4253C7]"
                        />
                        <p className="mt-1 text-right text-xs text-[#9299A8]">
                          {clientDescription.length}/100
                        </p>
                      </div>
                    </div>

                    <div className="hidden">
                      <span className="text-lg text-[#4253C7]">ℹ</span>
                      <p className="text-xs text-[#4253C7]">
                        An Invitation Will Be Sent To This Email To Invite The
                        Client's.
                      </p>
                    </div>

                    <div className="mb-6">
                      <label className="mb-1.5 block text-sm font-medium text-[#414141]">
                        Start Date
                      </label>
                      <div className="relative">
                        <div className="pointer-events-none absolute left-0 top-0 z-10 flex h-[42px] w-[42px] items-center justify-center rounded-l-lg border border-r-0 border-[#CFD4DE] bg-[#F7F8FC] text-[#3478F6]">
                          <Icons.Calendar />
                        </div>
                        <DatePicker
                          selected={
                            clientStartDate
                              ? new Date(`${clientStartDate}T12:00:00`)
                              : null
                          }
                          onChange={(date) => {
                            setClientStartDate(
                              date ? toDateOnlyISO(date).slice(0, 10) : "",
                            );
                            setClientEmailError("");
                          }}
                          placeholderText="Select start date"
                          dateFormat="dd MMM yyyy"
                          wrapperClassName="w-full"
                          className="h-[42px] w-full rounded-lg border border-[#CFD4DE] pl-[56px] pr-3 text-sm text-[#333] outline-none placeholder:text-[#9BA4B5] focus:border-[#4253C7]"
                        />
                      </div>
                    </div>

                    <div className="mb-6 rounded-lg bg-[#F0F0FF] px-3 py-3">
                      <p className="text-xs leading-[18px] text-[#4253C7]">
                        An invitation will be sent to this email to complete
                        their account setup.
                      </p>
                    </div>

                    <div className="flex justify-end gap-3">
                      <button
                        onClick={resetInviteClientForm}
                        className="flex h-[40px] w-[140px] items-center justify-center rounded-lg border border-[#4253C7] text-sm font-medium text-[#4253C7] transition hover:bg-[#F4F5FF]"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleInviteClient}
                        className="flex h-[40px] w-[140px] items-center justify-center rounded-lg bg-[#4253C7] text-sm font-medium text-white transition hover:bg-[#3547B8]"
                      >
                        Invite
                      </button>
                    </div>
                  </div>
                </>
              )}
              {showModal && (
                <div className="fixed inset-0 bg-black/20 flex items-center justify-center z-50 px-3">
                  <div className="bg-white w-[635px] max-w-full rounded-[12px] shadow-2xl px-[40px] py-[40px]">
                    {/* Header */}
                    <div className="flex justify-between items-start mb-[18px]">
                      <div>
                        <h2 className="text-[30px] leading-[42px] font-semibold text-black">
                          Add New Member
                        </h2>
                        <p className="text-[16px] leading-[24px] text-black/50 mt-[8px]">
                          Add a new member and assign them to a new project
                        </p>
                      </div>
                      <button
                        onClick={() => setShowModal(false)}
                        className="text-[24px] leading-none text-black/50 hover:text-black transition mt-1"
                      >
                        ×
                      </button>
                    </div>

                    {/* Project Members label */}
                    <p className="text-[16px] leading-[24px] font-normal text-black mb-[8px]">
                      Project Members
                    </p>

                    {/* Search input — rounded full, standalone */}
                    <div className="w-full relative mb-0">
                      <FiSearch
                        className="absolute left-[14px] top-1/2 -translate-y-1/2 text-black/40"
                        size={20}
                      />
                      <input
                        type="text"
                        placeholder="Search Members"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full h-[40px] border border-black/20 rounded-[8px] pl-[44px] pr-3 text-[16px] text-black/50 placeholder:text-black/50 outline-none focus:border-[#4253C7]"
                      />
                    </div>

                    {/* Members list box */}
                    <div className="w-full bg-white border border-black/20 rounded-[8px] h-[172px] overflow-y-auto mt-[12px] mb-[32px]">
                      {filteredMembers.length === 0 && (
                        <div className="h-full flex items-center justify-center text-[14px] text-black/40">
                          No members found
                        </div>
                      )}
                      {filteredMembers.map((member, i) => {
                        const isExistingMember = resources.some(
                          (r) => r._id === member._id,
                        );
                        return (
                          <label
                            key={i}
                            className="flex items-center justify-between px-[20px] py-[4px] hover:bg-gray-50 cursor-pointer transition"
                          >
                            <div className="flex items-center gap-[16px]">
                              {/* Circle avatar with user icon */}
                              <div className="w-[28px] h-[28px] rounded-full border border-black flex items-center justify-center shrink-0">
                                <FiUser size={14} className="text-black" />
                              </div>
                              <span className="text-[16px] leading-[24px] text-black/50">
                                {member.name}
                              </span>
                            </div>
                            <input
                              type="checkbox"
                              checked={
                                isExistingMember ||
                                selectedMembers.includes(member._id)
                              }
                              disabled={isExistingMember}
                              onChange={() => {
                                if (isExistingMember) return;
                                setSelectedMembers((prev) =>
                                  prev.includes(member._id)
                                    ? prev.filter((id) => id !== member._id)
                                    : [...prev, member._id],
                                );
                              }}
                              className="w-[20px] h-[20px] accent-[#3B82F6] cursor-pointer"
                            />
                          </label>
                        );
                      })}
                    </div>

                    {/* Buttons */}
                    <div className="flex justify-end gap-[12px]">
                      <button
                        onClick={() => setShowModal(false)}
                        className="w-[163px] h-[40px] text-[14px] font-normal border border-[#4253C7] rounded-[8px] text-[#4253C7] hover:bg-blue-50 transition"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleAddMembers}
                        disabled={isAddingMember}
                        className="w-[163px] h-[40px] text-[14px] font-normal bg-[#4253C7] text-white rounded-[8px] hover:bg-[#2f63e0] transition"
                      >
                        {isAddingMember ? "Adding..." : "Add Member"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Projects;
